import { createContext, useContext, useMemo } from "react";
import { APP_LOCALES, type AppLocale, isAppLocale } from "@shared/i18n-locales";
import { detectUserCountry } from "@/lib/countryDetection";

type Messages = Record<string, string>;
type LocaleMessages = Record<AppLocale, Messages>;
export type TranslationOverrides = Partial<Record<AppLocale, Messages>>;

export const DEFAULT_MESSAGES: LocaleMessages = {
  nl: {
    "seo.home.title": "VintMap - Plan geoptimaliseerde routes langs kringloopwinkels in {region}",
    "seo.home.description": "Deel jouw vondsten en restyle met AI. #1 Kringloop Community.",
    "seo.shops.title": "VintMap - Vind de leukste kringloopwinkels in {region}",
    "seo.shops.description": "Plan routes, deel jouw vondsten en restyle met AI. #1 Kringloop Community.",
    "seo.region.title": "VintMap: Plan geoptimaliseerde routes langs kringloopwinkels in {region}",
    "nav.wishlist": "Wishlist",
    "nav.routes": "Routes",
    "nav.shops": "Kringloopwinkels",
    "nav.community": "Community",
    "nav.restyle": "Restyle",
    "nav.spaceblend": "SpaceBlend",
    "nav.login": "Inloggen",
    "nav.signup": "Registreren",
    "nav.account": "Account",
    "nav.admin": "Admin",
    "nav.scanner": "Scanner",
    "nav.insiderStart.button": "Start Direct Navigatie",
    "nav.freeStart.button": "Open in Google Maps",
    "nav.insiderStart.desc": "Route begint automatisch vanaf jouw locatie",
    "nav.freeStart.desc": "Pas startlocatie handmatig aan voor navigatie",
    "nav.freeStart.upsell": "Word Insider en navigeer direct vanaf jouw locatie",
    "shops.title": "Kringloopwinkels",
    "shops.description": "Jouw route naar verborgen parels.",
    "shops.roadtrip": "Plan de ultieme roadtrip langs {count} unieke kringloopstops.",
    "shops.title.favorites": "Favoriete kringloopwinkels",
    "shops.title.wishlist": "Wishlist",
    "shops.description.personal": "Jouw persoonlijke selectie",
    "shops.region.province": "Provincie",
    "shops.region.county": "County",
    "shops.region.municipality": "Gemeente",
    "shops.region.city": "Stad",
    "shops.region.whichOne": "Bedoel je dit?",
    "shops.region.multipleFound": "We hebben meerdere locaties gevonden. Maak een keuze:",
    "shops.region.choose": "Kies",
    "shops.cancel": "Annuleren",
    "footer.discover": "Ontdek",
    "footer.forShops": "Voor winkels",
    "footer.aboutUs": "Over ons",
    "footer.language": "Taal",
    "footer.madeWith": "Gemaakt met",
    "footer.sustainability": "voor duurzaamheid",
    "footer.claimShop": "Claim je Kringloopwinkel",
    "footer.aboutVintmap": "Over VintMap",
    "footer.faq": "Veelgestelde vragen",
    "footer.privacy": "Privacybeleid",
    "footer.terms": "Algemene Voorwaarden",
    "footer.disclaimer": "Disclaimer",
    "footer.contact": "Contact",
    "footer.tagline": "Ontdek community routes, plan je kringloopdag en deel je buit.",
    "header.planRoute": "Plan route",
    "header.planShort": "Plan",
    "header.myShops": "Mijn winkel(s)",
    "header.mobileMenuDesc": "Navigatiemenu voor mobiele gebruikers",
    "header.tier.free": "Gratis",
    "header.tier.insider": "Insider",
    "home.hero.title": "Ontdek kringloop routes gemaakt door de VintMap Community",
    "home.hero.subtitle": "De app voor kringloopliefhebbers. Vind de beste routes, ontdek nieuwe winkels en deel je mooiste pareltjes.",
    "home.hero.routes": "Ontdek routes",
    "home.hero.shops": "Zoek kringlopen",
    "home.hero.optimize": "Route Optimalisatie",
    "home.hero.startHome": "Start & eindig thuis",
    "home.hero.statusInsider": "Status: VintMap Insider",
    "home.featured": "Uitgelicht",
    "upgrade.default.title": "Word VintMap Insider",
    "upgrade.default.desc": "Unlock alle premium features en ondersteun VintMap.",
    "upgrade.default.feat1": "Maak je eigen kringlooproutes",
    "upgrade.perk.socialWishlist": "Social Wishlist & Vrienden",
    "upgrade.default.feat3": "AI Meubel Restyle",
    "upgrade.default.feat4": "Optimaliseer bestaande routes",
    "upgrade.default.feat5": "Start & eindig bij je thuisadres",
    "upgrade.home.feat1": "Persoonlijke routeberekening",
    "upgrade.home.feat2": "Geoptimaliseerde volgorde",
    "upgrade.home.feat3": "Altijd de slimste weg",
    "upgrade.routes.title": "Meer routes nodig?",
    "upgrade.routes.desc": "Je hebt het maximum aantal gratis routes bereikt. Upgrade naar VintMap Insider voor onbeperkt routes.",
    "upgrade.routes.feat1": "Onbeperkt routes maken",
    "upgrade.routes.feat2": "Meer dan 10 stops per route",
    "upgrade.routes.feat3": "Route-optimalisatie",
    "upgrade.posts.title": "Meer buit delen?",
    "upgrade.posts.desc": "Je hebt deze week al gepost. Upgrade naar VintMap Insider voor onbeperkt posts.",
    "upgrade.posts.feat1": "Onbeperkt posts per week",
    "upgrade.posts.feat2": "Posten als winkel (eigenaren)",
    "upgrade.builder.title": "Route Builder - Pro Feature",
    "upgrade.builder.desc": "Bouw je eigen kringlooproute en navigeer direct met Google Maps!",
    "upgrade.builder.feat1": "Onbeperkt routes bouwen",
    "upgrade.builder.feat2": "Onbeperkt aantal stops",
    "upgrade.builder.feat3": "Direct navigeren via Google Maps",
    "upgrade.builder.feat4": "Routes delen met vrienden",
    "home.gem.title": "Pareltje van de week",
    "home.gem.empty": "Nog geen pareltje van de week deze week. Wees de eerste!",
    "home.posts.all": "Alle posts",
    "home.posts.viewAll": "Bekijk alle posts",
    "home.route.title": "Route van de week",
    "home.routes.all": "Alle routes",
    "home.routes.viewAll": "Bekijk alle routes",
    "home.how.title": "Hoe werkt het?",
    "home.how.subtitle": "In drie simpele stappen ben je klaar voor je kringloopavontuur.",
    "home.how.step1.title": "1. Ontdek",
    "home.how.step1.desc": "Blader door honderden community routes of zoek kringlopen bij jou in de buurt.",
    "home.how.step2.title": "2. Plan",
    "home.how.step2.desc": "Maak je eigen route met je favoriete winkels, of volg een bestaande route.",
    "home.how.step3.title": "3. Deel",
    "home.how.step3.desc": "Deel je route met anderen en post je mooiste pareltjes in de community.",
    "home.insider.badge": "VintMap Insider",
    "home.insider.titlePrefix": "Haal het maximale uit je",
    "home.insider.titleHighlight": "kringloopdag",
    "home.insider.subtitle": "Met VintMap Insider maak je onbeperkt routes, optimaliseer je de volgorde en start je direct vanuit je eigen voordeur. Ontdek meer, reis minder.",
    "home.insider.doorToDoor.title": "Altijd de beste route met VintMap Insider",
    "home.insider.doorToDoor.desc": "Met Insider herbereken je elke route direct vanaf jouw eigen adres. Zo rijd je nooit een meter te veel en ben je altijd via de slimste weg onderweg naar je volgende vintage vondst.",
    "common.offlineMode": "Offline Modus",
    "common.processing": "Optimaliseren...",
    "common.takePhoto": "Camera",
    "common.fromGallery": "Kies uit galerij",
    "common.chooseFiles": "Kies bestanden",
    "common.upload": "Foto uploaden",
    "common.original": "Origineel",
    "common.openFull": "Open origineel",
    "common.preview": "Voorbeeld",
    "common.shareExternally": "Deel extern",
    "common.download": "Downloaden",
    "common.save": "Opslaan",
    "common.search": "Zoeken",
    "home.insider.cta": "Word Insider",
    "home.insider.priceMonth": "/ maand",
    "home.insider.priceYear": "of €29,99 per jaar (2 maanden gratis)",
    "shops.search.placeholder": "Zoek op naam of plaats...",
    "shops.search.button": "Zoek",
    "shops.country.placeholder": "Land",
    "shops.country.all": "Alle landen",
    "shops.province.placeholder": "Provincie",
    "shops.province.all": "Alle provincies",
    "shops.filters.more": "Meer filters",
    "shops.filters.shopOptions": "Winkel opties",
    "shops.filters.payment": "Betaling",
    "shops.filters.card": "Pinnen mogelijk",
    "shops.filters.cashOnly": "Alleen contant",
    "shops.filters.openingHours": "Openingstijden",
    "shops.filters.openNow": "Nu open",
    "shops.filters.openOn": "Open op...",
    "shops.filters.day": "Dag",
    "shops.filters.radius": "Straal",
    "maintenance.title": "Service onderbreking",
    "maintenance.desc": "We ervaren momenteel database problemen. Excuses voor het ongemak.",
    "maintenance.status": "STATUS: OFFLINE",
    "shops.filters.openOnSelectedDay": "Open",
    "shops.filters.day.monday": "Maandag",
    "shops.filters.day.tuesday": "Dinsdag",
    "shops.filters.day.wednesday": "Woensdag",
    "shops.filters.day.thursday": "Donderdag",
    "shops.filters.day.friday": "Vrijdag",
    "shops.filters.day.saturday": "Zaterdag",
    "shops.filters.day.sunday": "Zondag",
    "shops.filters.categories": "Categorieen",
    "shops.filters.priceLevel": "Prijsniveau",
    "shops.filters.parking": "Parkeren",
    "shops.filters.notKnown": "Niet bekend",
    "shops.filters.clear": "Filters wissen",
    "shops.sort.placeholder": "Sorteren",
    "shops.sort.overall": "Overall Rating",
    "shops.sort.vintmap": "VintMap Rating",
    "shops.sort.google": "Google Rating",
    "shops.sort.popular": "Populair",
    "shops.sort.newest": "Nieuwste",
    "shops.sort.nameAsc": "Naam (A-Z)",
    "shops.sort.distance": "Afstand",
    "shops.addShop": "Winkel toevoegen",
    "shops.add": "Toevoegen",
    "shops.results.loading": "Winkels laden...",
    "shops.results.errorTitle": "Fout bij laden",
    "shops.results.errorDesc": "Er is een fout opgetreden bij het laden van de winkels.",
    "shops.results.retry": "Probeer opnieuw",
    "shops.results.withinRadius": "{count} {plural} gevonden binnen {radius} km van {search}",
    "shops.results.inProvince": "{count} {plural} gevonden in {province}",
    "shops.results.showing": "Toont {shown} van {total} resultaten",
    "shops.common.shopSingular": "kringloopwinkel",
    "shops.common.shopPlural": "kringloopwinkels",
    "translate.to": "Vertaal naar {language}",
    "translate.loading": "Vertalen...",
    "translate.failed": "Vertalen mislukt.",
    "translate.loginRequired": "Log in om te vertalen.",
    "shops.empty.title": "Geen kringloopwinkels gevonden",
    "shops.empty.desc": "Er zijn momenteel geen kringloopwinkels in VintMap die binnen je filters vallen.",
    "shops.empty.add": "Voeg kringloopwinkel toe",
    "shops.empty.loginToAdd": "Log in om een winkel toe te voegen",
    "shops.empty.expandSearch": "Verbreed zoekgebied:",
    "shops.pagination.prev": "Vorige",
    "shops.pagination.page": "Pagina",
    "shops.pagination.next": "Volgende",
    "shops.map.loginOnlyTitle": "Kaart alleen voor ingelogde gebruikers",
    "shops.map.loginOnlyDesc": "Login of maak een account aan om de interactieve kaart te bekijken.",
    "shops.map.loginCta": "Login of account maken",
    "currency.symbol": "🪙",
    "rating.overall": "Algehele indruk",
    "rating.atmosphere": "Sfeer",
    "rating.assortment": "Assortiment",
    "rating.price": "Prijs",
    "rating.average": "Gemiddelde score",
    "rating.commentOptional": "Opmerking (optioneel)",
    "rating.commentPlaceholder": "Wat vond je van deze winkel? Deel je ervaring met de community...",
    "rating.photosOptional": "Foto's toevoegen (optioneel)",
    "rating.addPhotos": "Uploaden",
    "rating.editTitle": "Wijzig je review",
    "common.optional": "(Optioneel)",
    "rating.parking": "Parkeren",
    "rating.priceLevel.cheap": "Goedkoop",
    "rating.priceLevel.average": "Gemiddeld",
    "rating.priceLevel.expensive": "Duurder",
    "rating.priceLevel.exclusive": "Exclusief",
    "rating.parkingLevel.bad": "Slecht parkeren",
    "rating.parkingLevel.ok": "Oké",
    "rating.parkingLevel.easy": "Makkelijk parkeren",
    "shops.details": "Kenmerken van de winkel",
    "shops.isCharity": "Goed doel",
    "shops.paymentMethods": "Betaalmogelijkheden",
    "shops.cash": "Contant",
    "shops.card": "Pinnen",
    "shops.categoriesAvailable": "Aanwezige categorieën",
    "rating.title": "Beoordeel",
    "shops.title.region": "Kringloopwinkels {name}",
    "shops.description.region": "Start een route langs de leukste kringloopwinkels van {name}!",
    "rating.desc": "Deel je ervaring en help anderen met het vinden van de leukste winkels.",
    "rating.loginRequired": "Je moet ingelogd zijn om een beoordeling te plaatsen.",
    "rating.allRequired": "Geef voor alle drie de categorieën een beoordeling.",
    "rating.saveFailed": "Fout bij opslaan van beoordeling",
    "rating.genericError": "Er is iets misgegaan. Probeer het opnieuw.",
    "rating.confirmDelete": "Weet je zeker dat je deze beoordeling wilt verwijderen?",
    "rating.deleteFailed": "Fout bij verwijderen van beoordeling",
    "rating.writeReview": "Schrijf een review",
    "categories.Meubels": "Meubels",
    "categories.Kleding & Schoenen": "Kleding",
    "categories.Boeken & Media (LP's, CD's)": "Media",
    "categories.Woondecoratie & Kunst": "Decoratie",
    "categories.Keuken- & Huishoudelijk": "Huishoud",
    "categories.Elektronica (klein)": "Elektronica",
    "categories.Speelgoed & Spellen": "Speelgoed",
    "categories.Sieraden & Accessoires": "Sieraden",
    "categories.Winkelnieuws": "Winkelnieuws",
    "routes.page.title": "Routes",
    "routes.page.favorites": "Favoriete Routes",
    "routes.page.wishlist": "Mijn Bucketlist Routes",
    "routes.page.description": "Ontdek de beste kringlooproutes en vind jouw unieke pareltjes!",
    "routes.new": "Nieuwe Route",
    "routes.mine": "Jouw Routes",
    "routes.more": "Meer ({count} over)",
    "routes.favorites.title": "Favorieten",
    "routes.wishlist.title": "Bucketlist",
    "routes.community.title": "Community Routes",
    "routes.viewAll": "Alles bekijken",
    "routes.empty.favorites": "Nog geen favoriete routes.",
    "routes.empty.wishlist": "Nog geen routes op je bucketlist.",
    "routes.empty.noneIn": "Geen routes gevonden in {name}.",
    "routes.discover": "Ontdek Routes",
    "scanner.title": "Insider Scanner",
    "scanner.subtitle": "Analyseer je pareltjes met VintMap AI.",
    "scanner.myScans": "Mijn Scans",
    "scanner.noPhotos.title": "Geen foto's",
    "scanner.noPhotos.desc": "Upload eerst minimaal een foto van het item.",
    "scanner.error.title": "Scan fout",
    "scanner.invalidResponse": "Scan antwoord is ongeldig. Probeer opnieuw.",
    "scanner.step1.title": "Stap 1: Foto's uploaden",
    "scanner.step1.desc": "Upload max. 4 foto's van verschillende hoeken.",
    "scanner.step2.title": "Stap 2: Objecttype (optioneel)",
    "scanner.step2.desc": "Kies een type voor gerichtere analyse. Laat op \"Onbekend\" staan als je twijfelt.",
    "scanner.category.search": "Zoek objecttype...",
    "scanner.category.empty": "Geen categorie gevonden.",
    "scanner.start.title": "Expert Analyse",
    "scanner.start.desc": "Voor identificatie, herkomst en expert verdict.",
    "scanner.feature.identify": "Expert Identificatie",
    "scanner.feature.value": "Expert Verdict",
    "scanner.feature.links": "Markt Referenties",
    "scanner.tips.title": "Insider Tips",
    "scanner.tips.one": "Zorg voor goed licht en een neutrale achtergrond.",
    "scanner.tips.two": "Maak foto's van merktekens, stempels of labels.",
    "scanner.tips.three": "Upload duidelijke close-ups voor betere herkenning van details en merksporen.",
    "scanner.faqHelp": "Scanner FAQ & Hulp",
    "scanner.feedback.title": "Feedback?",
    "scanner.feedback.desc": "Help ons de AI te verbeteren. Klopt een resultaat niet? Laat het ons weten via de feedback knop in het resultaat.",
    "scanner.usage.month": "deze maand",
    "scanner.insiderCta": "Word Insider voor EUR2,99/maand",
    "scanner.haveAccount": "Al een account? Log hier in",
    "scanner.back": "Terug naar Scanner",
    "scanner.notFound": "Scan niet gevonden",
    "scanner.badge": "Scan",
    "scanner.hasGlass": "Bevat glas",
    "scanner.hasMirror": "Bevat spiegel",
    "scanner.hasWebbing": "Bevat webbing",
    "scanner.scannedOn": "Gescand op",
    "scanner.imageAlt": "Gescand item",
    "scanner.keyPoints": "Kernpunten",
    "scanner.identification": "Identificatie",
    "scanner.rarity": "Zeldzaamheid",
    "scanner.valueTitle": "Marktpositie en Verdict",
    "scanner.valueEstimated": "Expert Verdict",
    "scanner.expertRapport": "Expert Rapport",
    "scanner.expertise": "Expertise en context",
    "scanner.historicalContext": "Historische context",
    "scanner.historicalFallback": "Geen historische context beschikbaar.",
    "scanner.authenticityTags": "Authenticiteit tags",
    "scanner.noMarkers": "Geen markers beschikbaar.",
    "scanner.liveSearch": "Live zoekresultaten (grounding)",
    "scanner.noInsights": "Geen zoekinzichten beschikbaar.",
    "scanner.expertTip": "Expert-tip",
    "scanner.searchTerm": "Professionele zoekterm",
    "scanner.physicalTip": "Fysieke controle-tip",
    "scanner.generatedBy": "Gegenereerd door VintMap AI met Live Google Search. Check altijd externe bronnen.",
    "scanner.startNew": "Nieuwe scan starten",
    "scanner.invalidResultLoad": "Resultaat is ongeldig. Probeer opnieuw.",
    "scanner.history.loadError": "Kon geschiedenis niet ophalen.",
    "scanner.history.deleteError": "Verwijderen mislukt.",
    "scanner.history.deletedTitle": "Scan verwijderd",
    "scanner.history.deletedDesc": "De scan is succesvol verwijderd.",
    "scanner.loginToViewScans": "Log in om je scans te bekijken",
    "scanner.history.title": "Mijn Scans",
    "scanner.history.subtitle": "Een overzicht van al je AI-analyses.",
    "scanner.history.emptyTitle": "Nog geen scans uitgevoerd",
    "scanner.history.emptyDesc": "Gebruik de Insider Scanner om je vintage schatten te analyseren.",
    "scanner.history.firstScan": "Start je eerste scan",
    "scanner.history.openResult": "Open resultaat",
    "scanner.history.resultFallback": "Scan resultaat",
    "scanner.history.viewAnalysis": "Bekijk analyse",
    "scanner.summary.valueRange": "Expert Verdict beschikbaar",
    "scanner.noSpecificFeatures": "Geen specifieke kenmerken gevonden.",
    "scanner.marketValueCheck": "Markt Referenties",
    "scanner.checkEbay": "Check eBay",
    "scanner.checkEtsy": "Check Etsy",
    "scanner.checkGoogle": "Google Afbeeldingen",
    "scanner.summary.valueFrom": "Expert Verdict beschikbaar",
    "scanner.summary.shortAnalysis": "Korte AI-analyse beschikbaar",
    "account.loginRequired": "Inloggen vereist",
    "account.goToLogin": "Naar login",
    "account.logout": "Uitloggen",
    "account.tabs.profile": "Profiel",
    "account.tabs.reviews": "Reviews",
    "account.tabs.shopAdmin": "Winkelbeheer",
    "account.tabs.settings": "Instellingen",
    "account.tabs.privacy": "Privacy",
    "account.languagePreference": "Taalvoorkeur",
    "account.languageSaved": "Wordt opgeslagen voor volgende bezoeken.",
    "account.settings.title": "Mijn gegevens",
    "account.settings.desc": "Beheer je persoonlijke voorkeuren",
    "account.username": "Gebruikersnaam",
    "account.save": "Opslaan",
    "account.email": "E-mailadres",
    "account.edit": "Wijzigen",
    "account.send": "Verstuur",
    "account.newEmail": "Nieuw e-mailadres",
    "account.confirmationNote": "Je ontvangt een bevestigingsmail op zowel je oude als nieuwe adres.",
    "account.homeAddress": "Home adres (zoek op postcode of straat)",
    "account.homeAddressPlaceholder": "Typ je adres of postcode...",
    "account.loading": "Laden...",
    "account.insiderOnly": "Alleen beschikbaar voor Insiders",
    "account.subscription": "Abonnement",
    "account.currentStatus": "Je huidige status: {tier}",
    "account.tier.free": "Gratis",
    "account.tier.insider": "Insider",
    "account.becomeInsider": "Word Insider",
    "account.manageSubscription": "Beheer abonnement",
    "account.ownerBadge": "Winkeleigenaar",
    "account.profileUpdated": "Profielfoto bijgewerkt",
    "account.profileUpdatedDesc": "Je nieuwe profielfoto is succesvol opgeslagen.",
    "account.uploadFailed": "Upload mislukt",
    "account.uploadFailedDesc": "Er ging iets mis tijdens het uploaden.",
    "account.addressSaved": "Adres opgeslagen",
    "account.addressSavedDesc": "Je thuisadres (inclusief coordinaten) is bijgewerkt.",
    "account.addressDeleted": "Adres verwijderd",
    "account.addressDeletedDesc": "Je thuisadres is gewist.",
    "account.myFinds": "Pareltjes",
    "account.myGems": "Mijn pareltjes (community)",
    "account.manageGems": "Beheer pareltjes (community)",
    "account.manageWishlist": "Beheer wishlist",
    "account.noItemsFound": "Nog geen items gevonden.",
    "account.goWishlistHint": "Ga naar je wishlist om items af te vinken.",
    "account.favoriteShops": "Favoriete kringloopwinkels",
    "account.shopBucketlist": "Bucketlist (winkels)",
    "account.myContributions": "Mijn bijdragen",
    "account.favoriteRoutes": "Favoriete routes",
    "account.routeBucketlist": "Bucketlist routes",
    "account.myCreatedRoutes": "Mijn gemaakte routes",
    "account.myAiScans": "Mijn AI scans",
    "account.myTopRoutes": "Meest gereden routes",
    "account.noRoutesDriven": "Je hebt nog geen routes gereden.",
    "account.startRoute": "Start een route",
    "account.recentReviews": "Recente reviews",
    "account.noReviews": "Nog geen reviews geschreven.",
    "account.unknownShop": "Onbekende winkel",
    "account.openPost": "Bekijk post",
    "account.viewResult": "Resultaat bekijken",
    "account.foundOn": "Gevonden op",
    "account.insiderActive": "Je bent een Insider!",
    "account.error.notLoggedIn": "Niet ingelogd",
    "account.error.portalLoadFailed": "Kon portaal niet laden",
    "account.error.usernameEmpty": "Gebruikersnaam mag niet leeg zijn",
    "account.error.updateUsernameFailed": "Gebruikersnaam kon niet worden bijgewerkt",
    "account.usernameUpdated": "Gebruikersnaam bijgewerkt",
    "account.usernameUpdatedDesc": "Je nieuwe gebruikersnaam is opgeslagen.",
    "account.error.saveFailed": "Fout bij opslaan",
    "account.error.invalidEmail": "Ongeldig e-mailadres",
    "account.error.invalidEmailDesc": "Voer een geldig e-mailadres in.",
    "account.emailConfirmationSent": "Bevestigingsmail verstuurd",
    "account.emailConfirmationSentDesc": "Check zowel je oude als nieuwe e-mailadres om de wijziging te bevestigen.",
    "account.error.changeFailed": "Fout bij wijzigen",
    "account.error.noActiveSession": "Geen actieve sessie gevonden",
    "account.error.updateProfileFailed": "Profiel kon niet worden bijgewerkt",
    "account.error.fetchDashboardFailed": "Dashboard kon niet worden geladen",
    "account.error.fetchMyRoutesFailed": "Mijn routes konden niet worden geladen",
    "account.error.notAuthenticated": "Niet geauthenticeerd",
    "account.error.deleteRouteFailed": "Route kon niet worden verwijderd",
    "account.routeDeleted": "Route verwijderd",
    "account.routeDeletedDesc": "Je route is succesvol verwijderd.",
    "account.error.deleteFailed": "Fout bij verwijderen",
    "account.emptyShopBucketlist": "Je hebt nog geen winkels op je bucketlist.",
    "account.emptyContributions": "Je hebt nog geen winkels toegevoegd.",
    "account.emptyRouteBucketlist": "Je hebt nog geen routes op je bucketlist.",
    "account.emptyCreatedRoutes": "Je hebt nog geen routes gemaakt.",
    "account.analyzing": "Analyseren...",
    "account.emptyScans": "Je hebt nog geen scans gemaakt.",
    "account.unnamedRoute": "Naamloze route",
    "account.routeDrivenCount": "{count}x gereden",
    "account.insiderUpgradeText": "Upgrade naar Insider voor onbeperkt routes, Social Wishlist{restyleSuffix} en deur-tot-deur navigatie.",
    "account.insiderUpgradeRestyleSuffix": ", Meubel Restyler",
    "account.dangerZone": "Gevarenzone",
    "account.deleteAccount": "Account verwijderen",
    "account.deleteAccountDesc": "Verwijder je account en al je persoonlijke gegevens permanent. Dit kan niet ongedaan worden gemaakt.",
    "account.deleteConfirmTitle": "Weet je het zeker?",
    "account.deleteConfirmDesc": "Er wordt een bevestigingsmail gestuurd naar je e-mailadres. Je moet op de link in die mail klikken om de verwijdering definitief te maken.",
    "account.deleteWarning": "Let op: Je moet actieve abonnementen eerst opzeggen via het Stripe portaal.",
    "account.deleteConfirm": "Er wordt een bevestigingsmail gestuurd naar je e-mailadres. Je moet op de link in die mail klikken om de verwijdering definitief te maken.",
    "account.deleteWarningDesc": "Let op: Je moet actieve abonnementen eerst opzeggen via het Stripe portaal.",
    "account.deleteRequestSuccess": "Bevestigingsmail verzonden. Controleer je inbox (en spam) om de verwijdering te voltooien.",
    "account.deleteError": "Fout bij aanvragen van verwijdering",
    "account.subscriptionActiveError": "Je hebt nog actieve abonnementen of zit nog in een betaalde periode. Zeg deze eerst op via 'Beheer abonnement' en wacht tot de periode is afgelopen.",
    "account.confirmDeleteTitle": "Verwijdering Bevestigen",
    "account.confirmDeleteDesc": "Klik op de knop hieronder om je account definitief te verwijderen. Dit kan niet ongedaan worden gemaakt.",
    "account.confirmDeleteButton": "Account Definitief Verwijderen",
    "account.confirmDeleteSuccess": "Je account is verwijderd.",
    "account.confirmDeleteError": "Fout bij bevestigen van verwijdering. De link is mogelijk verlopen.",
    "account.invalidLink": "Ongeldige link",
    "account.invalidLinkDesc": "Deze link is niet geldig of verlopen.",
    "account.deletedTitle": "Account verwijderd",
    "account.deletedMessage": "Je account en bijbehorende gegevens zijn succesvol verwijderd. Bedankt dat je deel uitmaakte van de VintMap community.",
    "account.backToHome": "Terug naar home",
    "account.devDowngrade": "(Dev) Downgrade",
    "faq.q1": "Wat is VintMap?",
    "faq.a1": "VintMap is hét platform voor kringloop- en vintageliefhebbers. Je ontdekt eenvoudig de leukste kringloopwinkels, plant routes, en deelt je vondsten met de community.",
    "faq.q2": "Is VintMap gratis te gebruiken?",
    "faq.a2": "Ja, het gebruik van VintMap is gratis voor bezoekers. Je kunt gratis zoeken, routes bekijken en winkels vinden. Voor extra functies zoals 'Mijn Routes' (onbeperkt opslaan) bieden we een Insider abonnement aan.",
    "faq.q3": "Is er een app beschikbaar?",
    "faq.a3": "VintMap is een Progressive Web App (PWA). This means you can 'install' the website via your browser on your phone as an app, without going through the App Store or Play Store. You'll also get an icon on your home screen.",
    "faq.q4": "Kan ik mijn vondsten delen?",
    "faq.a4": "Ja, via 'Deel je buit' kun je foto's van je mooiste aankopen delen met de community. Andere gebruikers kunnen deze liken en reageren.",
    "faq.q5": "Zijn er limieten aan het gebruik van de kaart?",
    "faq.a5": "Ja, om VintMap gratis en duurzaam te houden voor iedereen, hanteren we een Fair Use policy voor interactieve onderdelen. Voor Insiders geldt een ruime limiet van 20 interactieve kaartweergaven per dag.",
    "loginModal.send": "Inloggen",
    "notFound.title": "Pagina niet gevonden",
    "notFound.desc": "Oeps! De pagina die je zoekt lijkt niet te bestaan. Mogelijk is de link verouderd of is de pagina verplaatst.",
    "restyle.aiDisclaimer": "AI designs zijn ter inspiratie en kunnen afwijken van de werkelijkheid.",
    "restyle.descriptionPlaceholder": "Bijv: Oude houten kast gevonden in Utrecht, nu in mat zwart met gouden grepen.",
    "restyle.errorDesc": "Er is een fout opgetreden bij het genereren van je restyle. Probeer het opnieuw.",
    "restyle.kiesGreep": "Kies grepen...",
    "restyle.optimizingSubject": "Meubel optimaliseren...",
    "routeBuilder.calculateNow": "Nu berekenen",
    "routeBuilder.mapPreviewPending": "Kaart wordt geladen...",
    "routeBuilder.mapPreviewPendingDesc": "We berekenen de beste route voor je.",
    "routeBuilder.reversedWithAddressesDesc": "De route is omgedraaid, inclusief je opgegeven start- en eindadres.",
    "scanner.marketItems": "Markt Referenties",
    "scanner.noMarketLinks": "Geen directe marktlinks gevonden voor dit specifieke item.",
    "faq.visitors.q3": "Waarom navigeer ik in Google Maps altijd vanaf mijn huidige locatie in plaats van mijn Startadres?",
    "faq.visitors.a3": "Het door jou opgegeven startadres dient op VintMap puur om de optimale route of volgorde te berekenen, zodat je dit als voorbeeld kan bekijken. Wanneer je de route daadwerkelijk in Google Maps (of een andere app) opent, start de navigatie standaard altijd vanaf jouw huidige locatie, want dit is hoe Google Maps werkt. Belangrijk: dit betekent NIET dat VintMap jouw exacte locatie in kan zien als je deze niet expliciet met VintMap gedeeld hebt. Alle locatiegegevens voor de actieve navigatie blijven volledig privé bij Google Maps op je eigen toestel.",
    "restyle.floor.dark_gray_tiles": "Donkergrijze tegels",
    "common.viewAll": "Bekijk alles ({count})",
    "common.showLess": "Toon minder",
    "common.showAll": "Toon alles ({count})",
    "common.closed": "Gesloten",
    "common.error": "Fout",
    "common.delete": "Verwijderen",
    "common.details": "Details",
    "common.view": "Bekijk",
    "common.edit": "Wijzig",
    "common.close": "Sluiten",
    "common.chooseFile": "Bestand kiezen",
    "common.back": "Terug",
    "common.share": "Deel",
    "common.loading": "Laden...",
    "common.anonymous": "Anonieme Schatzoeker",
    "shops.reviews": "Reviews",
    "shops.noReviewsYet": "Nog geen reviews",
    "addShop.useCurrentLocation": "Gebruik huidige locatie",
    "addShop.loginRequiredDesc": "Alleen ingelogde leden kunnen nieuwe winkels toevoegen aan VintMap. Log in of maak een account aan om te helpen de route te verbeteren.",
    "addShop.intro": "Help de community en voeg je favoriete kringloopwinkel toe aan de route!",
    "addShop.searchTitle": "Winkel zoeken",
    "addShop.foundShops": "Gevonden winkels",
    "addShop.selectShopPrompt": "Selecteer de winkel die je wilt toevoegen.",
    "addShop.orViaGoogle": "Of via Google Link",
    "addShop.googleMapsLink": "Link naar Google Maps",
    "addShop.pasteLinkHint": "Plak de link voor automatische herkenning.",
    "addShop.cantFindPrompt": "Lukt het niet?",
    "addShop.manualEntry": "Handmatig invullen",
    "addShop.detailsTitle": "Winkel gegevens",
    "addShop.detailsDesc": "Controleer of de gegevens kloppen",
    "addShop.nameLabel": "Naam van de winkel *",
    "addShop.namePlaceholder": "bijv. De Kringloper",
    "addShop.addressLabel": "Adres *",
    "addShop.addressPlaceholder": "Straatnaam en huisnummer",
    "addShop.cityLabel": "Stad *",
    "addShop.postalCodeLabel": "Postcode",
    "addShop.provinceLabel": "Provincie",
    "addShop.multiplePlaces": "Meerdere plaatsen gevonden",
    "addShop.multiplePlacesPrompt": "Er zijn meerdere plaatsen met de naam \"{city}\". Kies de juiste provincie:",
    "addShop.openingHoursTitle": "Openingstijden",
    "addShop.openingHoursDesc": "Wanneer is de winkel open?",
    "addShop.openingHoursNotesLabel": "Uitzonderingen of toelichting",
    "addShop.openingHoursNotesPlaceholder": "bijv: Alleen open op afspraak...",
    "addShop.featuresTitle": "Kenmerken en categorieen",
    "addShop.featuresDesc": "Wat kunnen bezoekers hier vinden?",
    "addShop.charity": "Goed doel",
    "addShop.horeca": "Horeca/Koffiecorner",
    "addShop.cardPayment": "Pinnen mogelijk",
    "addShop.cashPayment": "Contant betalen",
    "addShop.categories": "Assortiment",
    "addShop.photosTitle": "Foto's toevoegen",
    "addShop.photosDesc": "Maximaal 4 foto's voor een snelle eerste indruk",
    "addShop.facadeTitle": "Gevelfoto (vereist voor beste zichtbaarheid)",
    "addShop.facadePreviewAlt": "Gevel preview",
    "addShop.makeFacadePhoto": "Maak gevelfoto",
    "addShop.facadeHint": "Deze foto tonen we als eerste. Bij onduidelijke gevel komt de foto in handmatige controle.",
    "addShop.sharePhotosTitle": "Deel winkelfoto's",
    "addShop.sharePhotosDesc": "Max 4 foto's - Automatisch geoptimaliseerd",
    "addShop.previewAlt": "Preview",
    "addShop.instantAddTitle": "Directe toevoeging mogelijk",
    "addShop.instantAddDesc": "Omdat deze winkel op Google Maps staat, kunnen we deze direct toevoegen aan het overzicht.",
    "addShop.manualReviewTitle": "Wordt nagekeken",
    "addShop.manualReviewDesc": "Winkels die we niet direct kunnen verifieren worden eerst door ons team gecontroleerd.",
    "addShop.shopExistsTitle": "Winkel bestaat al",
    "addShop.shopExistsDesc": "\"{name}\" staat al in onze database.",
    "addShop.fetchDetailsFailed": "Kon details niet ophalen.",
    "addShop.detailsFilledTitle": "Gegevens ingevuld!",
    "addShop.detailsFilledDesc": "Controleer de gegevens en voeg toe.",
    "addShop.fetchNearbyFailed": "Kon buurtwinkels niet ophalen",
    "addShop.noNearbyTitle": "Geen winkels gevonden",
    "addShop.noNearbyDesc": "Geen kringloopwinkels in de buurt gevonden (radius 100m).",
    "addShop.locationErrorTitle": "Locatie fout",
    "addShop.locationErrorDesc": "Sta locatie toegang toe om winkels in de buurt te vinden.",
    "addShop.notSupportedTitle": "Niet ondersteund",
    "addShop.notSupportedDesc": "Locatiebepaling wordt niet ondersteund.",
    "addShop.linkNotResolved": "Kon geen gegevens vinden voor deze link.",
    "addShop.shopFoundTitle": "Winkel gevonden!",
    "addShop.shopFoundDesc": "De gegevens zijn automatisch ingevuld.",
    "addShop.searchErrorTitle": "Fout bij zoeken",
    "addShop.incompleteTitle": "Incomplete gegevens",
    "addShop.incompleteDesc": "Naam, adres en stad zijn verplicht.",
    "addShop.facadeUploadFailed": "Gevel-foto upload mislukt.",
    "addShop.photoUploadFailed": "Foto upload mislukt.",
    "addShop.shopExistsOverview": "Deze winkel staat al in ons overzicht. Je kunt hem direct bekijken.",
    "addShop.viewShop": "Bekijk winkel",
    "addShop.saveShopFailed": "Fout bij opslaan winkel.",
    "addShop.thanksTitle": "Bedankt!",
    "addShop.directAddedDesc": "De winkel is direct toegevoegd.",
    "addShop.submittedReviewDesc": "De winkel is ingestuurd voor controle.",
    "addShop.submitErrorTitle": "Fout bij verzenden",
    "claimShop.notFound": "Winkel niet gevonden",
    "claimShop.loginRequiredDesc": "Je moet ingelogd zijn om een winkel te claimen.",
    "claimShop.sessionExpired": "Sessie verlopen. Log opnieuw in aub.",
    "claimShop.paymentLinkFailed": "Betaallink aanmaken mislukt",
    "claimShop.noPaymentLink": "Geen betaallink ontvangen",
    "claimShop.title": "Claim je winkel",
    "claimShop.aboutToClaim": "Je staat op het punt om",
    "claimShop.aboutToClaimSuffix": "te claimen.",
    "claimShop.confirmDetails": "Bevestig je winkelgegevens",
    "claimShop.addressNotice": "Let op: We sturen de verificatiebrief en sticker naar dit adres.",
    "claimShop.featureLetterTitle": "Verificatiebrief en sticker",
    "claimShop.featureLetterDesc": "Ontvang een fysieke brief met een unieke code en een VintMap raamsticker.",
    "claimShop.featureOwnerTitle": "Eigenaar profiel",
    "claimShop.featureOwnerDesc": "Krijg toegang tot je eigen beheeromgeving om foto's en openingstijden bij te werken.",
    "claimShop.oneTimeCost": "Eenmalige kosten",
    "claimShop.goToPayment": "Naar betaling",
    "claimShop.stripeNotice": "Beveiligde betaling via Stripe. Je claim wordt verwerkt na succesvolle betaling.",
    "ownersKringloop.howTitle": "Hoe werkt het?",
    "ownersKringloop.how.step1.title": "Vind je winkel",
    "ownersKringloop.how.step1.desc": "Zoek je winkel op in onze database met honderden kringloopwinkels.",
    "ownersKringloop.how.step2.title": "Claim je profiel",
    "ownersKringloop.how.step2.desc": "Klik op \"Claim deze winkel\" en verifieer dat jij de eigenaar bent.",
    "ownersKringloop.how.step3.title": "Beheer je winkel",
    "ownersKringloop.how.step3.desc": "Voeg foto's toe, update openingstijden en bereik meer klanten.",
    "ownersKringloop.badge": "Voor kringloopwinkels",
    "ownersKringloop.title": "Zet je kringloopwinkel in de spotlight",
    "ownersKringloop.subtitle": "Claim je winkel en bereik duizenden kringloopliefhebbers. Word vindbaar in routes en zoekresultaten, en bouw aan je online community.",
    "ownersKringloop.findShop": "Vind je winkel",
    "ownersKringloop.everythingTitle": "Alles wat je nodig hebt",
    "ownersKringloop.free.title": "Gratis basis profiel",
    "ownersKringloop.free.desc": "Elke kringloopwinkel in Nederland heeft automatisch een gratis profiel op VintMap.",
    "ownersKringloop.free.feature1": "Basis vermelding met naam en adres",
    "ownersKringloop.free.feature2": "Zichtbaar in routes en zoekresultaten",
    "ownersKringloop.free.feature3": "Reviews en beoordelingen ontvangen",
    "ownersKringloop.free.feature4": "Community posts van bezoekers",
    "ownersKringloop.premium.title": "Premium features",
    "ownersKringloop.premium.badge.title": "Geverifieerd badge",
    "ownersKringloop.premium.badge.desc": "Laat bezoekers zien dat jouw winkel echt is met een officiele badge.",
    "ownersKringloop.premium.photos.title": "Eigen foto's",
    "ownersKringloop.premium.photos.desc": "Upload foto's van je winkel, producten en interieur.",
    "ownersKringloop.premium.hours.title": "Openingstijden beheren",
    "ownersKringloop.premium.hours.desc": "Houd je openingstijden actueel zodat bezoekers weten wanneer je open bent.",
    "ownersKringloop.premium.promotions.title": "Acties plaatsen",
    "ownersKringloop.premium.promotions.desc": "Plaats aanbiedingen en acties om meer bezoekers te trekken.",
    "ownersKringloop.premium.website.title": "Website link",
    "ownersKringloop.premium.website.desc": "Voeg een link naar je website of social media toe.",
    "ownersKringloop.premium.posting.title": "Posten als winkel",
    "ownersKringloop.premium.posting.desc": "Plaats posts met je winkelnaam en eigenaar-badge.",
    "ownersKringloop.premiumAccessTitle": "Premium Toegang",
    "ownersKringloop.oneTimeInvestment": "Eenmalige investering",
    "ownersKringloop.oneTime": "eenmalig",
    "ownersKringloop.includesSticker": "Inclusief fysieke verificatiebrief en officiele raamsticker",
    "ownersKringloop.whatYouGet": "Wat krijg je:",
    "ownersKringloop.andMore": "En nog veel meer...",
    "ownersKringloop.claimShop": "Claim je winkel",
    "ownersKringloop.noMonthlyCosts": "Geen maandelijkse kosten. Levenslange toegang.",
    "ownersSelection.title": "Voor eigenaren",
    "ownersSelection.subtitle": "Krijg meer zichtbaarheid en beheer je profiel op VintMap.",
    "ownersSelection.cardTitle": "Kringloopwinkel",
    "ownersSelection.cardDesc": "Claim je kringloopwinkel, update je openingstijden en word onderdeel van de populairste routes.",
    "ownersSelection.cta": "Ik ben een winkeleigenaar",
    "restyleHistory.loginRequiredDesc": "Log in om je restyle projecten te bekijken.",
    "restyleHistory.toLogin": "Naar inloggen",
    "restyleHistory.backToRestyle": "Terug naar Restyle",
    "restyleHistory.title": "Mijn Restyle projecten",
    "restyleHistory.subtitle": "Overzicht van je restyles, met voor en na naast elkaar.",
    "restyleHistory.filterTitle": "Filter op maand en jaar",
    "restyleHistory.year": "Jaar",
    "restyleHistory.month": "Maand",
    "restyleHistory.empty": "Geen restyles gevonden voor deze periode.",
    "restyleHistory.openResult": "Open resultaat",
    "restyleHistory.before": "Origineel",
    "restyleHistory.after": "Na",
    "restyle.before": "Origineel",
    "restyle.after": "Na",
    "restyleHistory.beforeImageAlt": "Voor foto",
    "restyleHistory.afterImageAlt": "Restyle resultaat",
    "routeBuilder.saveSuccessTitle": "Succes!",
    "routeBuilder.saveSuccessDesc": "Je route is succesvol opgeslagen.",
    "routeBuilder.saveErrorTitle": "Fout bij opslaan",
    "routeBuilder.tooFewShopsTitle": "Te weinig winkels",
    "routeBuilder.tooFewShopsDesc": "Een openbare route moet minimaal 4 winkels bevatten.",
    "routeBuilder.noHomeTitle": "Huisadres ontbreekt",
    "routeBuilder.noHomeDesc": "Vul je huisadres in bij je accountinstellingen om deze functie te gebruiken.",
    "routeBuilder.personalizingTitle": "Route personaliseren...",
    "routeBuilder.personalizingDesc": "Je huisadres is ingesteld en we optimaliseren de route.",
    "routeBuilder.homeSetTitle": "Huisadres ingevuld",
    "routeBuilder.homeSetDesc": "Je huisadres is automatisch als start- en eindadres ingesteld.",
    "routeBuilder.loginTitle": "Maak een account aan",
    "routeBuilder.loginDesc": "Je moet ingelogd zijn om routes te kunnen maken. Maak een gratis account aan om te beginnen.",
    "routeBuilder.toAccountPage": "Naar account pagina",
    "routeBuilder.reversedTitle": "Route omgedraaid",
    "routeBuilder.reversedDesc": "De volgorde van de winkels is omgedraaid.",
    "routeBuilder.clearConfirm": "Weet je zeker dat je de route wilt leegmaken? Alle winkels en adressen worden verwijderd.",
    "routeBuilder.clearedTitle": "Route leeggemaakt",
    "routeBuilder.clearedDesc": "Je kunt nu met een schone lei beginnen.",
    "routeBuilder.tooFewStopsTitle": "Te weinig stops",
    "routeBuilder.tooFewStopsDesc": "Voeg minimaal 3 winkels toe om te optimaliseren.",
    "routeBuilder.dailyLimitTitle": "Daglimiet bereikt",
    "routeBuilder.dailyLimitDesc": "Je hebt de daglimiet van {limit} optimalisaties bereikt. Probeer het morgen weer!",
    "routeBuilder.monthlyLimitTitle": "Maandlimiet bereikt",
    "routeBuilder.monthlyLimitDesc": "Je hebt de maandlimiet van {limit} optimalisaties bereikt. Probeer het volgende maand weer!",
    "routeBuilder.alreadyOptimalTitle": "Reeds optimaal",
    "routeBuilder.alreadyOptimalDesc": "Niet genoeg tussenstops om te optimaliseren.",
    "routeBuilder.optimizedTitle": "Route geoptimaliseerd",
    "routeBuilder.optimizedDesc": "Nieuwe afstand: {distance}",
    "routeBuilder.optimizeFailedDesc": "Kon route niet optimaliseren.",
    "routeBuilder.backToRoutes": "Terug naar routes",
    "routeBuilder.editRoute": "Route aanpassen",
    "routeBuilder.newRoute": "Nieuwe route maken",
    "routeBuilder.mapTitle": "Route kaart",
    "routeBuilder.openMapsRoute": "Open Maps route",
    "routeBuilder.selectedStops": "Geselecteerde stops",
    "routeBuilder.noStopsYet": "Nog geen winkels toegevoegd aan je route.",
    "routeBuilder.searchShops": "Winkels zoeken",
    "routeBuilder.day": "Dag",
    "routeBuilder.addShop": "Kringloop toevoegen",
    "routeBuilder.openGoogleMaps": "Open Google Maps",
    "routeBuilder.recalculatePrompt": "Je hebt je route gewijzigd. Klik op herberekenen om afstand en route opnieuw op te halen.",
    "routeBuilder.recalculate": "Herbereken",
    "routeBuilder.detailsTitle": "Route details",
    "routeBuilder.nameLabel": "Route naam",
    "routeBuilder.namePlaceholder": "Geef je route een naam...",
    "routeBuilder.distance": "Afstand",
    "routeBuilder.travelTime": "Reistijd",
    "routeBuilder.descriptionOptional": "Beschrijving (optioneel)",
    "routeBuilder.descriptionPlaceholder": "Wat maakt deze route leuk?",
    "routeBuilder.reverse": "Omdraaien",
    "routeBuilder.clear": "Leegmaken",
    "routeBuilder.clearShops": "Verwijder alle winkels",
    "routeBuilder.clearShopsConfirm": "Weet je zeker dat je alle winkels uit je route wilt verwijderen?",
    "routeBuilder.personalizeBtn": "Personaliseer deze route",
    "routeBuilder.personalizeDesc": "Pas de volgorde aan, voeg winkels toe of verwijder ze.",
    "routeBuilder.personalizeUpgradeTitle": "Insider feature",
    "routeBuilder.personalizeUpgradeDesc": "Personaliseren is alleen beschikbaar voor Insiders en Power Users. Upgrade om jouw eigen routes samen te stellen!",
    "routeBuilder.saving": "Bezig...",
    "routeBuilder.save": "Opslaan",
    "routeBuilder.sharePublic": "Deel publiek",
    "routeBuilder.startEndTitle": "Start en eindpunt",
    "routeBuilder.startAddressHelp": "Dit startadres is uitsluitend voor de berekening. Zodra geopend, navigeert Google Maps vanaf je huidige locatie. Zie FAQ.",
    "routeBuilder.startAddressOptional": "Startadres (optioneel)",
    "routeBuilder.endAddressOptional": "Eindadres (optioneel)",
    "routeBuilder.autoOptimize": "Auto-optimalisatie",
    "routeBuilder.useHomeOptimize": "Gebruik huisadres en optimaliseer",
    "routeBuilder.optimizeOrder": "Optimaliseer volgorde",
    "routeBuilder.recalculateInfo": "Bereken info opnieuw",
    "routeBuilder.mergePrompt.title": "Samenvoegen met huidige selectie?",
    "routeBuilder.generateRoute": "Maak route",
    "routeBuilder.reverseRoute": "Draai route om",
    "routeBuilder.mergePrompt.description": "Je hebt op dit moment al {count} winkel(s) geselecteerd staan in je tijdelijke lijst. Wil je deze toevoegen aan de geselecteerde route \"{name}\"?",
    "routeBuilder.mergePrompt.yes": "Ja, toevoegen aan deze route",
    "routeBuilder.mergePrompt.no": "Nee, gebruik alleen opgeslagen route",
    "restyle.popupBlockedTitle": "Popup geblokkeerd",
    "restyle.popupBlockedDesc": "Sta popups toe om fullscreen te openen.",
    "restyle.openImageFailedTitle": "Kon afbeelding niet openen",
    "restyle.tryAgain": "Probeer het opnieuw.",
    "restyle.noImageTitle": "Geen afbeelding",
    "restyle.noImageDesc": "Upload eerst een foto van je meubel.",
    "restyle.serverError": "Serverfout",
    "restyle.invalidResponse": "Restyle antwoord is ongeldig. Probeer opnieuw.",
    "restyle.completedTitle": "Restyle voltooid!",
    "restyle.completedDesc": "Je nieuwe ontwerp is klaar.",
    "restyle.generateErrorTitle": "Fout bij genereren",
    "restyle.loginRequiredDesc": "Log in om VintMap Restyle te gebruiken.",
    "restyle.successTitle": "Opgeslagen!",
    "restyle.successDesc": "Je ontwerp is opgeslagen in je archief.",
    "community.restyles": "Community Restyles",
    "community.noRestyles": "Nog geen restyle projecten gevonden. Wees de eerste om jouw project te delen!",
    "restyle.projectNotFound": "Project niet gevonden",
    "restyle.projectDetails": "Project Details",
    "restyle.material": "Materiaal",
    "restyle.color": "Kleur",
    "restyle.pattern": "Patroon",
    "restyle.treatment": "Behandeling",
    "restyle.backToCommunity": "Terug naar Community",
    "restyle.realResult": "Echt Resultaat ✨",
    "restyle.aiDesign": "AI Ontwerp 🎨",
    "restyle.original": "Origineel",
    "restyle.aiProposal": "AI Voorstel",
    "restyle.comments": "Reacties",
    "restyle.addComment": "Voeg een reactie toe...",
    "restyle.noCommentsYet": "Nog geen reacties.",
    "restyle.commentPosted": "Reactie geplaatst",
    "restyle.commentDeleted": "Reactie verwijderd",
    "restyle.noDescription": "Geen beschrijving opgegeven.",
    "restyle.styleSettings": "Stijl Instellingen",
    "restyle.foundAt": "Gevonden bij",
    "restyle.viewProject": "Bekijken",
    "restyle.shareWithCommunity": "Deel met de Community",
    "restyle.publishModalDesc": "Deel je ontwerp of vondst! Voeg details toe om anderen te helpen.",
    "restyle.realResultLabel": "Heb je dit project echt uitgevoerd?",
    "restyle.realResultDesc": "Upload een foto van het ECHTE resultaat!",
    "restyle.foundAtKringloop": "Gevonden bij een Kringloop? (Optioneel)",
    "restyle.shareInCommunity": "Delen in de Community",
    "restyle.pricePaid": "Betaalde prijs",
    "restyle.priceInfo": "Wat heb je betaald voor dit item?",
    "common.description": "Beschrijving",
    "common.cancel": "Annuleren",
    "shops.search.noResults": "Geen winkels gevonden.",
    "restyle.toLogin": "Naar inloggen",
    "restyle.insiderExclusive": "Insider exclusive",
    "restyle.insiderDesc1": "VintMap Restyle is exclusief voor Insiders.",
    "restyle.insiderDesc2": "Upcycle je kringlooppareltjes met AI kracht!",
    "restyle.feature1": "✓ 25 AI designs per maand",
    "restyle.feature2": "✓ Fotorealistisch 1024px",
    "restyle.feature3": "✓ Slimme meeverven optie",
    "restyle.feature4": "✓ Direct resultaat",
    "restyle.becomeInsider": "Word Insider (EUR2,99/m)",
    "restyle.title": "Kringloop Upcycle Studio",
    "spaceblend.title": "SpaceBlend AI",
    "spaceblend.subtitle": "Bekijk je vondst direct in jouw interieur.",
    "spaceblend.start": "Upload",
    "spaceblend.chooseInterior": "Kies je interieur",
    "spaceblend.uploadInterior": "Upload nieuwe kamerfoto",
    "spaceblend.uploadInteriorDesc": "Maak een foto van de plek waar het meubel moet komen.",
    "spaceblend.myInteriors": "Mijn Interieurs",
    "spaceblend.recentRestyles": "JE SCHATTEN",
    "spaceblend.noTreasures": "Nog geen schatten om te blenden.",
    "spaceblend.placeInInterior": "Plaats in mijn interieur",
    "spaceblend.generating": "SpaceBlend is bezig...",
    "spaceblend.preparing": "Meubel voorbereiden...",
    "spaceblend.editor.dragTip": "Sleep het meubel naar de juiste plek.",
    "spaceblend.editor.pinchTip": "Gebruik twee vingers om te schalen.",
    "spaceblend.editor.generate": "Genereer Blend",
    "spaceblend.archive.title": "Mijn Blends",
    "spaceblend.archive.link": "Archief",
    "spaceblend.archive.subtitle": "Je complete geschiedenis van SpaceBlend projecten.",
    "spaceblend.archive.emptyTitle": "Nog geen blends",
    "spaceblend.archive.emptyDesc": "Start je eerste SpaceBlend project om hier je resultaten te zien.",
    "spaceblend.archive.aiBadge": "AI Blend",
    "spaceblend.defaultInteriorName": "Mijn interieur",
    "spaceblend.uploadSuccessTitle": "Succes!",
    "spaceblend.uploadSuccessDesc": "Interieur toegevoegd.",
    "spaceblend.uploadErrorTitle": "Upload mislukt",
    "spaceblend.uploadErrorDesc": "Er is iets misgegaan.",
    "spaceblend.editor.generateSuccessTitle": "Succes!",
    "spaceblend.editor.generateSuccessDesc": "Je blend is gegenereerd.",
    "spaceblend.editor.generateErrorTitle": "Fout",
    "spaceblend.editor.generateErrorDesc": "Genereren mislukt.",
    "spaceblend.editor.rmbgErrorTitle": "Fout",
    "spaceblend.editor.rmbgErrorDesc": "Achtergrond verwijderen mislukt.",
    "spaceblend.editor.back": "Terug",
    "spaceblend.editor.generatingDetails": "VintMap AI combineert je meubel met je interieur. Matcht verlichting en schaduwen...",
    "spaceblend.editor.noRestyles": "Nog geen gerestylede items gevonden.",
    "spaceblend.project.notFound": "Project niet gevonden",
    "spaceblend.project.backToSpaceblend": "Terug naar SpaceBlend",
    "spaceblend.project.back": "Terug",
    "spaceblend.project.share": "Delen",
    "spaceblend.project.download": "Download",
    "spaceblend.project.downloadFailed": "Downloaden mislukt",
    "spaceblend.project.interior": "Interieur",
    "spaceblend.project.composition": "Composition",
    "spaceblend.project.title": "SpaceBlend Project",
    "spaceblend.project.resultTitle": "RESULTAAT",
    "spaceblend.project.defaultDesc": "Nieuw meubel geplaatst in interieur met SpaceBlend AI.",
    "spaceblend.project.report": "Rapporteer",
    "spaceblend.project.createAnother": "Maak nog een blend",
    "spaceblend.project.shareTitle": "Bekijk mijn SpaceBlend!",
    "spaceblend.project.shareText": "Ik heb dit meubel virtueel in mijn huis geplaatst met de VintMap SpaceBlend AI!",
    "spaceblend.usage": "{used} / {limit} blends deze maand",
    "spaceblend.limitReached": "Maandelijkse limiet bereikt",
    "spaceblend.noInteriors": "Upload eerst een foto van je kamer.",
    "spaceblend.maxInteriors": "Maximum aantal interieurs bereikt (20).",
    "restyle.subtitle": "Visualiseer je nieuwe project voordat je begint te schuren.",
    "restyle.tipTitle": "Tip:",
    "restyle.tipText": "Voor het beste resultaat, zorg voor een front-aanzicht van je meubel met goede belichting.",
    "restyle.usageMonth": "{used} / {limit} designs gebruikt deze maand",
    "restyle.usageUnlimited": "Onbeperkt restylen deze maand",
    "restyle.myRestyles": "Mijn restyles",
    "restyle.projectCount": "{count} restyle projecten",
    "restyle.myRestylesCount": "Mijn Restyles ({count})",
    "restyle.fullscreen": "Groot scherm (voor/na)",
    "restyle.openFullscreen": "Open op volledig scherm",
    "restyle.yourDesign": "Jouw ontwerp",
    "restyle.furniturePhoto": "Foto van je meubel",
    "restyle.startRestyle": "Start je Restyle",
    "restyle.takeOrChoosePhoto": "Maak een foto van je meubel of kies er een uit je galerij (Verticaal 2:3 is het beste).",
    "restyle.removingBackground": "We zijn je meubel aan het klaarmaken voor de restyler.",
    "restyle.localProcessing": "Dit gebeurt lokaal op je toestel",
    "restyle.camera": "Upload",
    "restyle.previewAlt": "Preview",
    "restyle.clear": "Wissen",
    "restyle.furnitureColor": "Meubel kleur*",
    "restyle.finishStyle": "Afwerking stijl*",
    "restyle.chooseFinish": "Kies afwerking",
    "restyle.finish.mat": "Mat",
    "restyle.finish.satin": "Satin finish",
    "restyle.finish.gloss": "Glans",
    "restyle.finish.highGloss": "Hoogglans",
    "restyle.finish.matte": "Matte finish",
    "restyle.finish.semi_gloss": "Zijdeglans finish",
    "restyle.finish.high_gloss": "Hoogglans finish",
    "restyle.finish.chalk": "Krijtverf",
    "restyle.finish.patina": "Antiek patina",
    "restyle.finish.chalkPaint": "Krijtverf",
    "restyle.handleOption.na": "Niet van toepassing",
    "restyle.handleOption.original": "Behoud origineel",
    "restyle.woodworkColor": "Houtwerk kleur",
    "restyle.woodworkFinish": "Houtwerk afwerking",
    "restyle.finish.woodVisible": "Houtnerf zichtbaar",
    "restyle.finish.distressed": "Distressed",
    "restyle.floor.oak_boards": "Eiken vloerplanken",
    "restyle.floor.herringbone_oak": "Visgraat parket",
    "restyle.floor.terracotta_tiles": "Terracotta tegels",
    "restyle.floor.travertine_tiles": "Travertijn (Natuursteen)",
    "restyle.floor.concrete_floor": "Betonvloer",
    "restyle.floor.dark_oak_boards": "Donker eiken planken",
    "restyle.floor.carrara_marble": "Carrara Marmer",
    "restyle.handleColor": "Greepjes kleur*",
    "restyle.chooseColor": "Kies kleur",
    "restyle.handleColorOption.keepCurrent": "Behoud huidige",
    "restyle.handleColorOption.gold": "Goud",
    "restyle.handleColorOption.black": "Zwart",
    "restyle.handleColorOption.bronze": "Brons",
    "restyle.handleColorOption.rustCopper": "Roestkoper",
    "restyle.handleColorOption.chrome": "Verchroomd",
    "restyle.handleColorOption.matteBlack": "Matzwart",
    "restyle.handleColorOption.white": "Wit",
    "restyle.handleColorOption.matchFurniture": "Schilder mee in kleur meubel",
    "restyle.handleStyle": "Greepjes stijl*",
    "restyle.chooseStyle": "Kies stijl",
    "restyle.handleStyleOption.keepCurrent": "Behoud huidige",
    "restyle.handleStyleOption.vintage": "Vintage greepjes",
    "restyle.handleStyleOption.modern": "Moderne knoppen",
    "restyle.handleStyleOption.leatherLoops": "Leren lusjes",
    "restyle.handleStyleOption.leatherHandles": "Leren handgrepen",
    "restyle.handleStyleOption.halfMoon": "Halve maan grepen",
    "restyle.handleStyleOption.bar": "Staafgrepen",
    "restyle.handleStyleOption.wooden": "Houten knoppen",
    "restyle.handleStyleOption.porcelain": "Porseleinen knoppen",
    "restyle.handleStyleOption.cup": "Komgrepen",
    "restyle.handleStyleOption.industrial": "Industriele beugels",
    "restyle.typeToggle": "Meubel type",
    "restyle.hout": "Hout / Hard materiaal",
    "restyle.bekleed": "Bekleed (Stof/Leer)",
    "restyle.houtwerk_behouden": "Houtwerk behouden",
    "restyle.houtwerk_verwijderen": "Houtwerk ook schilderen",
    "restyle.meubel_kleur": "Meubel kleur",
    "restyle.achterwand_kleur": "Achterwand kleur",
    "restyle.greepjes": "Greepjes",
    "restyle.niet_toepasselijk": "Niet van toepassing",
    "restyle.houtwerk_behandeling": "Houtwerk behandeling",
    "restyle.hasGlass": "Bevat glas",
    "restyle.hasMirror": "Bevat spiegel",
    "restyle.hasWebbing": "Bevat webbing",
    "restyle.behandeling.behouden": "Behouden (Origineel)",
    "restyle.behandeling.beitsen": "Beitsen (Donkerder)",
    "restyle.behandeling.logen": "Logen/Schuren (Lichter)",
    "restyle.behandeling.schilderen": "Schilderen (In kleur)",
    "restyle.stoffering_kleur": "Kleur stoffering",
    "restyle.meubel_kleur1": "Hoofdkleur",
    "restyle.meubel_kleur2": "Kleur 2",
    "restyle.meubel_kleur3": "Kleur 3",
    "restyle.stof_patroon": "Stof patroon",
    "restyle.print.effen": "Effen",
    "restyle.print.bloemen": "Bloemen",
    "restyle.print.strepen": "Strepen",
    "restyle.print.kleine_bloemetjes": "Fijne bloemetjes",
    "restyle.print.tartan": "Tartan / Plaid",
    "restyle.print.ruit": "Ruit",
    "restyle.print.toile_de_jouy": "Toile de Jouy",
    "restyle.print.custom": "Mijn eigen patroon",
    "restyle.stof_type": "Stof type",
    "restyle.afwerking": "Afwerking",
    "restyle.vloer": "Vloer type",
    "restyle.genereren": "Ontwerp nu",
    "restyle.resultaat": "Resultaat",
    "restyle.upload": "Foto uploaden",
    "restyle.handleStyleOption.apothecary": "Apothekersgrepen",
    "restyle.handleStyleOption.ring": "Ringgrepen",
    "restyle.handleStyleOption.edge": "Minimalistische randgrepen",
    "restyle.handleStyleOption.none": "Geen grepen (push-to-open)",
    "restyle.backwallColor": "Achterwand / muur kleur*",
    "restyle.default.furnitureColor": "Mosgroen",
    "restyle.default.backwall": "Witte stuc",
    "mossGreen": "mosgroen",
    "whiteStucco": "wit stucwerk",
    "restyle.webbing": "Webbing",
    "restyle.webbing.none": "Geen webbing",
    "restyle.webbing.natural": "Naturel (vlechtwerk)",
    "restyle.webbing.painted": "Webbing meeschilderen",
    "restyle.glass.transparent": "Transparant houden",
    "restyle.glass.painted": "Meeschilderen",
    "restyle.glass.webbing": "Vervangen door webbing",
    "restyle.mirror.transparent": "Reflecterend houden",
    "restyle.mirror.painted": "Meeschilderen",
    "restyle.mirror.webbing": "Vervangen door webbing",
    "restyle.backofficeTranslation": "Backoffice vertaling",

    "restyle.outputRatio": "Output verhouding*",
    "restyle.chooseRatio": "Kies verhouding",
    "restyle.ratio.portrait": "Verticaal (1024x1536)",
    "restyle.ratio.square": "Vierkant (1024x1024)",
    "restyle.ratio.landscape": "Liggend (1536x1024)",
    "restyle.generating": "AI Studio is bezig...",
    "restyle.color.bordeaux": "Bordeauxrood",
    "restyle.color.warm_terracotta": "Warm Terracotta",
    "restyle.color.coral": "Koraal",
    "restyle.color.dusty_rose": "Oudroze",
    "restyle.color.ochre": "Okergeel",
    "restyle.color.honey_yellow": "Honinggeel",
    "restyle.color.egg_yellow": "Eigeel",
    "restyle.color.olive_green": "Olijfgroen",
    "restyle.color.moss_green": "Mosgroen",
    "restyle.color.sage_green": "Saliegroen",
    "restyle.color.pine_green": "Dennegroen",
    "restyle.color.teal": "Pauwblauw (Teal)",
    "restyle.color.sky_blue": "Hemelsblauw",
    "restyle.color.royal_blue": "Koningsblauw",
    "restyle.color.lavender": "Lavendel",
    "restyle.color.aubergine": "Aubergine",
    "restyle.color.linen": "Linnen",
    "restyle.color.antique_white": "Antiek Wit",

    "restyle.color.sand_beige": "Zandbeige",
    "restyle.color.taupe": "Taupe",
    "restyle.color.slate_gray": "Leigrijs",
    "restyle.color.charcoal": "Houtskool",
    "restyle.color.jet_black": "Gitzwart",
    "restyle.behandeling.bladgoud": "Bladgoud (vergulden)",
    "restyle.behandeling.bladzilver": "Bladzilver (verzilveren)",
    "restyle.zoom_title": "Vergroting",


    "restyle.aiPainting": "VintMap AI is aan het schilderen...",
    "restyle.generateConcept": "Genereer Restyle concept",
    "restyle.preparing": "Even geduld...",
    "restyle.resultPending": "Jouw nieuwe design verschijnt hier.",

    "restyle.creditInfo": "Kost 1 credit - 25/maand als Insider",
    "restyle.aiResult": "AI resultaat",
    "restyle.analyzing": "Bezig met analyseren...",
    "restyle.analyzingDesc": "Onze Expert AI bekijkt je meubel en genereert een unieke fotorealistische visualisatie van je keuzes.",
    "restyle.aiResultAlt": "AI resultaat",
    "restyle.placeholderTitle": "Jouw AI Restyle concept verschijnt hier",
    "restyle.placeholderDesc": "Upload een foto en kies je kleuren",
    "restyle.archiveTitle": "Mijn Restyle archief",
    "restyle.archiveDesc": "Zoek je restyles terug per maand of jaar (inclusief voor-foto).",
    "restyle.openSeparatePage": "Open aparte pagina",
    "restyle.disclaimer": "AI doet z’n best, maar kleurt soms buiten de lijntjes. Gebruik het als inspiratie!",
    "restyle.verticalTip": "Verticaal (2:3 verhouding) werkt het beste voor meubels.",
    "restyle.optimizing": "AI Optimalisatie...",
    "restyle.fabric.woven_pattern": "Patroon Stof",
    "restyle.fabric.chenille": "Chenille",
    "restyle.fabric.velvet": "Fluweel",
    "restyle.fabric.linen": "Linnen",
    "restyle.fabric.leather": "Leer",
    "restyle.fabric.microsuede": "Microsuède",
    "routeDetail.addedFavorite": "Toegevoegd aan favorieten",
    "routeDetail.removedFavorite": "Verwijderd uit favorieten",
    "routeDetail.addedBucketlist": "Toegevoegd aan bucketlist",
    "routeDetail.removedBucketlist": "Verwijderd uit bucketlist",
    "routeDetail.errorTitle": "Er ging iets mis",
    "routeDetail.loading": "Laden...",
    "routeDetail.notFoundTitle": "Route niet gevonden",
    "routeDetail.notFoundDesc": "Deze route bestaat niet of is verwijderd.",
    "routeDetail.onBucketlist": "{count} op bucketlist",
    "routeDetail.stopsCount": "{count} stops",
    "routeDetail.openInMaps": "Open in Google Maps",
    "routeDetail.share": "Deel",
    "routeDetail.copyAsOwn": "Kopieer als eigen route",
    "routeDetail.editRoute": "Route bewerken",
    "routeDetail.stopsHeading": "Stops ({count})",
    "routeDetail.summary": "Route samenvatting",
    "routeDetail.numberOfStops": "Aantal stops",
    "routeDetail.totalDistance": "Totaal afstand",
    "routeDetail.provinces": "Provincie(s)",
    "routeDetail.cities": "Stad/Steden",
    "routeDetail.unknown": "Onbekend",
    "routeDetail.saved": "Opgeslagen",
    "routeDetail.shopStatus": "Status winkels",
    "routeDetail.dayShort.mon": "Ma",
    "routeDetail.dayShort.tue": "Di",
    "routeDetail.dayShort.wed": "Wo",
    "routeDetail.dayShort.thu": "Do",
    "routeDetail.dayShort.fri": "Vr",
    "routeDetail.dayShort.sat": "Za",
    "routeDetail.dayShort.sun": "Zo",
    "routeDetail.closedShort": "Gesl",
    "routeDetail.personalize": "Personaliseer route",
    "routeDetail.insiderOnly": "Alleen voor Insider leden",
    "routeDetail.routeFinds": "Buit van deze route",
    "routeDetail.shareTitlePrefix": "Kringlooproute",
    "routeDetail.shareTextIntro": "Bekijk deze kringlooproute op VintMap!",
    "restyle.shareTitle": "VintMap Restyle Studio",
    "restyle.shareText": "Kijk mijn meubeltje eens! Gemaakt met VintMap Restyle AI.",
    "routeDetail.distanceUnknown": "Afstand onbekend",
    "routeList.deleteFailed": "Verwijderen mislukt",
    "routeList.deletedTitle": "Route verwijderd",
    "routeList.deletedDesc": "De route is succesvol verwijderd uit je overzicht.",
    "routeList.deleteErrorTitle": "Fout bij verwijderen",
    "routeList.deleteErrorDesc": "Er is een fout opgetreden bij het verwijderen van de route.",
    "routeList.foundCount": "{count} routes gevonden",
    "routeList.searchPlaceholder": "Zoek routes...",
    "routeList.allCities": "Alle steden",
    "routeList.sort.new": "Nieuw",
    "routeList.sort.mostSaved": "Meest opgeslagen",
    "routeList.sort.mostShops": "Meeste winkels",
    "routeList.sort.fewestShops": "Minste winkels",
    "routeList.sort.longest": "Langste route",
    "routeList.sort.shortest": "Kortste route",
    "routeList.noRoutesFound": "Geen routes gevonden",
    "routeList.tryOtherFilters": "Probeer andere zoektermen of filters.",
    "routeList.inYourOverview": "{count} routes in jouw overzicht",
    "routeList.noMyRoutes": "Je hebt nog geen routes aangemaakt.",
    "routeList.makeFirstRoute": "Maak je eerste route",
    "routeList.searchTitle": "Zoek routes",
    "routeList.resultsFor": "{count} resultaten voor \"{search}\"",
    "routeList.noResultsFor": "Geen resultaten voor \"{search}\". Probeer andere zoektermen.",
    "routeList.enterSearch": "Voer een zoekterm in om routes te vinden.",
    "routeList.clearSearchTerms": "Zoektermen wissen",
    "shopReviews.backToShop": "Terug naar winkel",
    "shopReviews.title": "Reviews",
    "shopReviews.forShop": "voor {name}",
    "shopReviews.userFallback": "Gebruiker",
    "shopReviews.atmosphere": "Sfeer",
    "shopReviews.service": "Service",
    "shopReviews.price": "Prijs",
    "shopReviews.empty": "Er zijn nog geen reviews voor deze winkel.",
    "shopDetail.notFoundTitle": "Winkel niet gevonden",
    "shopDetail.notFoundDesc": "Deze winkel bestaat niet of is verwijderd.",
    "shopDetail.backToShops": "Terug naar winkels",
    "shopDetail.backToThriftShops": "Terug naar kringloopwinkels",
    "shopDetail.uploadGem": "Uploaden",
    "shopDetail.camera": "Camera",
    "shopDetail.addToExistingOrNewRoute": "Wil je deze kringloop toevoegen aan je huidige route, of een nieuwe starten?",
    "shopDetail.addToExistingRoute": "Voeg toe aan route",
    "shopDetail.startNewRoute": "Start nieuwe route",
    "shopDetail.temporarilyClosed": "Deze winkel is tijdelijk gesloten",
    "shopDetail.reopenExpectedOn": "Heropening verwacht op:",
    "shopDetail.permanentlyClosed": "Deze winkel staat gemarkeerd als permanent gesloten.",
    "shopDetail.permanentlyClosedDesc": "Hij is niet zichtbaar voor bezoekers. Als de winkel weer open is, kun je hem hier heractiveren.",
    "shopDetail.reopenShop": "Winkel heropenen",
    "shopDetail.adminPanel": "Beheerderspaneel",
    "shopDetail.adminPanelDesc": "Je bent de eigenaar van deze winkel op VintMap.",
    "shopDetail.thriftProfileDashboard": "Kringloop profiel dashboard",
    "shopDetail.editShop": "Winkel bewerken",
    "shopDetail.verificationPending": "Verificatie in afwachting",
    "shopDetail.verificationPendingDesc": "De brief met je verificatiecode is onderweg naar",
    "shopDetail.enterCodeUnlock": "Voer de code hieronder in om alle functies vrij te schakelen.",
    "shopDetail.verificationCode": "Verificatiecode",
    "shopDetail.verificationCodePlaceholder": "Bijv. VINT-1234",
    "shopDetail.testTip": "Test tip",
    "shopDetail.yourCodeIs": "Jouw code is",
    "shopDetail.verifyError": "Fout bij verifieren",
    "shopDetail.congrats": "Gefeliciteerd!",
    "shopDetail.shopFullyVerified": "Je winkel is nu volledig geverifieerd.",
    "shopDetail.verifyCode": "Verifieer code",
    "shopDetail.shopPremiumVisible": "Je winkel is volledig geverifieerd en zichtbaar als premium.",
    "shopDetail.areYouOwner": "Ben jij de eigenaar?",
    "shopDetail.changeAddress": "Adres wijzigen?",
    "shopDetail.improveInfo": "Informatie verbeteren",
    "shopDetail.reportProblem": "Probleem melden?",
    "shopDetail.like": "Like",
    "shopDetail.bucketlist": "Bucketlist",
    "shopDetail.review": "Review",
    "shopDetail.toVisit": "Toegevoegd",
    "shopDetail.toVisitAlt": "Wil ik bezoeken",
    "shopDetail.writeReview": "Schrijf review",
    "shopDetail.route": "Route",
    "shopDetail.shopPhotos": "Foto's van de winkel",
    "shopDetail.shopInfo": "Winkelinformatie",
    "shopDetail.aboutShop": "Over de winkel",
    "shopDetail.address": "Adres",
    "shopDetail.phone": "Telefoonnummer",
    "shopDetail.openingHours": "Openingstijden",
    "shopDetail.noOpeningHoursKnown": "Geen openingstijden bekend",
    "shopDetail.paymentOptions": "Betalingsmogelijkheden",
    "shopDetail.noSpecificCategories": "Geen specifieke categorieen bekend",
    "shopDetail.shareWithFollowers": "Wat wil je delen met je volgers, {name}?",
    "shopDetail.photoVideo": "Foto/Video",
    "shopDetail.photo": "Foto",
    "shopDetail.postUpdate": "Update plaatsen",
    "shopDetail.update": "Update",
    "shopDetail.shopUpdates": "Updates van de winkel",
    "shopDetail.foundGems": "Gevonden pareltjes",
    "shopDetail.shareGem": "Deel pareltje",
    "shopDetail.noGemsShared": "Er zijn nog geen pareltjes gedeeld bij deze winkel.",
    "shopDetail.shareFirstGem": "Deel het eerste pareltje!",
    "shopDetail.inRoutes": "In routes",
    "shopDetail.notInRouteYet": "Deze winkel zit nog niet in een route.",
    "shopDetail.reviews": "Beoordelingen",
    "shopDetail.viewAllReviews": "Bekijk alle reviews",
    "shopDetail.reviewCount": "{count} reviews",
    "shopDetail.noReviewsPosted": "Er zijn nog geen reviews geplaatst.",
    "shopDetail.shopClaimed": "Winkel geclaimd!",
    "shopDetail.claimCongrats": "Gefeliciteerd! Je hebt",
    "shopDetail.claimSuccessSuffix": "succesvol geclaimd.",
    "shopDetail.whatNow": "Wat nu?",
    "shopDetail.whatNow1": "We sturen je verificatiebrief en sticker binnen 2 werkdagen op.",
    "shopDetail.whatNow2": "Je kunt nu direct foto's en openingstijden aanpassen via de \"Bewerk\" knop.",
    "shopDetail.whatNow3": "Binnenkort ontvang je een e-mail met extra tips voor je profiel.",
    "shopDetail.getStarted": "Aan de slag",
    "shopDetail.sharePrefix": "Bekijk",
    "shopDetail.shareSuffix": "op VintMap!",
    "shopDetail.reviewsLabel": "reviews",
    "wishlist.title": "Wishlist",
    "wishlist.subtitle": "Beheer je eigen zoektocht en help anderen hun pareltjes te vinden.",
    "wishlist.mineTab": "Mijn wishlist",
    "wishlist.followingTab": "Wishlists van vrienden",
    "wishlist.mySearchTitle": "Mijn zoektocht",
    "wishlist.mySearchDesc": "Voeg items toe die je zoekt. Vink ze af als je ze hebt gevonden!",
    "wishlist.private": "Privé",
    "wishlist.privateInfo": "Wanneer je jouw wishlist deelt, zullen items die als 'privé' zijn gemarkeerd niet zichtbaar zijn voor anderen.",
    "wishlist.backToSearchResults": "Terug naar resultaten",
    "wishlist.helpSearch": "Help {name} zoeken!",
    "wishlist.alreadyFound": "Al gevonden!",
    "wishlist.iFoundThis": "Ik heb dit gevonden!",
    "wishlist.userNoItems": "{name} heeft nog geen items op de wishlist staan.",
    "wishlist.notFound": "Wishlist niet gevonden.",
    "wishlist.goToOwn": "Ga naar je eigen wishlist",
    "wishlist.loginToFollow": "Log in om deze lijst te volgen en mee te zoeken.",
    "wishlist.searchFriendsTitle": "Vrienden zoeken",
    "wishlist.searchFriendsDesc": "Vind andere vintage liefhebbers en bekijk hun wishlists.",
    "wishlist.searchByUsername": "Zoek op gebruikersnaam...",
    "wishlist.noUsersFound": "Geen gebruikers gevonden.",
    "wishlist.view": "Bekijken",
    "wishlist.follow": "Volgen",
    "wishlist.unfollow": "Ontvolgen",
    "wishlist.unfollowConfirmTitle": "Wishlist ontvolgen?",
    "wishlist.unfollowConfirmDesc": "Je ziet de updates van {name} dan niet meer.",
    "wishlist.followers": "{count} volgers",
    "wishlist.copyLink": "Link kopiëren",
    "wishlist.linkCopied": "Link gekopieerd!",
    "wishlist.shareViaWhatsApp": "WhatsApp",
    "wishlist.shareViaEmail": "E-mail",
    "wishlist.shareMessage": "Bekijk mijn wishlist op VintMap! Help je mee zoeken? {url}",
    "wishlist.sharedTitle": "Wishlist van {name}",
    "wishlist.inviteFriends": "Vrienden uitnodigen",
    "wishlist.inviteDesc": "Deel je wishlist met vrienden zodat ze voor je mee kunnen kijken in de kringloop!",
    "wishlist.followingEmpty": "Je volgt nog geen wishlists.",
    "wishlist.addItem": "Item toevoegen",
    "wishlist.foundSection": "Gevonden ({count})",
    "wishlist.foundSectionEmpty": "Nog niets gevonden – succes met zoeken!",
    "wishlist.toFindSection": "Zoeklijst ({count})",
    "wishlist.pagePrev": "Vorige",
    "wishlist.pageNext": "Volgende",
    "wishlist.pageLabel": "Pagina {page} van {total}",
    "wishlist.saveChanges": "Opslaan",
    "wishlist.loginRequired": "Mijn Wishlist",
    "wishlist.loginRequiredDesc": "Log in om je eigen wishlist aan te maken, te delen met vrienden en items als 'gevonden' te markeren.",
    "emailConfirmed.title": "E-mailadres bevestigd!",
    "emailConfirmed.desc": "Bedankt voor het bevestigen van je e-mailadres. Je kunt nu inloggen op je account.",
    "emailConfirmed.loginTitle": "Log in op VintMap",
    "wishlist.dimensions": "Afmetingen",
    "wishlist.guidePrice": "Richtprijs",
    "wishlist.notes": "Notities",
    "wishlist.sendWhatsApp": "Stuur via WhatsApp",
    "wishlist.foundForFriend": "Hoi {name}, ik heb de {item} voor je gevonden!",
    "wishlist.imageOptional": "Foto (optioneel)",
    "wishlist.dimensionsPlaceholder": "bv. 80×40×120 cm",
    "wishlist.guidePricePlaceholder": "bv. 25",
    "wishlist.notesPlaceholder": "Extra info of opmerkingen",
    "wishlist.editItem": "Item bewerken",
    "wishlist.addedTitle": "Pareltje toegevoegd",
    "wishlist.addedDesc": "Dit pareltje staat nu op je bucketlist!",
    "privacyDialog.title": "Privacy bescherming",
    "privacyDialog.intro": "Je wilt deze route publiekelijk delen, maar je hebt persoonlijke adressen toegevoegd.",
    "privacyDialog.notShared": "De volgende gegevens worden NIET gedeeld:",
    "privacyDialog.startAddress": "Je startadres",
    "privacyDialog.endAddress": "Je eindadres",
    "privacyDialog.shared": "Wat WEL wordt gedeeld:",
    "privacyDialog.sharedItem1": "Route naam en beschrijving",
    "privacyDialog.sharedItem2": "Geselecteerde kringloopwinkels",
    "privacyDialog.sharedItem3": "Volgorde van de winkels",
    "privacyDialog.outro": "Je kunt deze route nog steeds prive bewaren met je persoonlijke adressen, of publiek delen zonder adressen.",
    "privacyDialog.shareWithoutAddresses": "Deel zonder adressen",
    "gems.shareTitle": "Pareltje op VintMap",
    "gems.shareText": "Kijk wat leuk! {caption}",
    "postDetail.title": "Post details",
    "postDetail.fullViewDesc": "Volledige weergave van deze community post inclusief reacties.",
    "postDetail.viewShop": "Bekijk winkel",
    "postDetail.viewRoute": "Bekijk route",
    "postDetail.comments": "Reacties",
    "postDetail.noComments": "Nog geen reacties",
    "postDetail.addComment": "Voeg een reactie toe...",
    "postDetail.loginToComment": "Log in om te reageren",
    "community.pricePaid": "Betaalde prijs",
    "community.currency": "Valuta",
    "community.currency.eur": "EUR (€)",
    "community.currency.gbp": "GBP (£)",
    "community.currency.usd": "USD ($)",
    "community.currency.chf": "CHF (Fr.)",
    "postCard.report": "Rapporteren",
    "postCard.confirmDelete": "Weet je zeker dat je deze post wilt verwijderen?",
    "postCard.noImage": "Geen afbeelding",
    "postComposer.captionRequired": "Beschrijving is verplicht",
    "postComposer.minOnePhotoRequired": "Minimaal 1 foto is verplicht",
    "postComposer.categoryRequired": "Categorie is verplicht",
    "postComposer.loginRequired": "Je moet ingelogd zijn om posts te delen. Log eerst in.",
    "postComposer.uploadFailed": "Upload mislukt",
    "postComposer.createFailed": "Post maken mislukt",
    "postComposer.genericError": "Er is een fout opgetreden. Probeer het opnieuw.",
    "postComposer.placeShopUpdate": "Plaats een winkelupdate",
    "postComposer.shareFind": "Deel je buit",
    "postComposer.shopUpdateDesc": "Deel nieuws, openingstijden of aankondigingen met je volgers.",
    "postComposer.shareDesc": "Deel je mooiste pareltje met de community.",
    "postComposer.messageLabel": "Bericht",
    "postComposer.shopUpdatePlaceholder": "Wat wil je delen met je volgers?",
    "postComposer.findPlaceholder": "Vertel over je pareltje...",
    "postComposer.photosLabel": "Foto's",
    "postComposer.optional": "(optioneel)",
    "postComposer.photosRequiredHint": "* (minimaal 1, maximaal 4)",
    "postComposer.shareFindWithCommunity": "Deel je buit met de community",
    "postComposer.maxPhotosHint": "Max 4 foto's - Automatisch geoptimaliseerd",
    "postComposer.shopLabel": "Winkel",
    "postComposer.selectedShop": "Geselecteerde winkel",
    "postComposer.searchShop": "Zoek winkel...",
    "postComposer.noShopFound": "Geen winkel gevonden. Probeer een andere zoekterm.",
    "postComposer.categoryLabel": "Categorie",
    "postComposer.chooseCategory": "Kies categorie",
    "postComposer.postAs": "Posten als",
    "postComposer.myAccount": "Mijn account",
    "postComposer.userFallback": "user",
    "postComposer.owner": "eigenaar",
    "postComposer.postOfficiallyAsShop": "Je post dit bericht officieel namens de winkel.",
    "postComposer.sharing": "Delen...",
    "postComposer.cropImage": "Afbeelding bijsnijden",
    "editShop.approveFailed": "Goedkeuren mislukt",
    "editShop.approved": "Goedgekeurd",
    "editShop.suggestionApplied": "De suggestie is toegepast.",
    "editShop.removed": "Verwijderd",
    "editShop.suggestionRejected": "De suggestie is afgewezen.",
    "editShop.bannerUploadFailed": "Banner upload mislukt",
    "editShop.saveFailed": "Opslaan mislukt",
    "editShop.updated": "Bijgewerkt",
    "editShop.suggestionSaved": "Suggestie opgeslagen",
    "editShop.shopSaved": "Winkelgegevens zijn succesvol opgeslagen.",
    "editShop.suggestionSavedDesc": "Jouw suggestie is opgeslagen en wacht op goedkeuring.",
    "editShop.noPendingSuggestions": "Er zijn momenteel geen openstaande suggesties.",
    "editShop.communitySuggestionsDesc": "Hieronder vind je verbeteringen die door de community zijn ingestuurd. Keur ze goed om de gegevens op je profiel direct bij te werken.",
    "editShop.submittedOn": "Ingezonden op",
    "editShop.addressOnlyDesc": "Kloppen de adresgegevens niet? Geef hier de juiste gegevens door. Een moderator zal de wijziging controleren.",
    "editShop.streetNumber": "Straat en huisnummer",
    "editShop.city": "Plaats",
    "editShop.name": "Naam",
    "editShop.website": "Website",
    "editShop.aboutShop": "Over de winkel",
    "editShop.bannerImage": "Banner afbeelding",
    "editShop.setBanner": "Banner instellen",
    "editShop.bannerVerifiedOnly": "Banners zijn alleen beschikbaar voor geverifieerde kringloopwinkels.",
    "editShop.featuresPayment": "Kenmerken en betaling",
    "editShop.shopPhotos": "Winkelfoto's ({count} / {max})",
    "editShop.facadeFirst": "Gevel-foto (wordt altijd als eerste getoond)",
    "editShop.addFacadePhoto": "Gevel-foto toevoegen",
    "editShop.ownerDesc": "Beheer je winkelgegevens en bekijk suggesties van bezoekers.",
    "editShop.communityDesc": "Help mee de informatie actueel te houden.",
    "editShop.suggestions": "Suggesties",
    "editShop.submitSuggestion": "Suggestie verzenden",
    "itemWishlist.addedTitle": "Pareltje toegevoegd",
    "itemWishlist.addedDesc": "Dit item staat nu op je wishlist.",
    "itemWishlist.deletedTitle": "Item verwijderd",
    "itemWishlist.deletedDesc": "Item is van je wishlist gehaald.",
    "itemWishlist.itemsToFind": "{count} items te vinden",
    "itemWishlist.placeholder": "Wat zoek je? (bv. Vintage bureaulamp)",
    "itemWishlist.foundOn": "Gevonden op",
    "itemWishlist.emptyTitle": "Je wishlist is nog leeg.",
    "itemWishlist.emptyDesc": "Voeg items toe die je graag wilt vinden!",
    "itemWishlist.postCaptionPrefix": "Ik heb eindelijk mijn",
    "itemWishlist.itemFallback": "pareltje",
    "itemWishlist.postCaptionSuffix": "gevonden! #kringloopgeluk",
    "itemWishlist.foundCongratsTitle": "Gefeliciteerd met je pareltje! 🎉",
    "itemWishlist.foundCongratsDesc1": "Wat goed dat je",
    "itemWishlist.foundCongratsDesc2": "gevonden hebt! Zet dit pareltje op de kaart!",
    "itemWishlist.noOnlyCheck": "Nee, alleen afvinken",
    "itemWishlist.yesShare": "Ja, delen!",
    "cookie.title": "Privacy & Cookies",
    "cookie.desc": "Wij gebruiken cookies om de app te laten werken en anoniem gebruik te meten.",
    "cookie.readMore": "Lees meer in ons",
    "cookie.accept": "Alles accepteren",
    "cookie.preferences": "Voorkeuren",
    "cookie.functional": "Functioneel (Altijd aan)",
    "cookie.analytical": "Analytisch",
    "cookie.marketing": "Marketing",
    "cookie.save": "Voorkeuren opslaan",
    "cookie.functional.desc": "Basis functionaliteit om de app te laten werken.",
    "cookie.analytical.desc": "Helpt ons de app te verbeteren door anoniem gebruik te meten.",
    "cookie.marketing.desc": "Stelt ons in staat om relevante advertenties te tonen.",
    "addressSelector.locationFetchFailed": "Kon je locatie niet ophalen. Controleer je browserinstellingen.",
    "addressSelector.geolocationNotSupported": "Geolocatie wordt niet ondersteund door je browser.",
    "addressSelector.selectSource": "Selecteer adresbron",
    "addressSelector.none": "Geen",
    "addressSelector.homeAddress": "Thuis adres",
    "addressSelector.customAddress": "Custom adres",
    "addressSelector.currentLocation": "Huidige locatie",
    "addressSelector.home": "Thuis",
    "addressSelector.custom": "Custom",
    "addressSelector.noCoordinates": "Geen coordinaten",
    "limitBanner.maxReached": "Je hebt het maximum bereikt ({current}/{max} {plural})",
    "limitBanner.nearLimit": "Bijna aan je limiet ({current}/{max} {plural})",
    "limitBanner.upgradeUnlimited": "Upgrade naar Routes Insider voor onbeperkt gebruik.",
    "limitBanner.upgradeUnlimitedOptions": "Upgrade naar Routes Insider voor onbeperkte mogelijkheden.",
    "loginModal.signupFailed": "Er is een fout opgetreden bij het aanmaken van je account.",
    "loginModal.accountCreateFailed": "Account kon niet worden aangemaakt. Probeer het opnieuw.",
    "loginModal.accountCreatedCheckEmail": "Account aangemaakt! Check je email voor de verificatielink.",
    "loginModal.loginFailed": "Inloggen mislukt. Controleer je email en wachtwoord.",
    "loginModal.couldNotLogin": "Kon niet inloggen. Probeer het opnieuw.",
    "loginModal.genericError": "Er is iets misgegaan. Probeer het opnieuw.",
    "loginModal.googleLoginFailed": "Er is iets misgegaan met Google login. Probeer het opnieuw.",
    "loginModal.forgotPassword": "Wachtwoord vergeten?",
    "loginModal.resetPassword": "Wachtwoord herstellen",
    "loginModal.resetPasswordSent": "E-mail verstuurd! Check je inbox.",
    "loginModal.newPassword": "Nieuw wachtwoord",
    "loginModal.updatePassword": "Bijwerken",
    "loginModal.passwordUpdated": "Wachtwoord bijgewerkt!",
    "loginModal.email": "E-mailadres",
    "loginModal.createAccount": "Account aanmaken",
    "loginModal.createToUseFeatures": "Maak een account aan om alle features te gebruiken",
    "loginModal.password": "Wachtwoord",
    "loginModal.username": "Gebruikersnaam",
    "loginModal.orContinueWith": "Of ga verder met",
    "loginModal.loginWithGoogle": "Inloggen met Google",
    "loginModal.haveAccountLogin": "Heb je al een account? Log in",
    "loginModal.noAccountCreate": "Nog geen account? Maak er een aan",
    "loginModal.freeAccountCan": "Met een gratis account kun je:",
    "loginModal.termsNotice": "Door door te gaan ga je akkoord met onze voorwaarden.",
    "loginModal.agreeTo": "Ik ga akkoord met de",
    "loginModal.andThe": "en de",
    "loginModal.marketingOptIn": "Ik wil graag op de hoogte blijven van VintMap nieuws en aanbiedingen.",
    "footer.copyright": "© 2025 VintMap.app. Alle rechten voorbehouden.",
    "header.menu": "Menu",
    "profileAddress.title": "Thuis adres",
    "profileAddress.desc": "Sla je thuis adres op voor gebruik in routes",
    "profileAddress.save": "Adres opslaan",
    "profileAddress.saveFailed": "Kon adres niet opslaan. Probeer het opnieuw.",
    "pwa.title": "VintMap op je telefoon?",
    "pwa.desc": "Zet de app op je startscherm voor een snelle ervaring.",
    "pwa.installNow": "Nu installeren",
    "pwa.iosInstallInstructions": "VintMap op iPhone:\n1. Tik op 'Deel' (vierkantje met pijl omhoog) onderin Safari.\n2. Kies 'Zet op beginscherm'.",
    "pwa.androidInstallInstructions": "VintMap op Android:\n\n1. Tik op het menu (3 puntjes/lijntjes).\n2. Kies 'App installeren' of 'Toevoegen aan startscherm'.\n\nSamsung Internet:\nKies 'Pagina toevoegen aan' > 'Startscherm'.",
    "report.title": "Rapporteer inhoud",
    "report.desc": "Help ons de community veilig en leuk te houden. Waarom wil je dit rapporteren?",
    "report.explanationOptional": "Toelichting (optioneel)",
    "report.extraContext": "Geef extra context...",
    "report.submit": "Verstuur melding",
    "report.sending": "Versturen...",
    "report.thanksTitle": "Bedankt voor je melding",
    "report.thanksDesc": "We hebben je melding ontvangen en zullen ernaar kijken.",
    "report.alreadyReportedTitle": "Al gemeld",
    "report.alreadyReportedDesc": "Je hebt dit item al gerapporteerd.",
    "report.submitFailed": "Kon melding niet versturen",
    "report.errorTitle": "Er ging iets mis",
    "report.errorDesc": "Kon de melding niet versturen. Probeer het later opnieuw.",
    "reportShop.titleAbout": "Melding over {shopName}",
    "reportShop.desc": "Help ons de kaart actueel te houden. Wat is er aan de hand?",
    "reportShop.explanation": "Toelichting",
    "reportShop.tellMore": "Vertel ons meer...",
    "reportShop.checking": "We gaan het controleren.",
    "reportShop.closedPermanentlyDesc": "We controleren dit direct bij Google. Als de sluiting bevestigd wordt, passen we de status aan.",
    "report.reason.spam": "Spam of reclame",
    "report.reason.inappropriate": "Ongepaste inhoud",
    "report.reason.offensive": "Beledigend of kwetsend",
    "report.reason.misleading": "Misleidende informatie",
    "report.reason.copyright": "Schending van auteursrecht",
    "report.reason.other": "Anders",
    "reportShop.reason.notThrift": "Dit is geen kringloopwinkel / tweedehands zaak",
    "reportShop.reason.closedPermanently": "Deze kringloopwinkel bestaat niet meer",
    "reportShop.reason.closedTemporary": "Tijdelijk gesloten (verbouwing/vakantie)",
    "reportShop.reason.wrongInfo": "Informatie klopt niet (adres, tijd, etc.)",
    "reportShop.reason.other": "Anders",
    "account.stats.rides": "ritten",
    "account.stats.reviews": "reviews",
    "account.stats.scans": "scans",
    "map.loading": "Kaart laden...",
    "map.locationUnavailable": "Locatie niet beschikbaar",
    "map.startHome": "Start (Thuis)",
    "map.start": "Start",
    "map.endHome": "Eind (Thuis)",
    "map.startPoint": "Startpunt",
    "map.endPoint": "Eindpunt",
    "map.googleMapsNotConfigured": "Google Maps configuratie ontbreekt of is onjuist.",
    "map.loadInteractive": "Laad interactieve kaart",
    "map.addressNotFound": "Adres niet gevonden.",
    "map.stopLabel": "Stop {number}",
    "map.limitReachedTitle": "Kaartlimiet bereikt",
    "map.dailyLimitDesc": "Je hebt de daglimiet van {limit} interactieve kaartweergaven bereikt. Probeer het morgen weer!",
    "map.monthlyLimitDesc": "Je hebt de maandlimiet van {limit} interactieve kaartweergaven bereikt. Probeer het volgende maand weer!",
    "share.copiedTitle": "Gekopieerd!",
    "share.copiedDesc": "De tekst is naar je klembord gekopieerd.",
    "share.notSupportedTitle": "Niet ondersteund",
    "share.notSupportedDesc": "Je browser ondersteunt deze functie niet.",
    "share.failedTitle": "Delen mislukt",
    "share.failedDesc": "Het openen van het deelmenu is mislukt.",
    "share.pleaseWait": "Momentje...",
    "share.downloadingImage": "Afbeelding downloaden...",
    "share.imageDownloadedTitle": "Afbeelding gedownload",
    "share.imageDownloadedDesc": "Open Instagram en post de afbeelding!",
    "share.noImageTitle": "Geen afbeelding",
    "share.noImageDesc": "Er is geen afbeelding beschikbaar.",
    "share.titlePrefix": "Deel",
    "share.desc": "Via social media of kopieer de link.",
    "share.applyingBranding": "Branding toepassen...",
    "share.shopDetails": "Kringloop details",
    "share.routeOverview": "Route overzicht",
    "share.downloadImage": "Download afbeelding",
    "share.messagePreview": "Bericht preview",
    "share.beforeLabel": "VOOR",
    "share.afterLabel": "NA",
    "share.originalLabel": "ORIGINEEL",
    "share.whatsapp": "WhatsApp",
    "share.facebook": "Facebook",
    "share.pinterest": "Pinterest",
    "share.instaSave": "Insta (Opslaan)",
    "share.x": "X",
    "share.email": "E-mail",
    "share.shareVia": "Deel via...",
    "shopCard.charity": "Goed doel",
    "shopCard.inRoutes": "In {count} routes",
    "shopCard.verified": "Geverifieerd",
    "shopCard.paymentOptions": "Betaalmogelijkheden",
    "shopCard.cash": "Contant",
    "shopCard.card": "Pinnen",
    "shopCard.removeFavorite": "Verwijder van favorieten",
    "shopCard.addFavorite": "Voeg toe aan favorieten",
    "shopCard.removeWishlist": "Verwijder van te bezoeken",
    "shopCard.addWishlist": "Markeer als te bezoeken",
    "shopCard.navigateToShop": "Navigeer naar deze winkel",
    "shopCard.maps": "Maps",
    "shopCard.removedFromRoute": "Verwijderd uit route",
    "shopCard.addedToRoute": "Toegevoegd aan route",
    "shopCard.inRoute": "In route",
    "shopCard.planRoute": "+ Plan route",
    "shopDashboard.notLoggedIn": "Niet ingelogd",
    "shopDashboard.loginToManage": "Log in om je winkels te beheren.",
    "shopDashboard.loadingShops": "Winkels laden...",
    "shopDashboard.title": "Winkel dashboard",
    "shopDashboard.manageClaimed": "Beheer je geclaimde winkels",
    "shopDashboard.noneClaimed": "Nog geen winkels geclaimd",
    "shopDashboard.claimToManage": "Claim een winkel om deze te beheren.",
    "shopDashboard.manageAndVerify": "Beheer je geclaimde winkels en verificatie",
    "shopDashboard.viewProfile": "Bekijk profiel",
    "shopDashboard.hideStats": "Verberg stats",
    "shopDashboard.stats": "Stats",
    "shopDashboard.manage": "Beheer",
    "shopDashboard.confirmUnclaim": "Weet je zeker dat je deze winkel wilt ontkoppelen? Je verliest dan de beheerrechten.",
    "shopDashboard.waitingLetter": "Wacht op verificatiebrief",
    "shopDashboard.notVerified": "Niet geverifieerd",
    "shopDashboard.startVerification": "Start verificatie (EUR19,95 ex btw)",
    "shopDashboard.letterOnTheWay": "Je verificatiebrief is onderweg. Voer de code in zodra je deze ontvangt.",
    "shopDashboard.verificationCode": "Verificatiecode",
    "shopDashboard.codeExample": "Bijv. TEST1234",
    "shopDashboard.verify": "Verifieer",
    "shopDashboard.verifiedAllFeatures": "Je winkel is geverifieerd. Alle functies zijn nu beschikbaar.",
    "shopDashboard.validVerificationCode": "Voer een geldige verificatiecode in",
    "scanner.loginRequired": "Je moet ingelogd zijn om foto's te scannen.",
    "scanner.scanStarted": "Scan gestart",
    "scanner.photosUploadedForAnalysis": "{count} foto's geupload voor analyse.",
    "scanner.optimizingImages": "Afbeeldingen optimaliseren...",
    "scanner.keepQuality": "Kwaliteit behouden, formaat verkleinen",
    "scanner.startVintMapScan": "Start je VintMap Scan",
    "scanner.startScanDesc": "Maak een foto van een item of kies uit je galerij om te ontdekken wat het is.",
    "scanner.maxPhotosPerScan": "Maximaal {max} foto's per scan",
    "scanner.aiObjectRecognition": "AI-powered object herkenning",
    "scanner.enteredTraits": "Ingevoerde kenmerken",
    "scanner.trait.glass": "Glas",
    "scanner.trait.mirror": "Spiegel",
    "scanner.trait.webbing": "Webbing",
    "scanner.trait.transparent": "transparant",
    "scanner.trait.natural": "naturel",
    "scanner.characteristics": "Kenmerken",
    "scanner.verdictLabel": "VERDICT",
    "scanner.disclaimerShort": "Disclaimer: AI-detectie is een hulpmiddel. Resultaten op externe platforms zijn leidend. Vintmap is niet verantwoordelijk voor aankoopbeslissingen.",
    "shopsSearchMap.apiKeyMissing": "Google Maps API key niet gevonden",
    "shopsSearchMap.checkEnv": "Controleer VITE_GOOGLE_MAPS_API_KEY in je .env bestand",
    "shopsSearchMap.loadError": "Fout bij laden Google Maps",
    "simpleMapTest.title": "Simple Map Test",
    "simpleMapTest.checkEnv": "Check je .env bestand of Vercel omgevingsvariabelen",
    "simpleMapTest.desc": "Als je een RODE rand en GELE achtergrond ziet maar GEEN kaart, zit het probleem in Google Maps rendering.",
    "simpleMapTest.checkConsole": "Controleer de browserconsole voor logs.",
    "simpleMapTest.debugInfo": "Debug info:",
    "simpleMapTest.item1": "Container: Rode rand (10px), gele achtergrond, 500px hoogte",
    "simpleMapTest.item2": "Kaart: Moet 100% van de container vullen",
    "simpleMapTest.item3": "Controleer browserconsole voor gedetailleerde logs",
    "simpleMapTest.item4": "Zie je geel maar geen kaart -> Google Maps rendering probleem",
    "simpleMapTest.item5": "Zie je niets -> Container CSS probleem",
    "shopStats.loadFailed": "Niet gelukt om statistieken te laden.",
    "shopStats.titleFor": "Statistieken voor {shopName}",
    "shopStats.desc": "Inzicht in je zichtbaarheid en bereik.",
    "shopStats.thisMonth": "Deze maand",
    "shopStats.lastMonth": "Vorige maand",
    "shopStats.routeImpact": "Route Impact",
    "shopStats.newPublicRoutes": "Nieuwe openbare routes:",
    "shopStats.newPrivateRoutes": "Nieuwe prive routes:",
    "shopStats.routeImpactHint": "Hoe vaker je winkel in routes staat, hoe vaker deze bezocht wordt door gerichte shoppers.",
    "shopRating.vintMap": "VintMap:",
    "shopRating.yourScore": "Jouw score:",
    "shopRating.ownerScore": "Shop score:",
    "shopRating.google": "Google:",
    "shopRating.noReviews": "geen reviews",
    "rating.noReviews": "geen reviews",
    "rating.count": "reviews",
    "shopRating.writeReview": "Schrijf een review",
    "shopRating.editReview": "Bewerk je review",
    "openingHours.tempClosed": "Tijdelijk gesloten",
    "openingHours.untilDate": "tot",
    "openingHours.unknown": "Openingstijden onbekend",
    "openingHours.closedOnSelectedDay": "Gesloten op deze dag",
    "openingHours.openLabel": "Open",
    "openingHours.openUntil": "Open tot",
    "openingHours.closingSoon": "sluit bijna",
    "openingHours.closedNow": "Nu gesloten",
    "openingHours.reopensAt": "gaat weer open om",
    "openingHours.tomorrow": "morgen",
    "openingHours.opensLabel": "open",
    "openingHours.atLabel": "om",
    "upgrade.monthly": "Maandelijks",
    "upgrade.yearly": "Jaarlijks",
    "upgrade.maybeLater": "Misschien later",
    "app.name": "VintMap",
    "about.title": "Het verhaal achter VintMap",
    "about.p1": "Wie mij kent, weet dat ik het liefst op schattenjacht ga. Of het nu om mijn interieur gaat of om de kleding die ik draag: zeker 90% is tweedehands. Voor mij is een kringloopwinkel geen gewone winkel. Het is een plek vol verhalen, duurzaamheid en die ene vondst die je nergens anders tegenkomt.",
    "about.p2": "Mijn favoriete vrije dagen breng ik door met mijn zoon. Samen in de auto, een beetje kriskras door de regio, op zoek naar de leukste adresjes. En hoe gezellig dat ook is, ik liep steeds tegen hetzelfde aan: winkels die ineens dicht bleken, onnodige omwegen, of net dat ene pareltje dat we misten omdat we het niet wisten.",
    "about.p3": "Ik zocht een tool die verder ging dan alleen navigeren. Iets dat begrijpt hoe leuk kringlopen is. Een plek waar je niet alleen een route plant, maar ook je vondsten deelt, tips krijgt en elkaar inspireert.",
    "about.quote": "\"Omdat die app niet bestond, hebben we 'm zelf gebouwd.\"",
    "about.p4": "VintMap is gemaakt voor iedereen die van hunten houdt. Het is meer dan een routeplanner. Met VintMap zie je in een oogopslag welke winkels open zijn, plan je een logische route langs je favoriete kringlopen en voeg je onderweg makkelijk een koffiestop toe.",
    "about.p5": "Samen maken we de kaart steeds beter met verborgen parels, reviews en gedeelde vondsten.",
    "about.cta": "Laten we samen schatten blijven vinden.",
    "about.signatureLead": "Veel plezier met schatzoeken,",
    "about.signatureName": "VintMap",
    "about.signatureRole": "",
    "social.follow": "Volgen",
    "social.unfollow": "Ontvolgen",
    "social.following": "Volgend",
    "social.followers": "Volgers",
    "social.feed": "Schatzoekers Feed",
    "social.feed.empty": "Nog geen posts in je feed. Volg andere schatzoekers of winkels om hun updates te zien!",
    "social.feed.postedTreasure": "Plaatste een schat",
    "social.feed.shopUpdate": "Winkel Update",
    "social.profile.edit": "Profiel bewerken",
    "social.profile.completed": "Profiel voltooid",
    "social.search.placeholder": "Zoek schatzoekers...",
    "social.wishlist.public": "Openbare wishlist",
    "social.wishlist.private": "Privé wishlist",
    "social.style.lookingFor": "Op zoek naar",
    "social.style.bio": "Stijl & Inspiratie",
    "social.feed.noUpdates": "Nog geen updates in je feed",
    "social.noUsersFoundFor": "Geen schatzoekers gevonden voor \"{search}\"",
    "social.search.minChars": "Type minstens 2 letters om te zoeken naar andere schatzoekers.",
    "social.profile.postsCount": "Posts",
    "social.profile.info": "Info",
    "social.profile.wishlist": "Wishlist",
    "social.profile.userNotFound": "De schatzoeker \"{username}\" kon niet worden gevonden.",
    "social.profile.shareText": "Bekijk het profiel van {name} op VintMap",
    "social.profile.linkCopied": "Link gekopieerd!",
    "social.profile.since": "Sinds {date}",
    "social.profile.website": "Website",
    "social.profile.noStyle": "Deze schatzoeker heeft nog geen stijl-beschrijving toegevoegd.",
    "social.profile.noInterests": "Nog geen specifieke interesses opgegeven.",
    "social.profile.wishlistComingSoon": "Wishlist items worden hier getoond (COMING SOON)",
    "social.profile.noPosts": "Deze schatzoeker heeft nog geen pareltjes gepost.",
    "social.settings.fullName": "Volledige naam (of alias)",
    "social.settings.location": "Woonplaats / Regio",
    "account.tabs.social": "Sociaal",
    "shopDashboard.postUpdate": "Plaats Update",
    "contact.title": "Contact",
    "contact.intro": "Heb je vragen over VintMap, suggesties voor de app, of wil je een samenwerking bespreken? We horen graag van je.",
    "contact.emailTitle": "E-mail",
    "contact.emailDesc": "Voor algemene vragen en ondersteuning.",
    "contact.emailCta": "Mail naar info@vintmap.app",
    "contact.faqTitle": "Veelgestelde vragen",
    "contact.faqDesc": "Bekijk onze FAQ voor directe antwoorden.",
    "contact.faqCta": "Ga naar FAQ",
    "contact.ownersTitle": "Voor winkeleigenaren",
    "contact.ownersDesc": "Wil je je winkel aanmelden, claimen of gegevens wijzigen? Dit kun je in de meeste gevallen direct regelen via de app.",
    "contact.ownersBullet1": "Zoek je winkel op de kaart en geef wijzigingen door met het vlaggetje.",
    "contact.ownersBullet2": "Klik op \"Claim deze winkel\" op de detailpagina om beheerder te worden.",
    "contact.ownersBullet3prefix": "Nog niet in de app?",
    "contact.ownersBullet3link": "Voeg je winkel hier toe",
    "legal.updated": "Laatst bijgewerkt: 05-03-2026",
    "privacy.title": "Privacybeleid VintMap",
    "privacy.1.title": "1. Inleiding en reikwijdte",
    "privacy.1.body": "### 1.1 Toepassingsgebied\nDit privacybeleid beschrijft hoe VintMap persoonsgegevens verzamelt, gebruikt, deelt en beschermt in het kader van de PWA, de website en alle bijbehorende functionaliteiten zoals routeplanning, AI meubel restyler en de community.\n\n### 1.2 Toepasselijke wetgeving\nWij verwerken gegevens conform de AVG (GDPR), de Nederlandse Uitvoeringswet AVG en de Telecommunicatiewet.\n\n### 1.3 Leeftijdsbeperking\nVintMap is bedoeld voor personen van 16 jaar en ouder. Wij verzamelen niet bewust gegevens van minderjarigen.",
    "privacy.2.title": "2. Verwerkingsverantwoordelijke en contactgegevens",
    "privacy.2.body": "### 2.1 Verwerkingsverantwoordelijke\nVintMap is verantwoordelijk voor de verwerking van jouw gegevens. \nKvk: 01119829\nVoorstraat 142, 9291 CP Kollum\nNederland\n\n### 2.2 Contact voor privacyvragen\nVoor vragen over je privacy kun je contact opnemen via privacy@vintmap.nl of via de contactpagina in de app. Wij streven ernaar binnen 2 werkdagen te reageren.",
    "privacy.3.title": "3. Welke persoonsgegevens wij verwerken",
    "privacy.3.body": "### 3.1 Accountgegevens\nE-mailadres, gebruikersnaam, wachtwoord (versleuteld), profielfoto en accountstatus.\n\n### 3.2 Gebruiksgegevens\nOpgeslagen routes, favoriete winkels, zoekgeschiedenis en interacties met de community.\n\n### 3.3 Locatiegegevens\nHuidige locatie (alleen tijdens actief gebruik) en gekozen startpunten. Wij volgen je niet op de achtergrond.\n\n### 3.4 Gebruikersinhoud\nReviews, geuploade foto's en community routes.\n\n### 3.5 Technische gegevens\nIP-adres (geanonimiseerd), browsertype, apparaattype en foutrapporten.\n\n### 3.6 Betalingsmetadata\nVia Stripe verwerken wij abonnementsstatus en betaalgeschiedenis. Wij slaan geen volledige creditcardgegevens op.",
    "privacy.4.title": "4. Doeleinden en rechtsgronden van verwerking",
    "privacy.4.body": "### 4.1 Uitvoering van de overeenkomst\nWij verwerken gegevens om de app-functies, zoals routeplanning en accountbeheer, aan te kunnen bieden.\n\n### 4.2 Gerechtvaardigd belang\nBeveiliging, fraudepreventie, kwaliteitsverbetering van onze diensten en optimalisatie van de gebruikerservaring.\n\n### 4.3 Wettelijke verplichtingen\nFiscale administratie en boekhoudkundige verplichtingen (zoals de 7-jarige bewaarplicht).\n\n### 4.4 Toestemming\nVoor specifieke functies zoals locatietoegang vragen we expliciete toestemming, die je te allen tijde kunt intrekken.",
    "privacy.5.title": "5. Locatiegegevens en kaartfunctionaliteit",
    "privacy.5.body": "### 5.1 Wanneer locatiegegevens worden gebruikt\nBij het openen van de kaart, het starten van een route of het gebruiken van de Insider navigatie.\n\n### 5.2 Hoe locatiegegevens worden verwerkt\nLocatie wordt live verwerkt via Google Maps API. We bouwen geen historische bewegingsprofielen op.\n\n### 5.3 Locatietoestemming beheren\nJe kunt toestemming intrekken via je browser- of apparaatinstellingen. Zonder toestemming moet je handmatig een startpunt invoeren.\n\n### 5.4 Externe navigatie\nBij het gebruik van de \"Open in Google Maps\" knop verlaat je de VintMap-webapp. Vanaf dat moment zijn de privacyvoorwaarden en het beleid van Google Maps van toepassing.",
    "privacy.6.title": "6. AI-functies en beeldverwerking",
    "privacy.6.body": "### 6.1 AI Meubel Restyler\nMet deze functie kun je foto's van meubels uploaden voor visualisatie.\n\n### 6.2 Verwerking van afbeeldingen\nFoto's worden beveiligd verstuurd voor AI-verwerking. De bewerkte afbeeldingen worden opgeslagen in je persoonlijke geschiedenis zodat je ze later kunt terugkijken. Je kunt deze afbeeldingen te allen tijde handmatig verwijderen.\n\n### 6.3 Externe AI-provider\nWij werken samen met gespecialiseerde AI-providers (zoals OpenAI/Replicate). Foto's worden niet gebruikt om hun modellen te trainen zonder jouw toestemming.",
    "privacy.7.title": "7. Community-functies en gebruikersinhoud",
    "privacy.7.body": "### 7.1 Publieke inhoud\nReviews, publieke routes en foto's bij reviews zijn zichtbaar voor andere gebruikers.\n\n### 7.2 Controle over inhoud\nJe bepaalt zelf wat je deelt. Gegevens zijn standaard privé. Je kunt content te allen tijde verwijderen of wijzigen.\n\n### 7.3 Moderatie\nWij modereren inhoud op basis van onze gebruikersvoorwaarden om de community veilig te houden.",
    "privacy.8.title": "8. Cookies, local storage en vergelijkbare technieken",
    "privacy.8.body": "### 8.1 Wat we gebruiken\nVintMap gebruikt cookies en local storage voor essentiële functies (sessies, taal) en functionele verbeteringen (voorkeuren).\n\n### 8.2 Jouw controle\nJe kunt opgeslagen gegevens beheren en verwijderen via je browserinstellingen of de app-instellingen. Let op dat de app daarna mogelijk minder optimaal werkt.",
    "privacy.9.title": "9. Betaling en financiële gegevens",
    "privacy.9.body": "### 9.1 Stripe\nBetalingen verlopen veilig via Stripe (PCI-DSS gecertificeerd). Wij slaan geen kaartnummers of CVC-codes op.\n\n### 9.2 Facturatie\nWij bewaren uitsluitend administratieve metadata zoals abonnementsstatus en factuurdata voor onze administratie.",
    "privacy.10.title": "10. Delen met derden en verwerkers",
    "privacy.10.body": "### 10.1 Geen verkoop van data\nWij verkopen je gegevens nooit aan data brokers of advertentienetwerken.\n\n### 10.2 Verwerkers\nWij delen alleen noodzakelijke data met vertrouwde partners:\n- **Hosting & Infrastructure**: Cloudflare & Vercel\n- **Database & Opslag**: Supabase\n- **Kaarten & Navigatie**: Google Maps API\n- **Betalingen**: Stripe\n- **E-mail**: Postmark / Resend\nMet hen zijn strikte verwerkersovereenkomsten gesloten om de veiligheid van jouw data te waarborgen.",
    "privacy.11.title": "11. Internationale doorgifte van gegevens",
    "privacy.11.body": "Wij streven naar verwerking binnen de EER. Waar dit buiten de EER gebeurt (bijv. Google of Stripe), gebruiken we goedgekeurde waarborgen zoals Standard Contractual Clauses (SCC's).",
    "privacy.12.title": "12. Bewaartermijnen",
    "privacy.12.body": "### 12.1 Termijnen\nAccountgegevens bewaren we zolang je actief bent. Inactieve accounts worden na 3,5 jaar verwijderd. Facturen blijven 7 jaar bewaard conform de wet.\n\n### 12.2 Verwijdering\nBij accountverwijdering worden je gegevens binnen 30 dagen definitief gewist of geanonimiseerd. Afbeeldingen in de AI-geschiedenis blijven bewaard tot je ze zelf verwijdert of je account opzegt.",
    "privacy.13.title": "13. Beveiliging van persoonsgegevens",
    "privacy.13.body": "Wij gebruiken moderne technieken zoals TLS 1.3 encryptie, bcrypt hashing en firewalls. Toegang tot identificeerbare gegevens is strikt beperkt en wordt gelogd voor audit-doeleinden.",
    "privacy.14.title": "14. Jouw privacyrechten",
    "privacy.14.body": "### 14.1 Rechten\nJe hebt recht op inzage, correctie, verwijdering, beperking, dataportabiliteit en bezwaar.\n\n### 14.2 Inzage en export\nJe kunt je gegevens direct inzien en exporteren via Instellingen > Privacy > 'Download mijn gegevens'. Voor overige verzoeken kun je mailen naar privacy@vintmap.nl.",
    "privacy.15.title": "15. Klachten en toezichthouder",
    "privacy.15.body": "Neem bij ontevredenheid eerst contact met ons op. Je hebt ook altijd het recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens.",
    "privacy.16.title": "16. Wijzigingen in dit privacybeleid",
    "privacy.16.body": "Wij kunnen dit beleid aanpassen. Bij grote wijzigingen informeren wij je proactief via de app of e-mail.",
    "privacy.17.title": "17. Contactgegevens",
    "privacy.17.body": "VintMap Privacy Team\nE-mail: privacy@vintmap.nl\nVoor dringende beveiligingsincidenten: security@vintmap.nl",
    "privacy.dashboard.title": "Privacy Beheer Dashboard",
    "privacy.dashboard.desc": "Beheer hier je privacyinstellingen, toestemmingen en datarechten conform de AVG.",
    "privacy.dashboard.savedTitle": "Instellingen opgeslagen",
    "privacy.dashboard.savedDesc": "Je privacyvoorkeuren zijn succesvol bijgewerkt.",
    "privacy.dashboard.exportStarted": "Gegevensexport gestart. Je download begint over enkele seconden.",
    "privacy.dashboard.deleteStarted": "Verwijdering ingepland",
    "privacy.dashboard.confirmDelete": "Account Definitief Verwijderen",
    "privacy.dashboard.deleteNote": "Let op: Gegevens onder wettelijke bewaarplicht (facturen) blijven 7 jaar bewaard.",
    "privacy.dashboard.ads": "Gepersonaliseerde Advertenties",
    "privacy.dashboard.adsDesc": "Sta toe dat we advertenties tonen die passen bij jouw interesses.",
    "privacy.dashboard.geotargeting": "Geotargeting Niveau",
    "privacy.dashboard.geotargetingDesc": "Bepaal op welk niveau we je locatie mogen gebruiken voor lokale aanbiedingen.",
    "privacy.dashboard.geotargeting.national": "Landelijk",
    "privacy.dashboard.geotargeting.provincial": "Provinciaal",
    "privacy.dashboard.geotargeting.local": "Lokaal",
    "privacy.dashboard.dataRights": "Jouw Datarechten",
    "privacy.dashboard.export": "Download mijn gegevens",
    "privacy.export.desc": "Ontvang een kopie van al je persoonlijke gegevens in JSON formaat.",
    "privacy.delete.desc": "Verwijder je account en alle persoonlijke data. Dit kan 30 dagen duren.",
    "privacy.locationConsent": "Locatietoegang",
    "privacy.locationConsentDesc": "Sta toe dat VintMap je locatie gebruikt voor de kaart en routeplanning.",
    "privacy.functional.desc": "Noodzakelijk voor de werking van de app (sessies, beveiliging).",
    "terms.footer.note": "VintMap behoudt zich het recht voor om deze voorwaarden op elk moment te wijzigen. Gebruik van de app na wijziging betekent acceptatie.",
    "disclaimer.footer.note": "VintMap is niet aansprakelijk voor schade door onjuiste informatie or verkeerde routekeuzes.",
    "privacy.footer.note": "VintMap verwerkt je gegevens met de grootst mogelijke zorgvuldigheid conform de AVG.",
    "privacy.dashboard.confirmDeleteTitle": "Weet je het zeker?",
    "privacy.dashboard.deleteConfirmDesc": "Je account en data worden over 30 dagen verwijderd. Dit kan niet ongedaan worden gemaakt.",
    "terms.title": "Algemene Voorwaarden VintMap",
    "terms.1.title": "1. Definities",
    "terms.1.body": "**Vintmap**: De dienst aangeboden via de website en app, gevestigd aan de Voorstraat 142, 9291 CP Kollum, Nederland (KvK: 01119829).\n\n**Dienst**: Alle functionaliteiten, features en content aangeboden via Vintmap.\n\n**Gebruiker / Je**: Elke natuurlijke persoon die gebruik maakt van Vintmap.\n\n**Account**: Het persoonlijke gebruikersprofiel.\n\n**Insiders**: Het betaalde premium abonnement (€ 2,99 p/m of € 29,99 p/j).\n\n**Gebruikersinhoud**: Alle content die je uploadt, plaatst of deelt.",
    "terms.2.title": "2. Toepasselijkheid en aanvaarding",
    "terms.2.body": "2.1 Deze voorwaarden zijn van toepassing op elk gebruik van de Vintmap website en app.\n\n2.2 Door gebruik te maken van Vintmap verklaar je deze Algemene Voorwaarden volledig te hebben gelezen en aanvaard, en minimaal 16 jaar oud te zijn.\n\n2.3 Aanvaarding vindt plaats door het aanvinken van de checkbox bij registratie of door het actief gebruiken van de Dienst.",
    "terms.3.title": "3. De dienst en functionaliteiten",
    "terms.3.body": "3.1 Vintmap biedt een platform voor het vinden van kringloopwinkels, routeplanning en AI-visualisaties.\n\n3.2 Gratis gebruikers hebben toegang tot basiszoekfuncties en beperkte routeplanning.\n\n3.3 Insiders krijgen extra functies zoals direct-start navigatie vanaf de huidige locatie en de AI Restyler voor meubels (25 restyles p/m).",
    "terms.4.title": "4. Account en registratie",
    "terms.4.body": "4.1 Voor bepaalde functionaliteiten is een Account vereist.\n\n4.2 Je verschaft waarheidige informatie en kiest een veilig wachtwoord.\n\n4.3 Je mag slechts één Account aanmaken. Het delen van accounts is niet toegestaan.",
    "terms.5.title": "5. Toegestaan en verboden gebruik",
    "terms.5.body": "5.1 Je gebruikt Vintmap op een rechtmatige en zorgvuldige manier.\n\n5.2 Expliciet verboden is technisch misbruik (hacking, scraping via bots), onrechtmatig gebruik (fraude, intimidatie) en het verspreiden van schadelijke code.\n\n5.3 Commercieel misbruik, zoals het systematisch kopiëren van de database, is verboden zonder toestemming.",
    "terms.6.title": "6. Gebruikersinhoud en community",
    "terms.6.body": "6.1 Gebruikersinhoud blijft jouw eigendom.\n\n6.2 Je verleent Vintmap een niet-exclusieve, wereldwijde licentie om je content weer te geven en te verwerken binnen de Dienst.\n\n6.3 Je garandeert dat je de rechthebbende bent en geen rechten van derden schendt.",
    "terms.7.title": "7. Intellectueel eigendom",
    "terms.7.body": "7.1 Alle intellectuele eigendomsrechten (software, design, database-inhoud) behoren toe aan Vintmap.\n\n7.2 Je krijgt een beperkte, niet-overdraagbare licentie om de Dienst te gebruiken zoals bedoeld.",
    "terms.8.title": "8. Insiders abonnement en betalingen",
    "terms.8.body": "8.1 Het abonnement verlengt automatisch tenzij tijdig opgezegd.\n\n8.2 Betaling geschiedt vooraf. Je hebt een 14-dagen herroepingsrecht na de eerste aankoop, tenzij je de Dienst (zoals de AI Restyler) reeds hebt gebruikt.\n\n8.3 Opzeggen kan tot 24 uur voor de verlengingsdatum via de instellingen.",
    "terms.9.title": "9. AI Restyler functie",
    "terms.9.body": "9.1 De Restyler stelt Gebruikers in staat om meubels te visualiseren. \n\n9.2 Het resultaat is een visualisatie en geen fotografische precisie. Vintmap is niet aansprakelijk voor verschillen tussen de visualisatie en de werkelijkheid.",
    "terms.10.title": "10. Beschikbaarheid en onderhoud",
    "terms.10.body": "10.1 We streven naar een hoge beschikbaarheid (99% uptime), maar kunnen dit niet garanderen.\n\n10.2 Gepland onderhoud wordt indien mogelijk 48 uur vooraf aangekondigd.",
    "terms.11.title": "11. Wijzigingen in de dienst",
    "terms.11.body": "11.1 Vintmap mag de Dienst wijzigen of functionaliteiten toevoegen/verwijderen.\n\n11.2 Materiële wijzigingen worden minimaal 30 dagen vooraf gecommuniceerd.",
    "terms.12.title": "12. Garanties en disclaimers",
    "terms.12.body": "12.1 De Dienst wordt geleverd 'as-is'.\n\n12.2 We garanderen niet dat alle informatie (openingstijden, locaties) 100% actueel of juist is.\n\n12.3 **Advertenties en Promoties**: De Dienst kan advertenties of links van derden bevatten. Vintmap is NIET verantwoordelijk voor de inhoud, producten of diensten in deze advertenties. Er kunnen GEEN RECHTEN worden ontleend aan getoonde advertenties.",
    "terms.13.title": "13. Aansprakelijkheid en schade",
    "terms.13.body": "13.1 Vintmap is niet aansprakelijk voor indirecte schade, verkeersboetes of letsel.\n\n13.2 VOOR ZOVER WETTELIJK TOEGESTAAN WIJST VINTMAP ELKE AANSPRAKELIJKHEID AF VOOR SCHADE VOORTVLOEIEND UIT INTERACTIE MET ADVERTENTIES OF TRANSACTIES MET DERDEN.\n\n13.3 Voor Insiders is de aansprakelijkheid beperkt tot het bedrag van het jaarabonnement.",
    "terms.14.title": "14. Beëindiging en opschorting",
    "terms.14.body": "14.1 Je kunt je account te allen tijde verwijderen.\n\n14.2 Vintmap kan je account beëindigen bij ernstige schendingen van deze voorwaarden.",
    "terms.15.title": "15. Gegevensbescherming en privacy",
    "terms.15.body": "15.1 Persoonsgegevens worden verwerkt conform de AVG en ons Privacybeleid.",
    "terms.16.title": "16. Klachten en geschillen",
    "terms.16.body": "16.1 Klachten kunnen gemeld worden via support@vintmap.nl.\n\n16.2 Wij streven naar een oplossing binnen 2 werkdagen.",
    "terms.17.title": "17. Toepasselijk recht en bevoegde rechter",
    "terms.17.body": "17.1 Op deze voorwaarden is Nederlands recht van toepassing.\n\n17.2 Geschillen worden voorgelegd aan de bevoegde rechter in Noord-Nederland.",
    "terms.18.title": "18. Wijzigingen van deze voorwaarden",
    "terms.18.body": "18.1 We mogen de voorwaarden aanpassen. Grote wijzigingen worden per e-mail aangekondigd.",
    "terms.19.title": "19. Diversen",
    "terms.19.body": "19.1 Indien een bepaling nietig blijkt, blijven de overige bepalingen van kracht.",
    "terms.20.title": "20. Contactgegevens",
    "terms.20.body": "VintMap\nVoorstraat 142, 9291 CP Kollum\nlegal@vintmap.nl",
    "disclaimer.title": "Disclaimer VintMap",
    "disclaimer.1.title": "1. Leeswijzer en toepasselijkheid",
    "disclaimer.1.body": "1.1 Doel van deze disclaimer\nDeze disclaimer verduidelijkt de reikwijdte en beperkingen van de informatie en diensten die VintMap aanbiedt. Het doel is om gebruikers te informeren over:\n\n- De aard en betrouwbaarheid van de aangeboden informatie\n- De grenzen van VintMap's verantwoordelijkheid\n- De eigen verantwoordelijkheid van gebruikers\n- Risico's verbonden aan het gebruik van de dienst\n\n1.2 Toepassingsgebied\nDeze disclaimer is van toepassing op:\n\n- Alle informatie getoond binnen VintMap (website en app)\n- Alle functionaliteiten en features van de dienst\n- Community-content en gebruikersbijdragen\n- Advertenties en commerci\u00eble content\n- Links naar externe websites en diensten\n\n1.3 Juridische context\nDeze disclaimer:\n\n- Maakt integraal onderdeel uit van de Algemene Voorwaarden\n- Dient gelezen te worden in samenhang met het Privacybeleid\n- Beperkt de aansprakelijkheid van VintMap conform wettelijke grenzen\n- Laat wettelijke consumentenrechten onverlet waar deze niet uitgesloten kunnen worden\n\n1.4 Aanvaarding\nDoor gebruik te maken van VintMap aanvaard je deze disclaimer en verklaar je:\n\n- Kennis te hebben genomen van de hierin beschreven beperkingen\n- Te begrijpen dat VintMap een informatief hulpmiddel is, geen absolute bron van waarheid\n- Eigen verantwoordelijkheid te nemen voor beslissingen op basis van VintMap-informatie\n- De grenzen van de dienst te accepteren",
    "disclaimer.2.title": "2. Algemeen informatief karakter",
    "disclaimer.2.body": "2.1 Hulpmiddel, geen garantie\nVintMap is bedoeld als hulpmiddel voor het ontdekken van kringloopwinkels en het plannen van routes. Het is geen:\n\n- Gegarandeerde bron van actuele informatie\n- Volledige inventarisatie van alle kringloopwinkels\n- Professionele navigatiedienst met garanties\n- Besluitvormingsinstrument met juridische gelding\n\n2.2 Geen rechten te ontlenen\nAan de informatie binnen VintMap kunnen geen (onvoorwaardelijke) rechten worden ontleend. Dit betekent:\n\n- Geen recht op aanwezigheid van specifieke informatie\n- Geen recht op juistheid of volledigheid van data\n- Geen recht op ononderbroken beschikbaarheid\n- Geen recht op specifieke functionaliteit of prestaties\n- Geen recht op compensatie bij onjuiste of ontbrekende informatie\n\n2.3 Best-effort basis\nVintMap streeft ernaar:\n\n- Informatie zo actueel en correct mogelijk te houden\n- Een betrouwbare en gebruiksvriendelijke dienst te bieden\n- Fouten en onvolledigheden te corrigeren wanneer gesignaleerd\n- De database regelmatig te actualiseren\n\nDit is echter een inspanningsverplichting, geen resultaatverplichting.\n\n2.4 Gebruik op eigen risico\nJe gebruikt VintMap geheel op eigen risico. VintMap aanvaardt geen aansprakelijkheid voor:\n\n- Beslissingen genomen op basis van VintMap-informatie\n- Teleurstellingen of ongemakken tijdens winkelbezoeken\n- Tijd of kosten gemaakt voor routes of bezoeken\n- Gemiste kansen door onjuiste informatie",
    "disclaimer.3.title": "3. Kaart- en locatiegegevens",
    "disclaimer.3.body": "3.1 Bronnen en actualiteit\nHerkomst gegevens:\nVintMap verzamelt kringloopwinkel-informatie uit diverse bronnen:\n\n- Openbare bronnen (websites, online directories)\n- Gebruikersbijdragen en community-input\n- Google Maps en andere kaartdiensten\n- Direct contact met winkels (waar mogelijk)\n\nActualiteit:\n\n- Informatie kan verouderd zijn op moment van raadpleging\n- Winkels kunnen zonder kennisgeving sluiten, verhuizen of openingstijden wijzigen\n- Database-updates vinden plaats op regelmatige maar niet-realtime basis\n- Seizoensgebonden wijzigingen worden niet altijd direct verwerkt\n\n3.2 Geen garantie op volledigheid\nVintMap garandeert NIET:\n\n- Dat alle kringloopwinkels in een gebied zijn opgenomen\n- Dat de weergegeven winkels daadwerkelijk bestaan of open zijn\n- Dat openingstijden actueel en correct zijn\n- Dat contactgegevens (telefoonnummer, website) kloppen\n- Dat adressen precies overeenkomen met werkelijke locatie\n\nRedenen voor onvolledigheid:\n\n- Nieuwe winkels nog niet toegevoegd\n- Gesloten winkels nog niet verwijderd\n- Kleine of onbekende winkels niet in database\n- Tijdelijke pop-ups of evenementen niet opgenomen\n- Geografische beperkingen of focus-gebieden\n\n3.3 Aanbeveling: altijd verifi\u00ebren\nVoor belangrijke bezoeken adviseren wij:\n\n- Bel vooraf om openingstijden te bevestigen\n- Check de website of social media van de winkel\n- Bereid je voor op mogelijk gesloten winkels (alternatief plan)\n- Neem contactgegevens van de winkel mee\n- Vertrouw niet blind op navigatie zonder verificatie\n\nVooral essentieel bij:\n\n- Lange reizen (>30 minuten rijden)\n- Bezoek voor specifiek gezocht item\n- Tijdgebonden plannen (laatste dag vakantie, etc.)\n- Groepsuitjes of geplande events\n\n3.4 Melden van onjuistheden\nHelp VintMap beter worden:\n\n- Meld gesloten winkels via \"Probleem melden\" in app\n- Update onjuiste openingstijden\n- Voeg ontbrekende winkels toe via community-functie\n- Deel correcties via feedback@vintmap.nl\n\nJe bijdrage helpt andere gebruikers en verbetert de database voor iedereen.",
    "disclaimer.4.title": "4. Routes en navigatie",
    "disclaimer.4.body": "4.1 Afhankelijkheid van externe diensten\nVintMap gebruikt Google Maps Platform voor:\n\n- Routeberekening en navigatie\n- Kaartweergave en geocoding\n- Verkeersinformatie (indien beschikbaar)\n- Geschatte reistijden\n\nBelangrijke disclaimer:\n\n- VintMap is afhankelijk van de juistheid van Google Maps data\n- Fouten in Google Maps worden overgenomen in VintMap\n- VintMap heeft geen controle over Google's data-kwaliteit\n- Zie Google's eigen disclaimers en voorwaarden\n\n4.2 Routevoorstellen zijn suggesties\nRoutes binnen VintMap zijn:\n\n- Voorgestelde volgorden, geen absolute optimale routes\n- Gebaseerd op afstand en beschikbare data\n- Niet altijd rekening houdend met alle lokale omstandigheden\n- Indicatief, niet bindend of gegarandeerd effici\u00ebnt\n\nRoutes houden GEEN rekening met:\n\n- Actuele verkeerssituatie (files, ongevallen)\n- Wegwerkzaamheden of wegafsluitingen\n- Verkeersregels (eenrichtingsverkeer, doorgaand verkeer verboden)\n- Seizoensgebonden veranderingen (winterse omstandigheden)\n- Voertuigspecifieke beperkingen (hoogte, gewicht, brandstof)\n- Persoonlijke voorkeuren (snelwegvermijding, tolwegen)\n\n4.3 Eigen verantwoordelijkheid verkeersveiligheid\nJIJ BENT TE ALLEN TIJDE VERANTWOORDELIJK VOOR:\n\n- Veilig en volgens de wet besturen van je voertuig\n- Concentratie op het verkeer, niet op de app\n- Naleven van alle verkeersregels en -borden\n- Beoordelen of een route geschikt en veilig is\n- Afwijken van de route indien situatie daarom vraagt\n\nVERBODEN:\n\n- Gebruik van VintMap tijdens het besturen\n- Invoeren van gegevens terwijl je rijdt\n- Aflaten afleiden door de app in het verkeer\n- Blind vertrouwen op navigatie zonder eigen beoordeling\n\nAANBEVELING:\n\n- Stel route in voordat je gaat rijden\n- Gebruik hands-free of voice-control waar mogelijk\n- Trek veilig over als je iets moet aanpassen\n- Laat een passagier de app bedienen\n\n4.4 Geschatte reistijden\nReistijden zijn indicatief en gebaseerd op:\n\n- Standaard verkeersomstandigheden\n- Gemiddelde rijsnelheden\n- Historische verkeersdata (indien beschikbaar)\n\nReistijden houden GEEN rekening met:\n\n- Actuele files of vertragingen\n- Parkeertijd bij winkels\n- Winkeltijd of browse-tijd\n- Pauzes, tankstops of andere stops\n- Persoonlijke rijstijl (rustig vs. sportief)\n\nWerkelijke reistijd kan significant afwijken.\n\n4.5 Geen aansprakelijkheid voor verkeerssituaties\nVintMap is NIET aansprakelijk voor:\n\n- Verkeersongelukken tijdens gebruik van routes\n- Verkeersboetes (te snel rijden, parkeren, etc.)\n- Schade aan voertuig (slechte wegen, hoogte, etc.)\n- Verkeerde afslagen of gemiste borden\n- Extra kosten (tol, parkeren, brandstof)\n- Gemiste afspraken door vertragingen\n\nJij bent de bestuurder. Jij bepaalt de route. Jij bent verantwoordelijk.",
    "disclaimer.5.title": "5. AI Restyler visualisaties",
    "disclaimer.5.body": "5.1 Karakter van AI-output\nDe AI Restyler is een visualisatie-tool die gebruikt maakt van kunstmatige intelligentie. Het is geen:\n\n- Professionele ontwerptool\n- Garantie voor eindresultaat\n- Fotografisch accurate weergave\n- Vervanging voor professioneel restauratie-advies\n\n5.2 Technische beperkingen\nAI-gegenereerde beelden zijn:\n\n- Digitale interpretaties, geen exacte voorspellingen\n- Afhankelijk van kwaliteit input-foto\n- Onderhevig aan AI-beperkingen en \"hallucinations\"\n- Niet altijd fysiek realiseerbaar\n\nAI heeft moeite met:\n\n- Complexe patronen of versieringen\n- Specifieke materialen (metaal, glas, stof)\n- Details zoals handvatten, scharnieren, textuur\n- Reflecties, schaduwen en belichting\n- Niet-standaard vormen of sizes\n\n5.3 Geen garanties op kleuren\nKleuren in restyles zijn indicatief:\n\n- Scherm kleurweergave verschilt van werkelijkheid\n- Verfkleuren in de winkel kunnen afwijken\n- Belichting be\u00efnvloedt hoe kleuren eruit zien\n- Houtsoorten reageren verschillend op verf\n- Voorbehandeling (primer, schuren) be\u00efnvloedt eindresultaat\n\nAanbeveling:\n\n- Gebruik restyles als inspiratie, niet als exacte blauwdruk\n- Test verfkleuren eerst op onzichtbaar deel\n- Raadpleeg professioneel advies voor belangrijke projecten\n- Verwacht afwijkingen tussen visualisatie en werkelijkheid\n\n5.4 Geen vervangingsadvies voor DIY-projecten\nVintMap geeft GEEN advies over:\n\n- Welke verf of materialen te gebruiken\n- Hoe meubels voor te behandelen (schuren, ontvetten)\n- Welke technieken toe te passen\n- Of een meubel geschikt is voor bewerking\n- Veiligheid van materialen (loodverf, asbest, etc.)\n\nVoor DIY-projecten:\n\n- Zoek professioneel advies\n- Volg instructies van verfproducenten\n- Neem veiligheidsmaatregelen (maskers, ventilatie)\n- Test eerst op klein oppervlak\n\n5.5 Aankoop op eigen beoordeling\nBij aankoop van meubel na restyling:\n\n- Inspecteer het meubel fysiek zorgvuldig\n- Beoordeel staat, kwaliteit en stabiliteit\n- Controleer op gebreken, houtworm, schade\n- Vraag advies van winkel over geschiktheid voor bewerking\n- VintMap is niet verantwoordelijk voor teleurstellingen\n\nVintMap is NIET aansprakelijk voor:\n\n- Meubels die tegenvallen na aankoop\n- Kosten van materialen en arbeid voor bewerking\n- Mislukte DIY-projecten\n- Verschil tussen verwachting (restyle) en werkelijkheid\n- Waarde of verkoopbaarheid van bewerkt meubel",
    "disclaimer.6.title": "6. Community en gebruikersinhoud",
    "disclaimer.6.body": "6.1 Inhoud van derden\nVintMap biedt community-functies waarbij gebruikers kunnen delen:\n\n- Reviews en beoordelingen van winkels\n- Foto's van meubels of winkels\n- Zelfgemaakte routes\n- Tips en ervaringen\n\nBelangrijke disclaimer:\nDeze content is gemaakt door gebruikers, niet door VintMap.\n\n6.2 Geen voorafgaande controle\nVintMap:\n\n- Controleert NIET alle gebruikersinhoud vooraf\n- Modereert reactief (na melding of bij toeval)\n- Verwijdert ongepaste content wanneer gesignaleerd\n- Is NIET verantwoordelijk voor inhoud van gebruikers\n\n6.3 Geen garanties op betrouwbaarheid\nReviews en beoordelingen:\n\n- Zijn subjectieve meningen van individuele gebruikers\n- Kunnen gedateerd, onvolledig of bevooroordeeld zijn\n- Worden niet geverifieerd op waarheidsgetrouwheid\n- Kunnen negatief of positief gekleurd zijn door persoonlijke ervaring\n\nVintMap garandeert NIET:\n\n- Dat reviews eerlijk of evenwichtig zijn\n- Dat beoordelingen representatief zijn\n- Dat foto's actueel zijn of kloppen met huidige situatie\n- Dat gebruikers echte ervaring hebben met de winkel\n\n6.4 Mogelijkheid van nepreviews\nVintMap probeert nepreviews en spam te voorkomen, maar kan niet garanderen:\n\n- Dat alle reviews authentiek zijn\n- Dat reviews niet door eigenaren of concurrenten zijn geplaatst\n- Dat geen sprake is van betaalde of be\u00efnvloede reviews\n- Dat reviews niet gemanipuleerd zijn\n\nAanbeveling:\n\n- Lees meerdere reviews voor een genuanceerd beeld\n- Let op details en consistentie tussen reviews\n- Vertrouw op je eigen indruk bij bezoek\n- Meld verdachte reviews via \"Probleem melden\"\n\n6.5 Gebruikersroutes\nCommunity-routes zijn:\n\n- Gemaakt door andere gebruikers, op basis van hun voorkeuren\n- Niet gecontroleerd of geoptimaliseerd door VintMap\n- Mogelijk niet actueel (winkels gesloten, verhuisd)\n- Gebaseerd op persoonlijke smaak en doelen\n\nGeen garantie op:\n\n- Effici\u00ebntie of optimaliteit van route\n- Geschiktheid voor jouw voertuig of voorkeuren\n- Actualiteit van opgenomen winkels\n- Kwaliteit van beschrijvingen\n\n6.6 Melden van ongepaste content\nAls je content tegenkomt die:\n\n- Ongepast, beledigend of illegaal is\n- Persoonlijke gegevens bevat\n- Misleidend of spam is\n- Intellectuele eigendomsrechten schendt\n\nMeld dit via:\n\n- \"Probleem melden\" functie in app\n- E-mail naar moderation@vintmap.nl\n\nVintMap onderneemt passende actie na beoordeling.",
    "disclaimer.7.title": "7. Advertenties en commerci\u00eble content",
    "disclaimer.7.body": "7.1 Aanwezigheid van advertenties\nVintMap kan advertenties tonen van:\n\n- Kringloopwinkels en vintage winkels\n- Gerelateerde producten en diensten (verven, DIY, interieur)\n- Partners en samenwerkingsverbanden\n- Third-party advertentienetwerken\n\nAdvertenties kunnen verschijnen als:\n\n- Banners in de app of website\n- Gesponsorde zoekresultaten\n- Gesponsorde kringloopwinkels (gemarkeerd)\n- Promotionele content in nieuwsbrieven (indien van toepassing)\n\n7.2 Geen garanties of endorsement\nBELANGRIJK: VintMap garandeert NIETS over geadverteerde producten, diensten of winkels.\nDit betekent:\n\n- VintMap test of verifieert geadverteerde producten NIET\n- Advertenties zijn GEEN aanbeveling of goedkeuring door VintMap\n- VintMap staat NIET in voor kwaliteit, veiligheid of legaliteit\n- VintMap is NIET betrokken bij transacties tussen jou en adverteerders\n\nAdverteren op VintMap betekent NIET:\n\n- Dat VintMap de adverteerder vertrouwt of aanbeveelt\n- Dat producten/diensten van hoge kwaliteit zijn\n- Dat claims in advertenties waar of bewezen zijn\n- Dat VintMap verantwoordelijkheid neemt\n\n7.3 Geen rechten te ontlenen aan advertenties\nAan advertenties kunnen GEEN rechten worden ontleend:\n\n- Geen recht op aanwezigheid van specifiek aanbod\n- Geen recht op correctheid van prijs of voorwaarden\n- Geen recht op beschikbaarheid van product\n- Geen recht op kwaliteit of specificaties zoals geadverteerd\n- Geen recht op nakoming van beloftes in advertenties\n\nAls een advertentie fout is:\n\n- VintMap kan de advertentie verwijderen\n- VintMap is NIET aansprakelijk voor schade\n- Jouw verhaal ligt bij de adverteerder, niet bij VintMap\n\n7.4 Third-party advertentienetwerken\nVintMap kan gebruik maken van externe advertentienetwerken (zoals Google Ads, Facebook Ads, etc.).\nDeze partijen:\n\n- Verzamelen mogelijk data over je gedrag (zie Privacybeleid)\n- Hebben eigen voorwaarden en privacybeleid\n- Bepalen welke advertenties getoond worden (binnen richtlijnen VintMap)\n- Zijn verantwoordelijk voor hun eigen advertenties\n\nVintMap heeft beperkte controle over:\n\n- Welke specifieke advertenties worden getoond\n- Inhoud van dynamische advertenties\n- Targeting en personalisatie door adverteerders\n\n7.5 Geen aansprakelijkheid voor adverteerders\nVintMap is NIET aansprakelijk voor:\n\n- Misleidende of valse claims in advertenties\n- Producten die niet voldoen aan verwachtingen\n- Non-delivery of slechte service van adverteerders\n- Geschillen tussen jou en adverteerder\n- Schade door gebrekkige producten\n- Privacy-schendingen door adverteerders\n- Problemen met betalingen of restitutie\n\nBij problemen met adverteerder:\n\n- Neem contact op met de adverteerder direct\n- Gebruik normale klachtenprocedures of consumentenrecht\n- Meld ernstige misleiding aan VintMap (moderation@vintmap.nl)\n- VintMap kan adverteerder verwijderen maar compenseert niet\n\n7.6 Gesponsorde content transparantie\nGesponsorde content wordt gemarkeerd als:\n\n- \"Gesponsord\"\n- \"Advertentie\"\n- \"Betaalde plaatsing\"\n- \"Partner\"\n\nVintMap streeft naar transparantie over commerci\u00eble relaties.\nLET OP:\nGesponsorde winkels in zoekresultaten zijn niet perse beter of betrouwbaarder. Zij betalen voor zichtbaarheid, niet voor kwaliteit.\n\n7.7 Advertentie-beleid\nVintMap weigert advertenties voor:\n\n- Illegale producten of diensten\n- Discriminerende of haatdragende content\n- Expliciete of ongepaste content\n- Duidelijk misleidende of frauduleuze aanbiedingen\n\nDit beleid is echter niet 100% waterdicht. Als je een problematische advertentie ziet, meld deze dan.",
    "disclaimer.8.title": "8. Externe diensten en links",
    "disclaimer.8.body": "8.1 Links naar externe websites\nVintMap kan links bevatten naar:\n\n- Websites van kringloopwinkels\n- Social media pagina's van winkels\n- Partner-websites\n- Informatieve bronnen\n- Google Maps en externe navigatie\n\n8.2 Geen controle of verantwoordelijkheid\nVintMap is NIET verantwoordelijk voor:\n\n- Inhoud van externe websites\n- Beschikbaarheid van externe links\n- Privacy-praktijken van externe partijen\n- Veiligheid of malware op externe sites\n- Voorwaarden of beleid van derden\n- Transacties via externe websites\n\nExterne websites zijn volledig eigen verantwoordelijkheid van de eigenaren ervan.\n\n8.3 Geen endorsement\nHet aanwezig zijn van een link betekent NIET:\n\n- Dat VintMap de website of organisatie aanbeveelt\n- Dat VintMap de inhoud heeft gecontroleerd of goedkeurt\n- Dat VintMap instaat voor juistheid van informatie\n- Dat enige affiliatie of samenwerking bestaat (tenzij expliciet vermeld)\n\n8.4 Verbroken links\nLinks kunnen na verloop van tijd:\n\n- Niet meer werken (404-error)\n- Naar andere content leiden (site overgenomen)\n- Omgeleid worden naar ongewenste inhoud\n\nMeld verbroken of problematische links via feedback@vintmap.nl.\n\n8.5 Third-party diensten\nVintMap maakt gebruik van diensten van derden:\n\n- Google Maps: Voor kaarten en navigatie\n- Stripe: Voor betalingen\n- OpenAI / AI providers: Voor restyler-functionaliteit\n- Hosting providers: Voor technische infrastructuur\n\nDeze partijen:\n\n- Hebben eigen voorwaarden en disclaimer\n- Zijn verantwoordelijk voor hun eigen dienstverlening\n- Kunnen eigen beperkingen of storingen hebben\n\nZie hun eigen websites voor specifieke disclaimers.\n\n8.6 Social media integratie\nVintMap kan integreren met social media:\n\n- Delen van content op Facebook, Instagram, etc.\n- Inloggen via social media accounts (indien ge\u00efmplementeerd)\n- Weergave van social media feeds\n\nBij gebruik van deze integraties:\n\n- Zijn de voorwaarden van het social media platform van toepassing\n- Deelt VintMap mogelijk data met het platform (zie Privacybeleid)\n- Heb je geen controle over hoe het platform je data verwerkt",
    "disclaimer.9.title": "9. Geen professioneel advies",
    "disclaimer.9.body": "9.1 Algemene informatievoorziening\nVintMap biedt algemene informatie en hulpmiddelen. Het verstrekt GEEN professioneel advies op het gebied van:\n\n- Juridische zaken\n- Financi\u00eble planning of investeringen\n- Taxaties of waardebepalingen\n- Restauratie of meubelbewerking\n- Interieurontwerp\n- Gezondheid en veiligheid (bijv. asbest, loodverf)\n\n9.2 Geen vervanging voor professionele consultatie\nVoor belangrijke beslissingen:\n\n- Raadpleeg een gekwalificeerde professional\n- Vertrouw niet alleen op informatie uit VintMap\n- Zoek second opinions waar nodig\n- Neem verantwoordelijkheid voor eigen onderzoek\n\n9.3 Specifieke disclaimers\nTaxaties en waardebepalingen:\n\n- VintMap geeft GEEN indicatie van waarde van meubels\n- Prijzen in winkels zijn geen objectieve marktwaarde\n- Voor verzekering of verkoop: raadpleeg taxateur\n\nRestauratie en DIY:\n\n- VintMap geeft GEEN instructies voor restauratie\n- Restyler is visualisatie, geen handleiding\n- Voor technisch advies: raadpleeg vakman\n\nGezondheid en veiligheid:\n\n- VintMap waarschuwt NIET voor gevaarlijke materialen\n- Oude meubels kunnen asbest, loodverf, etc. bevatten\n- Bij twijfel: laat testen door professional\n\nJuridisch:\n\n- VintMap geeft GEEN juridisch advies\n- Voor contracten, aansprakelijkheid, etc.: raadpleeg jurist\n\n9.4 Educatieve content\nEventuele tips, blogs of educatieve content binnen VintMap:\n\n- Zijn algemene informatie, geen persoonlijk advies\n- Zijn bedoeld ter inspiratie, niet als professionele richtlijn\n- Kunnen verouderd of onvolledig zijn\n- Vervangen geen professionele training of opleiding",
    "disclaimer.10.title": "10. Beschikbaarheid en technische werking",
    "disclaimer.10.body": "10.1 Geen garantie op beschikbaarheid\nVintMap streeft naar hoge beschikbaarheid maar garandeert niet:\n\n- 24/7 ononderbroken werking\n- Afwezigheid van bugs of errors\n- Compatibiliteit met alle apparaten/browsers\n- Specifieke laadtijden of prestaties\n\n10.2 Technische beperkingen\nVintMap kan problemen ondervinden door:\n\n- Server downtime of onderhoud\n- Internetuitval (bij gebruiker of provider)\n- Browser-incompatibiliteit\n- Mobiele data-beperkingen\n- Apparaat-specifieke problemen\n- Third-party diensten die uitvallen\n\n10.3 Geen compensatie bij uitval\nBij technische problemen of uitval:\n\n- VintMap lost problemen zo snel mogelijk op\n- Er is geen recht op compensatie (behalve waar wettelijk verplicht)\n- Insider-gebruikers: zie Algemene Voorwaarden voor specifieke regelingen\n\n10.4 Software updates en wijzigingen\nVintMap kan:\n\n- Features toevoegen, wijzigen of verwijderen\n- User interface aanpassen\n- Functionaliteit veranderen\n- Systeemvereisten wijzigen\n\nZie Algemene Voorwaarden artikel 11 voor details over wijzigingen.",
    "disclaimer.11.title": "11. Intellectueel eigendom van derden",
    "disclaimer.11.body": "11.1 Google Maps attributie\nKaartdata en routeberekeningen zijn eigendom van Google LLC en gelicenseerde dataproviders. Bij gebruik van VintMap accepteer je automatisch de Google Maps-voorwaarden.\nZie: https://www.google.com/help/terms_maps/\n\n11.2 Andere third-party content\nVintMap bevat mogelijk:\n\n- Iconen en graphics van third-party libraries\n- Fonts gelicenseerd van designers\n- AI-modellen gelicenseerd van providers\n- Data van openbare databases\n\nAlle rechten behoren toe aan respectievelijke eigenaren.\n\n11.3 Merken van derden\nMerknamen en logo's van kringloopwinkels, producten of diensten die binnen VintMap worden getoond, zijn eigendom van respectievelijke merkhouders. VintMap claimt geen eigendom over deze merken.",
    "disclaimer.12.title": "12. Prijzen en aanbiedingen",
    "disclaimer.12.body": "12.1 Prijzen in winkels\nPrijzen van items in kringloopwinkels:\n\n- Worden NIET door VintMap bepaald of gegarandeerd\n- Kunnen verschillen van eventuele vermeldingen in reviews\n- Zijn onderhevig aan wijziging zonder kennisgeving\n- Zijn altijd te verifi\u00ebren bij de winkel zelf\n\nVintMap toont GEEN productprijzen en geeft GEEN prijsindicaties.\n\n12.2 VintMap abonnementsprijzen\nPrijzen voor Insider-abonnementen:\n\n- Zijn vermeld op de website en in de app\n- Zijn inclusief BTW (voor Nederlandse gebruikers)\n- Kunnen wijzigen conform Algemene Voorwaarden artikel 8.5\n- Zijn altijd actueel op moment van aankoop (prijs in checkout geldt)\n\n12.3 Actiecodes en kortingen\nPromoties en kortingen:\n\n- Hebben beperkte geldigheid\n- Kunnen voorwaarden bevatten (eenmalig, nieuwe gebruikers)\n- Zijn niet combineerbaar tenzij anders vermeld\n- Kunnen zonder kennisgeving worden be\u00ebindigd\n\nGeen rechten te ontlenen aan verlopen acties.",
    "disclaimer.13.title": "13. Beperking van aansprakelijkheid",
    "disclaimer.13.body": "13.1 Algemene aansprakelijkheidsbeperking\nVoor zover wettelijk toegestaan is VintMap NIET aansprakelijk voor:\n\nDirecte schade:\n- Verloren tijd tijdens onjuiste routes\n- Parkeer- of brandstofkosten\n- Teleurstelling over winkelbezoek\n- Gemiste kansen door onjuiste informatie\n\nIndirecte schade:\n- Winstderving\n- Gemiste zakelijke kansen\n- Reputatieschade\n- Gevolgschade van welke aard dan ook\n\nImmateri\u00eble schade:\n- Stress of frustratie\n- Gemis van plezier of vrije tijd\n\n13.2 Maximering van aansprakelijkheid\nWaar VintMap WEL aansprakelijk is, is de aansprakelijkheid beperkt zoals beschreven in de Algemene Voorwaarden artikel 13.\n\n13.3 Uitzonderingen\nAansprakelijkheid wordt NIET uitgesloten voor:\n\n- Opzet of grove schuld van VintMap\n- Schade door overlijden of lichamelijk letsel\n- Wettelijke aansprakelijkheid die niet uitgesloten kan worden\n\n13.4 Schade door derden\nVintMap is NIET aansprakelijk voor schade veroorzaakt door:\n\n- Andere gebruikers (onjuiste reviews, etc.)\n- Kringloopwinkels (slechte service, defecte items)\n- Third-party diensten (Google Maps, AI provider)\n- Adverteerders\n- Externe websites\n\nJe verhaal ligt bij de veroorzaker van de schade, niet bij VintMap.",
    "disclaimer.14.title": "14. Eigen verantwoordelijkheid",
    "disclaimer.14.body": "14.1 Verificatieplicht\nHet is jouw verantwoordelijkheid om:\n\n- Belangrijke informatie te verifi\u00ebren (openingstijden, locatie)\n- Meerdere bronnen te raadplegen bij twijfel\n- Contact op te nemen met winkels bij onzekerheden\n- Realistische verwachtingen te hebben\n\n14.2 Eigen beoordeling\nGebruik je eigen oordeel bij:\n\n- Beslissingen om meubels te kopen\n- Beoordeling van kwaliteit en staat van items\n- Keuze van routes en reisplanning\n- Interpreteren van reviews en visualisaties\n- Vertrouwen op AI-restyles\n\n14.3 Veiligheid\nJij bent verantwoordelijk voor:\n\n- Verkeersveiligheid tijdens gebruik van routes\n- Veilige omgang met tools en materialen bij DIY\n- Beschermen van je eigen account en apparaten\n- Naleven van wetten en regels\n\n14.4 Back-ups\nMaak zelf back-ups van:\n\n- Belangrijke opgeslagen routes\n- Foto's of content die je niet wilt verliezen\n- Accountinformatie\n\nVintMap garandeert geen 100% data-behoud.",
    "disclaimer.15.title": "15. Wijzigingen en actualisering",
    "disclaimer.15.body": "15.1 Continue ontwikkeling\nVintMap is een doorlopend ontwikkeld platform. Dit betekent:\n\n- Informatie kan verouderd raken\n- Features kunnen wijzigen\n- Functionaliteit kan worden toegevoegd of verwijderd\n\n15.2 Geen garantie op actualiteit\nOndanks inspanningen kan informatie:\n\n- Achterhaald zijn op moment van gebruik\n- Niet de laatste wijzigingen weerspiegelen\n- Incompleet zijn door technische beperkingen\n\n15.3 Wijzigingen in disclaimer\nDeze disclaimer kan worden gewijzigd:\n\n- Om te voldoen aan nieuwe wetgeving\n- Bij veranderingen in de dienst\n- Ter verduidelijking of verbetering\n\nZie Algemene Voorwaarden artikel 18 voor details over wijzigingsprocedure.",
    "disclaimer.16.title": "16. Relatie met andere documenten",
    "disclaimer.16.body": "16.1 Algemene Voorwaarden\nDeze disclaimer maakt integraal onderdeel uit van de Algemene Voorwaarden van VintMap. Bij tegenstrijdigheid prevaleren de Algemene Voorwaarden.\nVoor juridisch bindende bepalingen over:\n\n- Rechten en verplichtingen\n- Abonnementen en betalingen\n- Be\u00ebindiging en opschorting\n- Toepasselijk recht\n\nZie: https://vintmap.nl/voorwaarden\n\n16.2 Privacybeleid\nVoor informatie over gegevensbescherming en privacy:\nZie: https://vintmap.nl/privacy\n\n16.3 Voorrang bij conflict\nBij tegenstrijdigheid tussen documenten geldt de volgende volgorde:\n\n- Algemene Voorwaarden (juridisch bindend)\n- Privacybeleid (AVG-compliance)\n- Deze Disclaimer (verduidelijking en waarschuwingen)",
    "disclaimer.17.title": "17. Contactgegevens",
    "disclaimer.17.body": "17.1 Vragen over deze disclaimer\nVoor vragen, opmerkingen of suggesties over deze disclaimer:\nE-mail: legal@vintmap.nl\nPostadres:\nVintMap\nVoorstraat 142\n9291 CP Kollum\nNederland\n\n17.2 Melden van problemen\nOnjuiste informatie of technische problemen:\nfeedback@vintmap.nl\nOngepaste content of advertenties:\nmoderation@vintmap.nl\nBeveiligingslekken:\nsecurity@vintmap.nl\n\n17.3 Reactietijd\nWij streven ernaar om binnen 5 werkdagen te reageren op vragen over deze disclaimer.",

    "faq.title": "Veelgestelde Vragen",
    "faq.intro": "Hier vind je antwoorden op de meest gestelde vragen over VintMap. Staat je vraag er niet tussen? Neem dan contact met ons op.",
    "faq.general.q1": "Wat is VintMap?",
    "faq.general.a1": "VintMap is hét platform voor kringloop- en vintageliefhebbers. Je ontdekt eenvoudig de leukste kringloopwinkels, plant routes, en deelt je vondsten met de community.",
    "faq.general.q2": "Is VintMap gratis te gebruiken?",
    "faq.general.a2": "Ja, het gebruik van VintMap is gratis voor bezoekers. Je kunt gratis zoeken, routes bekijken en winkels vinden. Voor extra functies zoals 'Mijn Routes' (onbeperkt opslaan) bieden we een Insider abonnement aan.",
    "faq.general.a3": "VintMap is een Progressive Web App (PWA). Dit betekent dat je de website via je browser op je telefoon kunt 'installeren' als app, zonder dat je via de App Store of Play Store hoeft te gaan. Je krijgt dan ook een icoontje op je startscherm.",
    "faq.visitors.title": "Voor Bezoekers",
    "faq.visitors.q1": "Hoe plan ik een route?",
    "faq.visitors.a1": "Ga naar de 'Routes' pagina in het menu. Hier kun je een begin- en eindpunt invullen en je favoriete winkels toevoegen. De app berekent dan de meest optimale route voor je.",
    "faq.visitors.q2": "Kan ik zelf een winkel toevoegen?",
    "faq.visitors.a2": "Zeker! Als je bent ingelogd kun je via 'Winkel toevoegen' in het menu een nieuwe suggestie doen. Ons team controleert de gegevens voordat de winkel officieel online gaat.",
    "faq.visitors.q4": "Kan ik mijn vondsten delen?",
    "faq.visitors.a4": "Ja, via 'Deel je buit' kun je foto's van je mooiste aankopen delen met de community. Andere gebruikers kunnen deze liken en reageren.",
    "faq.visitors.q5": "Zijn er limieten aan het gebruik van de kaart?",
    "faq.visitors.a5": "Ja, om VintMap gratis en duurzaam te houden voor iedereen, hanteren we een Fair Use policy voor interactieve onderdelen. Voor Insiders geldt een ruime limiet van 20 interactieve kaartweergaven per dag (240 per maand) en 10 route-optimalisaties per dag (60 per maand). Voor gratis accounts zijn deze limieten lager. De statische overzichtskaarten zijn altijd onbeperkt zichtbaar.",
    "faq.visitors.q6": "Verdwijnt mijn tijdelijke winkellijst na een tijdje automatisch?",
    "faq.visitors.a6": "Ja. Kringlopen die je tijdelijk hebt geselecteerd (je 'concept route') worden automatisch gewist na 72 uur. Dit is om te voorkomen dat je per ongeluk een verouderde selectie meeneemt. Sla je route op als je hem wilt bewaren!",
    "faq.owners.title": "Voor Winkeliers",
    "faq.owners.q1": "Hoe claim ik mijn winkel?",
    "faq.owners.a1": "Zoek je winkel op de kaart of in de lijst. Als deze nog niet geclaimd is, zie je een knop 'Ben jij de eigenaar?'. Volg de stappen om je winkel te claimen en toegang te krijgen tot het beheerderspaneel.",
    "faq.owners.q2": "Wat kost een bedrijfsprofiel?",
    "faq.owners.a2": "Een basisvermelding is altijd gratis. Voor uitgebreide opties zoals het beheren van openingstijden, uploaden van eigen foto's en reageren op reviews vragen we een eenmalige verificatiebijdrage of bieden we premium abonnementen aan.",
    "faq.owners.q3": "Hoe wijzig ik mijn openingstijden?",
    "faq.owners.a3": "Na het claimen en verifiëren van je winkel kun je inloggen op je dashboard. Daar kun je direct je openingstijden, adresgegevens en foto's aanpassen.",
    "faq.owners.q4": "Ik heb geen verificatiebrief ontvangen, wat nu?",
    "faq.owners.a4": "De verificatiebrief wordt binnen 2 werkdagen verstuurd. Heb je na 5 werkdagen nog niets ontvangen? Neem dan contact op met support@vintmap.nl.",
    "faq.account.title": "Account & Privacy",
    "faq.account.q1": "Hoe wijzig ik mijn wachtwoord?",
    "faq.account.a1": "In je accountinstellingen kun je je wachtwoord wijzigen. Als je je wachtwoord vergeten bent, gebruik dan de 'Wachtwoord vergeten' link op de inlogpagina.",
    "faq.account.q2": "Worden mijn gegevens gedeeld?",
    "faq.account.a2": "Wij geven veel om je privacy. Je persoonlijke gegevens worden niet verkocht aan derden. Alleen noodzakelijke gegevens voor de werking van de app (zoals locatie voor de kaart) worden gebruikt volgens ons Privacybeleid.",
    "faq.account.q3": "Hoe verwijder ik mijn account?",
    "faq.account.a3": "Je kunt een verzoek tot accountverwijdering indienen via je accountinstellingen of door een mail te sturen naar privacy@vintmap.nl. We verwijderen dan al je persoonlijke gegevens.",
    "faq.tech.title": "Technisch & Contact",
    "faq.tech.q1": "De kaart laadt niet, wat moet ik doen?",
    "faq.tech.a1": "Controleer of je internetverbinding werkt en of je locatietoegang hebt toegestaan in je browser. Probeer de pagina te verversen. Blijft het probleem? Stuur ons een berichtje.",
    "faq.tech.q2": "Hoe neem ik contact op?",
    "faq.tech.a2": "Voor technische vragen, suggesties of samenwerkingen kun je mailen naar info@vintmap.nl.",
    "faq.contact.title": "Niet gevonden wat je zocht?",
    "faq.contact.desc": "Ons support team staat voor je klaar. Stuur ons een berichtje en we helpen je graag verder.",
    "faq.contact.link": "Neem contact op",
    "faq.q12": "Waarom waarschuwt de scanner soms dat iets niet zichtbaar is?",
    "faq.a12": "Dat is bewust: de scanner mag geen details verzinnen. Als informatie visueel niet vaststaat, wordt dit expliciet aangegeven.",
    "faq.q13": "In welke taal komt scanneroutput?",
    "faq.a13": "Scanneroutput volgt je gekozen app-taal. Bij ontbrekende vertaling gebruikt de app tijdelijke fallback totdat vertalingen zijn aangevuld.",
    "faq.q14": "Kan ik scannerresultaten opslaan?",
    "faq.a14": "Ja, scannerresultaten worden gekoppeld aan je accountgeschiedenis binnen de beschikbare functionaliteit van je accountniveau.",
    "faq.q15": "Hoe werken likes, reviews en communityposts?",
    "faq.a15": "Je kunt bijdragen plaatsen en reageren binnen de communityfuncties. Inhoud moet aan de platformregels voldoen.",
    "faq.q16": "Hoe werken betaalde functies en abonnementen?",
    "faq.a16": "Betaalde functies worden via Stripe afgehandeld. Facturatie en verlenging volgen het gekozen abonnementstype.",
    "faq.q17": "Kan ik opzeggen?",
    "faq.a17": "Ja, opzeggen kan via je accountinstellingen. De wijziging gaat in volgens de voorwaarden van je actieve periode.",
    "faq.q18": "Welke gegevens bewaren jullie over mij?",
    "faq.a18": "Alleen gegevens die nodig zijn voor account, functionaliteit, veiligheid en wettelijke verplichtingen, zoals beschreven in het privacybeleid.",
    "faq.q19": "Hoe vraag ik inzage of verwijdering aan?",
    "faq.a19": "Via de beschikbare accountopties of door contact op te nemen via de contactpagina. We behandelen verzoeken conform wetgeving.",
    "faq.q20": "Waar meld ik bugs of juridische vragen?",
    "faq.a20": "Gebruik de contactpagina voor support, bugmeldingen en juridische vragen. Vermeld zo veel mogelijk context voor snelle opvolging.",
    "faq.q21": "Waarom geeft de Restyler soms vreemde resultaten?",
    "faq.a21": "De Restyler gebruikt generatieve AI om je meubel te 'herschilderen'. AI werkt met patronen en kan soms fouten maken (hallucineren). Zo kan de AI per ongeluk glas toevoegen aan houtsnijwerk of een handgreep vervormen. Gebruik de beschikbare extra opties (zoals 'Bevat glas') om de AI bij te sturen.",
    "faq.q22": "Is de Scanner altijd 100% accuraat?",
    "faq.a22": "Nee. De AI-scanner leert van miljoenen afbeeldingen, maar kan specifieke vintage objecten, materialen of zeldzame merken verkeerd interpreteren. Zie de scanner-output altijd als een 'second opinion' of startpunt voor eigen onderzoek, nooit als een onbetwistbaar feit.",
    "faq.q23": "Waarom lijkt de AI soms eigenwijs?",
    "faq.a23": "AI modellen hebben soms een sterke voorkeur voor bepaalde stijlen of belichting die ze tijdens hun training veel hebben gezien. Hierdoor kan het gebeuren dat de AI een specifieke instructie negeert of een eigen draai geeft aan een ontwerp. We verbeteren onze prompts continu om dit te minimaliseren.",
    "faq.q24": "Wat gebeurt er met de foto's die ik naar de AI stuur?",
    "faq.a24": "Foto's die je scant of restyled worden tijdelijk verwerkt door beveiligde AI-servers van derden. Deze beelden worden niet gebruikt om publieke modellen van derden te trainen zonder jouw toestemming. Bekijk ons privacybeleid voor alle details over gegevensverwerking.",
    "home.hero.imageAlt": "Kringloopwinkel",
    "#6D0E0E": "Bordeaux",
    "#E2725B": "Warm Terracotta",
    "#FF7F50": "Koraal",
    "#CCA9AC": "Oudroze",
    "#CC7722": "Okergeel",
    "#E3A857": "Honinggeel",
    "#F6E0A4": "Eigeel",
    "#6B8E23": "Olijfgroen",
    "#4E5B31": "Mosgroen",
    "#87A96B": "Saliegroen",
    "#1A2421": "Dennegroen",
    "#006060": "Teal",
    "#A5C5D9": "Hemelsblauw",
    "#1A3A6D": "Koningsblauw",
    "#B2A4D4": "Lavendel",
    "#3D0C29": "Aubergine",
    "#FAF9F6": "Linnen",
    "#F5F5DC": "Antiek Wit",
    "#D9C5A3": "Zandbeige",
    "#8B8589": "Taupe",
    "#708090": "Leigrijs",
    "#36454F": "Houtskool",
    "#0A0A0A": "Gitzwart",
    "badges.vonst_week": "Vondst van de week",
    "badges.route_week": "Route van de week",
    "badges.kringloop_expert": "Kringloop expert",
    "badges.route_planner": "Route planner",
    "badges.insider": "Insider",
    "badges.power_user": "Power user",
    "community.title": "Community",
    "community.subtitle": "Ontdek de beste kringloopvondsten en updates van je favoriete winkels.",
    "community.shareFind": "Deel je vondst",
    "community.viewMore": "Bekijk meer",
    "community.category": "Categorie",
    "community.allCategories": "Alle categorieën",
    "community.region": "Regio",
    "community.allRegions": "Alle regio's",
    "community.loadingPosts": "Posts laden...",
    "community.userGems": "Kringloop Geluk",
    "community.noGems": "Nog geen vondsten gevonden.",
    "community.shopUpdates": "Winkel updates",
    "community.myFavoriteShops": "Mijn favoriete winkels",
    "community.noFavoriteUpdates": "Geen updates van je favorieten",
    "community.likeShopsHint": "Like winkels om hun updates hier te zien.",
    "community.noShopUpdates": "Geen winkel updates gevonden.",
    "community.searchPlaceholder": "Zoek posts, winkels of restyles...",
    "community.tabs.explore": "Ontdekken",
    "community.tabs.treasureHunters": "Schatzoekers",
  },
  en: {
    "seo.home.title": "VintMap - Plan optimized routes along thrift stores in {region}",
    "seo.home.description": "Share your finds and restyle with AI. #1 Thrift Community.",
    "seo.shops.title": "VintMap - Find the best thrift stores in {region}",
    "seo.shops.description": "Plan routes, share your finds and restyle with AI. #1 Thrift Community.",
    "seo.region.title": "VintMap: Plan optimized routes along thrift stores in {region}",
    "nav.wishlist": "Wishlist",
    "community.searchPlaceholder": "Search posts, shops or restyles...",
    "community.pricePaid": "Price paid",
    "wishlist.itemHeart": "♡",
    "wishlist.itemName": "Item name",
    "nav.spaceblend": "SpaceBlend",
    "social.follow": "Follow",
    "social.unfollow": "Unfollow",
    "social.following": "Following",
    "social.followers": "Followers",
    "social.feed": "Treasures Feed",
    "social.feed.empty": "No posts in your feed yet. Follow other treasure hunters or shops to see their updates!",
    "social.feed.postedTreasure": "Posted a treasure",
    "social.feed.shopUpdate": "Shop Update",
    "social.profile.edit": "Edit Profile",
    "social.profile.completed": "Profile completed",
    "social.search.placeholder": "Search treasure hunters...",
    "social.wishlist.public": "Public wishlist",
    "social.wishlist.private": "Private wishlist",
    "social.style.lookingFor": "Looking for",
    "social.style.bio": "Style & Inspiration",
    "social.feed.noUpdates": "No updates in your feed yet",
    "social.noUsersFoundFor": "No treasure hunters found for \"{search}\"",
    "social.search.minChars": "Type at least 2 characters to search for other treasure hunters.",
    "social.profile.postsCount": "Posts",
    "social.profile.info": "Info",
    "social.profile.wishlist": "Wishlist",
    "social.profile.userNotFound": "The treasure hunter \"{username}\" could not be found.",
    "social.profile.shareText": "Check out the profile of {name} on VintMap",
    "social.profile.linkCopied": "Link copied!",
    "social.profile.since": "Since {date}",
    "social.profile.website": "Website",
    "social.profile.noStyle": "This treasure hunter hasn't added a style description yet.",
    "social.profile.noInterests": "No specific interests specified yet.",
    "social.profile.wishlistComingSoon": "Wishlist items will be shown here (COMING SOON)",
    "social.profile.noPosts": "This treasure hunter hasn't posted any gems yet.",
    "social.settings.fullName": "Full name (or alias)",
    "social.settings.location": "City / Region",
    "account.tabs.social": "Social",
    "shopDashboard.postUpdate": "Post Update",
    "common.loading": "Loading...",
    "common.anonymous": "Anonymous Treasure Hunter",
    "common.shareExternally": "Share externally",
    "common.download": "Download",
    "common.search": "Search",
    "shops.reviews": "Reviews",
    "shops.noReviewsYet": "No reviews yet",
    "shops.title.favorites": "Favorite thrift shops",
    "shops.title.wishlist": "My wishlist",
    "shops.title.region": "Thrift Stores {name}",
    "shops.description.region": "Start a trail via the best thrift stores in {name}!",
    "shops.description.personal": "Your personal selection",
    "shops.region.province": "Province",
    "shops.region.county": "County",
    "shops.region.municipality": "Municipality",
    "shops.region.city": "City",
    "shops.region.whichOne": "Did you mean this?",
    "shops.region.multipleFound": "We found multiple locations. Make a choice:",
    "shops.region.choose": "Choose",
    "shops.cancel": "Cancel",
    "footer.discover": "Discover",
    "footer.forShops": "For shops",
    "footer.aboutUs": "About us",
    "footer.language": "Language",
    "footer.madeWith": "Made with",
    "footer.sustainability": "for sustainability",
    "footer.claimShop": "Claim your thrift shop",
    "footer.aboutVintmap": "About VintMap",
    "footer.faq": "Frequently Asked Questions",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "General Terms and Conditions",
    "footer.disclaimer": "Disclaimer",
    "footer.contact": "Contact",
    "footer.tagline": "Discover community routes, plan your thrift day, and share your finds.",
    "header.planRoute": "Plan route",
    "header.planShort": "Plan",
    "header.myShops": "My shop(s)",
    "header.mobileMenuDesc": "Navigation menu for mobile users",
    "header.tier.free": "Free",
    "header.tier.insider": "Insider",
    "home.hero.title": "Discover community routes, plan your thrift day, and share your finds.",
    "home.hero.subtitle": "The app for thrift lovers. Find the best routes, discover new shops, and share your best finds.",
    "home.hero.routes": "Discover routes",
    "home.hero.shops": "Find thrift shops",
    "home.hero.optimize": "Route Optimization",
    "home.hero.startHome": "Start & end at home",
    "home.hero.statusInsider": "Status: VintMap Insider",
    "home.featured": "Featured",
    "home.gem.title": "Find of the week",
    "home.gem.empty": "No find of the week yet. Be the first!",
    "home.posts.all": "All posts",
    "home.posts.viewAll": "View all posts",
    "home.route.title": "Route of the week",
    "home.routes.all": "All routes",
    "home.routes.viewAll": "View all routes",
    "home.how.title": "How it works",
    "home.how.subtitle": "You are ready for your thrift adventure in three simple steps.",
    "home.how.step1.title": "1. Discover",
    "home.how.step1.desc": "Browse hundreds of community routes or find thrift stores near you.",
    "home.how.step2.title": "2. Plan",
    "home.how.step2.desc": "Create your own route with favorite shops, or follow an existing route.",
    "home.how.step3.title": "3. Share",
    "home.how.step3.desc": "Share your route and post your best finds in the community.",
    "home.insider.badge": "VintMap Insider",
    "home.insider.titlePrefix": "Get the most out of your",
    "home.insider.titleHighlight": "thrift day",
    "home.insider.subtitle": "With VintMap Insider, create unlimited routes, optimize order, and start from your own doorstep. Discover more, travel less.",
    "home.insider.doorToDoor.title": "Door-to-door navigation",
    "home.insider.doorToDoor.desc": "Custom start and end points require personal route calculations. With Insider, you help keep VintMap affordable.",
    "home.insider.cta": "Become Insider",
    "home.insider.priceMonth": "/ month",
    "home.insider.priceYear": "or €29.99 per year (2 months free)",
    "upgrade.default.title": "Become VintMap Insider",
    "upgrade.default.desc": "Unlock all premium features and support VintMap.",
    "upgrade.default.feat1": "Create your own thrift routes",
    "upgrade.perk.socialWishlist": "Social Wishlist & Friends",
    "upgrade.default.feat3": "AI Furniture Restyle",
    "upgrade.default.feat4": "Optimize existing routes",
    "upgrade.default.feat5": "Start & end at your home address",
    "upgrade.home.feat1": "Personal route calculation",
    "upgrade.home.feat2": "Optimized order",
    "upgrade.home.feat3": "Always the smartest way",
    "upgrade.routes.title": "Need more routes?",
    "upgrade.routes.desc": "You've reached the maximum number of free routes. Upgrade to VintMap Insider for unlimited routes.",
    "upgrade.routes.feat1": "Create unlimited routes",
    "upgrade.routes.feat2": "More than 10 stops per route",
    "upgrade.routes.feat3": "Route optimization",
    "upgrade.posts.title": "Share more finds?",
    "upgrade.posts.desc": "You've already posted this week. Upgrade to VintMap Insider for unlimited posts.",
    "upgrade.posts.feat1": "Unlimited posts per week",
    "upgrade.posts.feat2": "Post as a shop (owners)",
    "upgrade.builder.title": "Route Builder - Pro Feature",
    "upgrade.builder.desc": "Build your own thrift route and navigate directly with Google Maps!",
    "upgrade.builder.feat1": "Unlimited route building",
    "upgrade.builder.feat2": "Unlimited number of stops",
    "upgrade.builder.feat3": "Direct navigation via Google Maps",
    "upgrade.builder.feat4": "Share routes with friends",
    "upgrade.monthly": "Monthly",
    "upgrade.yearly": "Yearly",
    "upgrade.maybeLater": "Maybe later",
    "shops.search.placeholder": "Search by name or city...",
    "shops.search.button": "Search",
    "shops.country.placeholder": "Country",
    "shops.country.all": "All countries",
    "shops.province.placeholder": "Region",
    "shops.province.all": "All regions",
    "shops.filters.more": "More filters",
    "shops.filters.shopOptions": "Shop options",
    "shops.filters.payment": "Payment",
    "shops.filters.card": "Card accepted",
    "shops.filters.cashOnly": "Cash only",
    "shops.filters.openingHours": "Opening hours",
    "shops.filters.openNow": "Open now",
    "shops.filters.openOn": "Open on...",
    "shops.filters.day": "Day",
    "shops.filters.radius": "Radius",
    "shops.filters.openOnSelectedDay": "Open",
    "shops.filters.day.monday": "Monday",
    "shops.filters.day.tuesday": "Tuesday",
    "shops.filters.day.wednesday": "Wednesday",
    "shops.filters.day.thursday": "Thursday",
    "shops.filters.day.friday": "Friday",
    "shops.filters.day.saturday": "Saturday",
    "shops.filters.day.sunday": "Sunday",
    "shops.filters.categories": "Categories",
    "shops.filters.priceLevel": "Price level",
    "shops.filters.parking": "Parking",
    "shops.filters.notKnown": "Not known",
    "shops.filters.clear": "Clear filters",
    "shops.sort.placeholder": "Sort",
    "shops.sort.overall": "Overall rating",
    "shops.sort.vintmap": "VintMap rating",
    "shops.sort.google": "Google rating",
    "shops.sort.popular": "Popular",
    "shops.sort.newest": "Newest",
    "shops.sort.nameAsc": "Name (A-Z)",
    "shops.sort.distance": "Distance",
    "shops.addShop": "Add shop",
    "shops.add": "Add",
    "shops.results.loading": "Loading shops...",
    "shops.results.errorTitle": "Loading error",
    "shops.results.errorDesc": "An error occurred while loading shops.",
    "shops.results.retry": "Try again",
    "shops.results.withinRadius": "{count} {plural} found within {radius} km of {search}",
    "shops.results.inProvince": "{count} {plural} found in {province}",
    "shops.results.showing": "Showing {shown} of {total} results",
    "shops.common.shopSingular": "thrift shop",
    "shops.common.shopPlural": "thrift shops",
    "translate.to": "Translate to {language}",
    "translate.loading": "Translating...",
    "translate.failed": "Translation failed.",
    "translate.loginRequired": "Log in to translate.",
    "shops.empty.title": "No thrift shops found",
    "shops.empty.desc": "There are currently no thrift shops in VintMap matching your filters.",
    "shops.empty.add": "Add thrift shop",
    "shops.empty.loginToAdd": "Log in to add a shop",
    "shops.empty.expandSearch": "Expand search radius:",
    "shops.pagination.prev": "Previous",
    "shops.pagination.page": "Page",
    "shops.pagination.next": "Next",
    "shops.map.loginOnlyTitle": "Map for logged-in users only",
    "shops.map.loginOnlyDesc": "Log in or create an account to view the interactive map.",
    "shops.map.loginCta": "Log in or create account",
    "routes.page.title": "Routes",
    "routes.page.favorites": "Favorite routes",
    "routes.page.wishlist": "My route wishlist",
    "routes.page.description": "Discover community routes across the Netherlands",
    "routes.new": "New route",
    "routes.mine": "Your routes",
    "routes.more": "More ({count} left)",
    "routes.favorites.title": "Favorites",
    "routes.wishlist.title": "Wishlist",
    "routes.community.title": "Community routes",
    "routes.viewAll": "View all",
    "routes.empty.favorites": "No favorite routes yet.",
    "routes.empty.wishlist": "No routes in your wishlist yet.",
    "routes.empty.noneIn": "No routes found in {name}.",
    "wishlist": "Wishlist",
    "routes.discover": "Discover routes",
    "scanner.title": "Insider Scanner",
    "scanner.subtitle": "Analyze your finds with VintMap AI.",
    "scanner.myScans": "My scans",
    "scanner.noPhotos.title": "No photos",
    "scanner.noPhotos.desc": "Upload at least one photo of the item first.",
    "scanner.error.title": "Scan error",
    "scanner.invalidResponse": "Invalid scan response. Please try again.",
    "scanner.step1.title": "Step 1: Upload photos",
    "scanner.step1.desc": "Upload up to 4 photos from different angles.",
    "scanner.step2.title": "Step 2: Item type (optional)",
    "scanner.step2.desc": "Choose a type for more focused analysis. Leave as \"Unknown\" if unsure.",
    "scanner.category.search": "Search item type...",
    "scanner.category.empty": "No category found.",
    "scanner.start.title": "Expert Analysis",
    "scanner.start.desc": "For identification, origin and expert verdict.",
    "scanner.feature.identify": "Expert Identification",
    "scanner.feature.value": "Expert Verdict",
    "scanner.feature.links": "Market References",
    "scanner.tips.title": "Insider tips",
    "scanner.tips.one": "Use good lighting and a neutral background.",
    "scanner.tips.two": "Take photos of marks, stamps, or labels.",
    "scanner.tips.three": "Upload clear close-ups for better recognition of details and brand traces.",
    "scanner.faqHelp": "Scanner FAQ & Help",
    "scanner.feedback.title": "Feedback?",
    "scanner.feedback.desc": "Help us improve the AI. Is a result incorrect? Let us know via the feedback button in the result view.",
    "scanner.usage.month": "this month",
    "scanner.insiderCta": "Become Insider for EUR2.99/month",
    "scanner.haveAccount": "Already have an account? Log in here",
    "scanner.back": "Back to Scanner",
    "scanner.notFound": "Scan not found",
    "scanner.badge": "Scan",
    "scanner.scannedOn": "Scanned on",
    "scanner.imageAlt": "Scanned item",
    "scanner.keyPoints": "Key points",
    "scanner.identification": "Identification",
    "scanner.rarity": "Rarity",
    "scanner.valueTitle": "Market position and Verdict",
    "scanner.valueEstimated": "Expert Verdict",
    "scanner.expertRapport": "Expert Report",
    "scanner.expertise": "Expertise and context",
    "scanner.historicalContext": "Historical context",
    "scanner.historicalFallback": "No historical context available.",
    "scanner.authenticityTags": "Authenticity tags",
    "scanner.noMarkers": "No markers available.",
    "scanner.liveSearch": "Live search results (grounding)",
    "scanner.noInsights": "No search insights available.",
    "scanner.expertTip": "Expert tip",
    "scanner.searchTerm": "Professional search term",
    "scanner.physicalTip": "Physical inspection tip",
    "scanner.generatedBy": "Generated by VintMap AI with Live Google Search. Expert verdict is a guideline.",
    "scanner.startNew": "Start new scan",
    "scanner.enteredTraits": "Entered traits",
    "scanner.trait.glass": "Glass",
    "scanner.trait.mirror": "Mirror",
    "scanner.trait.webbing": "Webbing",
    "scanner.trait.transparent": "transparent",
    "scanner.trait.natural": "natural",
    "scanner.characteristics": "Characteristics",
    "scanner.verdictLabel": "VERDICT",
    "scanner.disclaimerShort": "Disclaimer: AI detection is a tool. Results on external platforms are leading. Vintmap is not responsible for purchase decisions.",
    "scanner.invalidResultLoad": "Invalid result. Please try again.",
    "scanner.history.loadError": "Could not load history.",
    "scanner.history.deleteError": "Delete failed.",
    "scanner.history.deletedTitle": "Scan deleted",
    "scanner.history.deletedDesc": "The scan was deleted successfully.",
    "scanner.loginToViewScans": "Log in to view your scans",
    "scanner.history.title": "My scans",
    "scanner.history.subtitle": "An overview of all your AI analyses.",
    "scanner.history.emptyTitle": "No scans yet",
    "scanner.history.emptyDesc": "Use the Insider Scanner to analyze your vintage finds.",
    "scanner.history.firstScan": "Start your first scan",
    "scanner.history.openResult": "Open result",
    "scanner.history.resultFallback": "Scan result",
    "scanner.history.viewAnalysis": "View analysis",
    "scanner.marketValueCheck": "Market References",
    "scanner.checkEbay": "Check eBay",
    "scanner.checkEtsy": "Check Etsy",
    "scanner.checkGoogle": "Google Images",
    "scanner.summary.valueRange": "Expert Verdict available",
    "scanner.summary.valueFrom": "Expert Verdict available",
    "scanner.summary.shortAnalysis": "Short AI analysis available",
    "account.loginRequired": "Login required",
    "account.goToLogin": "Go to login",
    "account.logout": "Log out",
    "account.tabs.profile": "Profile",
    "account.tabs.reviews": "Reviews",
    "account.tabs.shopAdmin": "Shop management",
    "account.tabs.settings": "Settings",
    "account.tabs.privacy": "Privacy",
    "account.languagePreference": "Language preference",
    "account.languageSaved": "Saved for future visits.",
    "account.settings.title": "My details",
    "account.settings.desc": "Manage your personal preferences",
    "account.username": "Username",
    "account.save": "Save",
    "account.email": "Email address",
    "account.edit": "Edit",
    "account.send": "Send",
    "account.newEmail": "New email address",
    "account.confirmationNote": "You will receive a confirmation email on both your old and new address.",
    "account.homeAddress": "Home address (search by zip code or street)",
    "account.homeAddressPlaceholder": "Type your address or zip code...",
    "account.loading": "Loading...",
    "account.insiderOnly": "Only available for Insiders",
    "account.subscription": "Subscription",
    "account.currentStatus": "Current status: {tier}",
    "account.tier.free": "Free",
    "account.tier.insider": "Insider",
    "account.becomeInsider": "Become Insider",
    "account.manageSubscription": "Manage subscription",
    "account.ownerBadge": "Shop owner",
    "account.profileUpdated": "Profile photo updated",
    "account.profileUpdatedDesc": "Your new profile photo was saved successfully.",
    "account.uploadFailed": "Upload failed",
    "account.uploadFailedDesc": "Something went wrong during upload.",
    "account.addressSaved": "Address saved",
    "account.addressSavedDesc": "Your home address (including coordinates) was updated.",
    "account.myGems": "My gems",
    "account.manageGems": "Manage gems",
    "account.manageWishlist": "Manage wishlist",
    "account.noItemsFound": "No items found yet.",
    "account.goWishlistHint": "Go to your wishlist to mark items as found.",
    "account.favoriteShops": "Favorite thrift shops",
    "account.shopBucketlist": "Shop bucketlist",
    "account.myContributions": "My contributions",
    "account.favoriteRoutes": "Favorite routes",
    "account.routeBucketlist": "Route bucketlist",
    "account.myCreatedRoutes": "My created routes",
    "account.myAiScans": "My AI scans",
    "account.myTopRoutes": "Most driven routes",
    "account.noRoutesDriven": "You have not driven any routes yet.",
    "account.startRoute": "Start a route",
    "account.recentReviews": "Recent reviews",
    "account.noReviews": "No reviews written yet.",
    "account.unknownShop": "Unknown shop",
    "account.openPost": "View post",
    "account.viewResult": "View result",
    "account.foundOn": "Found on",
    "account.insiderActive": "You are an Insider!",
    "account.error.notLoggedIn": "Not logged in",
    "account.error.portalLoadFailed": "Could not load portal",
    "account.error.usernameEmpty": "Username cannot be empty",
    "account.error.updateUsernameFailed": "Could not update username",
    "account.usernameUpdated": "Username updated",
    "account.usernameUpdatedDesc": "Your new username has been saved.",
    "account.error.saveFailed": "Failed to save",
    "account.error.invalidEmail": "Invalid email address",
    "account.error.invalidEmailDesc": "Please enter a valid email address.",
    "account.emailConfirmationSent": "Confirmation email sent",
    "account.emailConfirmationSentDesc": "Check both your old and new email address to confirm the change.",
    "account.error.changeFailed": "Failed to change",
    "account.error.noActiveSession": "No active session found",
    "account.error.updateProfileFailed": "Could not update profile",
    "account.error.fetchDashboardFailed": "Could not fetch dashboard",
    "account.error.fetchMyRoutesFailed": "Could not fetch my routes",
    "account.error.notAuthenticated": "Not authenticated",
    "account.error.deleteRouteFailed": "Could not delete route",
    "account.routeDeleted": "Route deleted",
    "account.routeDeletedDesc": "Your route has been deleted successfully.",
    "account.error.deleteFailed": "Failed to delete",
    "account.emptyShopBucketlist": "You have no shops in your bucket list yet.",
    "account.emptyContributions": "You have not added any shops yet.",
    "account.emptyRouteBucketlist": "You have no routes in your bucket list yet.",
    "account.emptyCreatedRoutes": "You have not created any routes yet.",
    "account.analyzing": "Analyzing...",
    "account.emptyScans": "You have not made any scans yet.",
    "account.unnamedRoute": "Unnamed route",
    "account.routeDrivenCount": "{count}x driven",
    "account.insiderUpgradeText": "Upgrade to Insider for unlimited routes, Social Wishlist{restyleSuffix} and door-to-door navigation.",
    "account.deleteAccount": "Delete Account",
    "account.deleteAccountDesc": "Permanently delete your account and associated data.",
    "account.deleteWarningTitle": "Are you sure?",
    "account.deleteConfirmTitle": "Are you sure?",
    "account.deleteConfirmDesc": "A confirmation email will be sent to your email address. You must click the link in that email to finalize the deletion.",
    "account.deleteWarning": "Note: You must cancel active subscriptions via the Stripe portal first.",
    "account.deleteConfirm": "A confirmation email will be sent to your email address. You must click the link in that email to finalize the deletion.",
    "account.deleteWarningDesc": "Note: You must first cancel active subscriptions via the Stripe portal.",
    "account.deleteRequestSuccess": "Confirmation email sent. Please check your inbox (and spam) to complete the deletion.",
    "account.deleteError": "Error requesting deletion",
    "account.subscriptionActiveError": "You still have active subscriptions or are within a paid period. Please cancel them via 'Manage subscription' first.",
    "account.confirmDeleteTitle": "Confirm Deletion",
    "account.confirmDeleteDesc": "Click the button below to permanently delete your account. This action cannot be undone.",
    "account.confirmDeleteButton": "Permanently Delete Account",
    "account.confirmDeleteSuccess": "Your account has been deleted.",
    "account.confirmDeleteError": "Error confirming deletion. The link may have expired.",
    "account.invalidLink": "Invalid link",
    "account.invalidLinkDesc": "This link is not valid or has expired.",
    "account.deletedTitle": "Account Deleted",
    "account.deletedMessage": "Your account and associated data have been successfully deleted. Thank you for being part of the VintMap community.",
    "account.backToHome": "Back to home",
    "account.devDowngrade": "(Dev) Downgrade",
    "faq.q1": "What is VintMap?",
    "faq.a1": "VintMap is the platform for thrift and vintage enthusiasts. Discover the best thrift stores, plan routes, and share your finds with the community. For shop owners, it offers a stage to present their shop and unique selection.",
    "faq.q2": "Is VintMap free to use?",
    "faq.a2": "Yes, using VintMap is free for visitors. You can search, view routes, and find shops for free. For additional features like 'My Routes' (unlimited saving), we offer an Insider subscription.",
    "faq.q3": "Is there an app available?",
    "faq.a3": "VintMap is a Progressive Web App (PWA). This means you can 'install' the website via your browser on your phone as an app, without going through the App Store or Play Store. You'll also get an icon on your home screen.",
    "faq.q4": "Can I share my finds?",
    "faq.a4": "Yes, via 'Share your loot' you can share photos of your best purchases with the community. Other users can like and comment on these.",
    "faq.q5": "Are there limits to map usage?",
    "faq.a5": "Yes, to keep VintMap free and sustainable for everyone, we use a Fair Use policy for interactive parts. For Insiders, a generous limit of 20 interactive map views per day applies.",
    "loginModal.send": "Login",
    "notFound.title": "Page not found",
    "notFound.desc": "Oops! The page you are looking for doesn't seem to exist. The link might be outdated or the page has been moved.",
    "restyle.aiDisclaimer": "AI designs are for inspiration and may differ from reality.",
    "restyle.descriptionPlaceholder": "E.g.: Old wooden cabinet found in London, now in matte black with gold handles.",
    "restyle.errorDesc": "An error occurred while generating your restyle. Please try again.",
    "restyle.kiesGreep": "Choose handles...",
    "restyle.optimizingSubject": "Optimizing furniture...",
    "routeBuilder.calculateNow": "Calculate now",
    "routeBuilder.mapPreviewPending": "Loading map...",
    "routeBuilder.mapPreviewPendingDesc": "We are calculating the best route for you.",
    "routeBuilder.reversedWithAddressesDesc": "The route has been reversed, including your specified start and end addresses.",
    "scanner.marketItems": "Market References",
    "scanner.noMarketLinks": "No direct market links found for this specific item.",
    "faq.visitors.q3": "Why do I always navigate from my current location in Google Maps instead of my start address?",
    "faq.visitors.a3": "The starting address you provided on VintMap is purely for calculating the optimal route or order so you can view it as a preview. When you actually open the route in Google Maps (or another app), navigation starts from your current location by default, as this is how Google Maps works. Important: this does NOT mean VintMap can see your exact location unless you explicitly shared it. All location data for active navigation remains completely private with Google Maps on your own device.",
    "restyle.floor.dark_gray_tiles": "Dark gray tiles",
    "common.viewAll": "View all ({count})",
    "common.showLess": "Show less",
    "common.showAll": "Show all ({count})",
    "common.closed": "Closed",
    "common.error": "Error",
    "common.processing": "Processing...",
    "common.takePhoto": "Take photo",
    "common.fromGallery": "Choose from gallery",
    "common.delete": "Delete",
    "common.details": "Details",
    "common.view": "View",
    "common.edit": "Edit",
    "common.chooseFile": "Choose file",
    "common.back": "Back",
    "common.share": "Share",
    "common.cancel": "Cancel",
    "addShop.useCurrentLocation": "Use current location",
    "addShop.loginRequiredDesc": "Only logged-in members can add new shops to VintMap. Log in or create an account to help improve routes.",
    "addShop.intro": "Help the community by adding your favorite thrift shop to the map!",
    "addShop.searchTitle": "Search shop",
    "addShop.foundShops": "Found shops",
    "addShop.selectShopPrompt": "Select the shop you want to add.",
    "addShop.orViaGoogle": "Or via Google link",
    "addShop.googleMapsLink": "Google Maps link",
    "addShop.pasteLinkHint": "Paste the link for automatic recognition.",
    "addShop.cantFindPrompt": "Cannot find it?",
    "addShop.manualEntry": "Enter manually",
    "addShop.detailsTitle": "Shop details",
    "addShop.detailsDesc": "Check if the details are correct",
    "addShop.nameLabel": "Shop name *",
    "addShop.namePlaceholder": "e.g. The Thrift Corner",
    "addShop.addressLabel": "Address *",
    "addShop.addressPlaceholder": "Street and number",
    "addShop.cityLabel": "City *",
    "addShop.postalCodeLabel": "Postal code",
    "addShop.provinceLabel": "Province",
    "addShop.multiplePlaces": "Multiple places found",
    "addShop.multiplePlacesPrompt": "There are multiple places named \"{city}\". Choose the correct province:",
    "addShop.openingHoursTitle": "Opening hours",
    "addShop.openingHoursDesc": "When is the shop open?",
    "addShop.openingHoursNotesLabel": "Exceptions or notes",
    "addShop.openingHoursNotesPlaceholder": "e.g: Open by appointment only...",
    "addShop.featuresTitle": "Features and categories",
    "addShop.featuresDesc": "What can visitors find here?",
    "addShop.charity": "Charity",
    "addShop.horeca": "Cafe/Coffee",
    "addShop.cardPayment": "Card payment",
    "addShop.cashPayment": "Cash payment",
    "addShop.categories": "Categories",
    "addShop.photosTitle": "Add photos",
    "addShop.photosDesc": "Maximum 4 photos for a quick first impression",
    "addShop.facadeTitle": "Facade photo (required for best visibility)",
    "addShop.facadePreviewAlt": "Facade preview",
    "addShop.makeFacadePhoto": "Take facade photo",
    "addShop.facadeHint": "We show this photo first. Unclear facade photos go to manual review.",
    "addShop.sharePhotosTitle": "Share shop photos",
    "addShop.sharePhotosDesc": "Max 4 photos - Automatically optimized",
    "addShop.previewAlt": "Preview",
    "addShop.instantAddTitle": "Direct add possible",
    "addShop.instantAddDesc": "Because this shop is on Google Maps, we can add it directly to the overview.",
    "addShop.manualReviewTitle": "Pending review",
    "addShop.manualReviewDesc": "Shops we cannot verify directly are reviewed by our team first.",
    "addShop.shopExistsTitle": "Shop already exists",
    "addShop.shopExistsDesc": "\"{name}\" is already in our database.",
    "addShop.fetchDetailsFailed": "Could not fetch details.",
    "addShop.detailsFilledTitle": "Details filled!",
    "addShop.detailsFilledDesc": "Check the details and submit.",
    "addShop.fetchNearbyFailed": "Could not fetch nearby shops",
    "addShop.noNearbyTitle": "No shops found",
    "addShop.noNearbyDesc": "No thrift shops found nearby (radius 100m).",
    "addShop.locationErrorTitle": "Location error",
    "addShop.locationErrorDesc": "Allow location access to find nearby shops.",
    "addShop.notSupportedTitle": "Not supported",
    "addShop.notSupportedDesc": "Geolocation is not supported.",
    "addShop.linkNotResolved": "Could not find details for this link.",
    "addShop.shopFoundTitle": "Shop found!",
    "addShop.shopFoundDesc": "Details were filled automatically.",
    "addShop.searchErrorTitle": "Search error",
    "addShop.incompleteTitle": "Incomplete details",
    "addShop.incompleteDesc": "Name, address and city are required.",
    "addShop.facadeUploadFailed": "Facade photo upload failed.",
    "addShop.photoUploadFailed": "Photo upload failed.",
    "addShop.shopExistsOverview": "This shop already exists in our overview. You can view it directly.",
    "addShop.viewShop": "View shop",
    "addShop.saveShopFailed": "Failed to save shop.",
    "addShop.thanksTitle": "Thank you!",
    "addShop.directAddedDesc": "The shop was added directly.",
    "addShop.submittedReviewDesc": "The shop was submitted for review.",
    "addShop.submitErrorTitle": "Submit error",
    "claimShop.notFound": "Shop not found",
    "claimShop.loginRequiredDesc": "You must be logged in to claim a shop.",
    "claimShop.sessionExpired": "Session expired. Please log in again.",
    "claimShop.paymentLinkFailed": "Failed to create payment link",
    "claimShop.noPaymentLink": "No payment link received",
    "claimShop.title": "Claim your shop",
    "claimShop.aboutToClaim": "You are about to claim",
    "claimShop.aboutToClaimSuffix": ".",
    "claimShop.confirmDetails": "Confirm your shop details",
    "claimShop.addressNotice": "Note: we send the verification letter and sticker to this address.",
    "claimShop.featureLetterTitle": "Verification letter and sticker",
    "claimShop.featureLetterDesc": "Receive a physical letter with a unique code and a VintMap window sticker.",
    "claimShop.featureOwnerTitle": "Owner profile",
    "claimShop.featureOwnerDesc": "Get access to your admin area to update photos and opening hours.",
    "claimShop.oneTimeCost": "One-time fee",
    "claimShop.goToPayment": "Go to payment",
    "claimShop.stripeNotice": "Secure payment via Stripe. Your claim is processed after successful payment.",
    "ownersKringloop.howTitle": "How does it work?",
    "ownersKringloop.how.step1.title": "Find your shop",
    "ownersKringloop.how.step1.desc": "Find your shop in our database with hundreds of thrift shops.",
    "ownersKringloop.how.step2.title": "Claim your profile",
    "ownersKringloop.how.step2.desc": "Click \"Claim this shop\" and verify that you are the owner.",
    "ownersKringloop.how.step3.title": "Manage your shop",
    "ownersKringloop.how.step3.desc": "Add photos, update opening hours, and reach more customers.",
    "ownersKringloop.badge": "For thrift shops",
    "ownersKringloop.title": "Put your thrift shop in the spotlight",
    "ownersKringloop.subtitle": "Claim your shop and reach thousands of thrift lovers. Be visible in routes and search results, and build your online community.",
    "ownersKringloop.findShop": "Find your shop",
    "ownersKringloop.everythingTitle": "Everything you need",
    "ownersKringloop.free.title": "Free basic profile",
    "ownersKringloop.free.desc": "Every thrift shop in the Netherlands automatically gets a free profile on VintMap.",
    "ownersKringloop.free.feature1": "Basic listing with name and address",
    "ownersKringloop.free.feature2": "Visible in routes and search results",
    "ownersKringloop.free.feature3": "Receive reviews and ratings",
    "ownersKringloop.free.feature4": "Community posts from visitors",
    "ownersKringloop.premium.title": "Premium features",
    "ownersKringloop.premium.badge.title": "Verified badge",
    "ownersKringloop.premium.badge.desc": "Show visitors your shop is authentic with an official badge.",
    "ownersKringloop.premium.photos.title": "Own photos",
    "ownersKringloop.premium.photos.desc": "Upload photos of your shop, products, and interior.",
    "ownersKringloop.premium.hours.title": "Manage opening hours",
    "ownersKringloop.premium.hours.desc": "Keep opening hours up to date so visitors know when you are open.",
    "ownersKringloop.premium.promotions.title": "Post promotions",
    "ownersKringloop.premium.promotions.desc": "Share offers and promotions to attract more visitors.",
    "ownersKringloop.premium.website.title": "Website link",
    "ownersKringloop.premium.website.desc": "Add a link to your website or social media.",
    "ownersKringloop.premium.posting.title": "Post as shop",
    "ownersKringloop.premium.posting.desc": "Create posts with your shop name and owner badge.",
    "ownersKringloop.premiumAccessTitle": "Premium access",
    "ownersKringloop.oneTimeInvestment": "One-time investment",
    "ownersKringloop.oneTime": "one-time",
    "ownersKringloop.includesSticker": "Includes physical verification letter and official window sticker",
    "ownersKringloop.whatYouGet": "What you get:",
    "ownersKringloop.andMore": "And much more...",
    "ownersKringloop.claimShop": "Claim your shop",
    "ownersKringloop.noMonthlyCosts": "No monthly fees. Lifetime access.",
    "ownersSelection.title": "For owners",
    "ownersSelection.subtitle": "Get more visibility and manage your profile on VintMap.",
    "ownersSelection.cardTitle": "Thrift shop",
    "ownersSelection.cardDesc": "Claim your thrift shop, update opening hours, and become part of the most popular routes.",
    "ownersSelection.cta": "I am a shop owner",
    "restyleHistory.loginRequiredDesc": "Log in to view your restyle projects.",
    "restyleHistory.toLogin": "Go to login",
    "restyleHistory.backToRestyle": "Back to Restyle",
    "restyleHistory.title": "My Restyle projects",
    "restyleHistory.subtitle": "Overview of your restyles, with before and after side by side.",
    "restyleHistory.filterTitle": "Filter by month and year",
    "restyleHistory.year": "Year",
    "restyleHistory.month": "Month",
    "restyleHistory.empty": "No restyles found for this period.",
    "restyleHistory.openResult": "Open result",
    "restyleHistory.before": "Original",
    "restyleHistory.after": "After",
    "restyleHistory.beforeImageAlt": "Before photo",
    "restyleHistory.afterImageAlt": "Restyle result",
    "routeBuilder.saveSuccessTitle": "Success!",
    "routeBuilder.saveSuccessDesc": "Your route has been saved successfully.",
    "routeBuilder.saveErrorTitle": "Save error",
    "routeBuilder.tooFewShopsTitle": "Too few shops",
    "routeBuilder.tooFewShopsDesc": "A public route must contain at least 4 shops.",
    "routeBuilder.noHomeTitle": "Home address missing",
    "routeBuilder.noHomeDesc": "Set your home address in account settings to use this feature.",
    "routeBuilder.personalizingTitle": "Personalizing route...",
    "routeBuilder.personalizingDesc": "Your home address is set and we are optimizing the route.",
    "routeBuilder.homeSetTitle": "Home address set",
    "routeBuilder.homeSetDesc": "Your home address was automatically set as start and end address.",
    "routeBuilder.loginTitle": "Create an account",
    "routeBuilder.loginDesc": "You must be logged in to create routes. Create a free account to get started.",
    "routeBuilder.toAccountPage": "Go to account page",
    "routeBuilder.reversedTitle": "Route reversed",
    "routeBuilder.reversedDesc": "The order of shops has been reversed.",
    "routeBuilder.clearConfirm": "Are you sure you want to clear this route? All shops and addresses will be removed.",
    "routeBuilder.clearedTitle": "Route cleared",
    "routeBuilder.clearedDesc": "You can now start from a clean slate.",
    "routeBuilder.tooFewStopsTitle": "Too few stops",
    "routeBuilder.tooFewStopsDesc": "Add at least 3 shops to optimize.",
    "routeBuilder.dailyLimitTitle": "Daily limit reached",
    "routeBuilder.dailyLimitDesc": "You reached the daily limit of {limit} optimizations. Try again tomorrow.",
    "routeBuilder.monthlyLimitTitle": "Monthly limit reached",
    "routeBuilder.monthlyLimitDesc": "You reached the monthly limit of {limit} optimizations. Try again next month.",
    "routeBuilder.alreadyOptimalTitle": "Already optimal",
    "routeBuilder.alreadyOptimalDesc": "Not enough intermediate stops to optimize.",
    "routeBuilder.optimizedTitle": "Route optimized",
    "routeBuilder.optimizedDesc": "New distance: {distance}",
    "routeBuilder.optimizeFailedDesc": "Could not optimize route.",
    "routeBuilder.backToRoutes": "Back to routes",
    "routeBuilder.editRoute": "Edit route",
    "routeBuilder.newRoute": "Create new route",
    "routeBuilder.mapTitle": "Route map",
    "routeBuilder.openMapsRoute": "Open Maps route",
    "routeBuilder.selectedStops": "Selected stops",
    "routeBuilder.noStopsYet": "No shops added to your route yet.",
    "routeBuilder.searchShops": "Search shops",
    "routeBuilder.day": "Day",
    "routeBuilder.addShop": "Add thrift shop",
    "routeBuilder.openGoogleMaps": "Open Google Maps",
    "routeBuilder.recalculatePrompt": "You changed your route. Click recalculate to refresh distance and route.",
    "routeBuilder.recalculate": "Recalculate",
    "routeBuilder.detailsTitle": "Route details",
    "routeBuilder.nameLabel": "Route name",
    "routeBuilder.namePlaceholder": "Give your route a name...",
    "routeBuilder.distance": "Distance",
    "routeBuilder.travelTime": "Travel time",
    "routeBuilder.descriptionOptional": "Description (optional)",
    "routeBuilder.descriptionPlaceholder": "What makes this route fun?",
    "routeBuilder.reverse": "Reverse",
    "routeBuilder.clearShops": "Remove all shops",
    "routeBuilder.clearShopsConfirm": "Are you sure you want to remove all shops from your route?",
    "routeBuilder.personalizeBtn": "Personalize this route",
    "routeBuilder.personalizeDesc": "Adjust the order, add or remove shops.",
    "routeBuilder.personalizeUpgradeTitle": "Insider feature",
    "routeBuilder.personalizeUpgradeDesc": "Personalizing routes is only available for Insiders and Power Users. Upgrade to build your own routes!",
    "routeBuilder.saving": "Saving...",
    "routeBuilder.save": "Save",
    "routeBuilder.sharePublic": "Share publicly",
    "routeBuilder.startEndTitle": "Start and end point",
    "routeBuilder.startAddressHelp": "This start address is strictly for calculation. Once opened, Google Maps always navigates from your current location. See FAQ.",
    "routeBuilder.startAddressOptional": "Start address (optional)",
    "routeBuilder.endAddressOptional": "End address (optional)",
    "routeBuilder.autoOptimize": "Auto optimization",
    "routeBuilder.useHomeOptimize": "Use home address and optimize",
    "routeBuilder.optimizeOrder": "Optimize order",
    "routeBuilder.recalculateInfo": "Recalculate info",
    "routeBuilder.mergePrompt.title": "Merge with current selection?",
    "routeBuilder.mergePrompt.description": "You currently have {count} shop(s) selected in your temporary list. Do you want to add these to the selected route \"{name}\"?",
    "routeBuilder.mergePrompt.yes": "Yes, add to this route",
    "routeBuilder.mergePrompt.no": "No, use saved route only",
    "restyle.popupBlockedTitle": "Popup blocked",
    "restyle.popupBlockedDesc": "Allow popups to open fullscreen.",
    "restyle.openImageFailedTitle": "Could not open image",
    "restyle.tryAgain": "Please try again.",
    "restyle.noImageTitle": "No image",
    "restyle.noImageDesc": "Upload a photo of your furniture first.",
    "restyle.serverError": "Server error",
    "restyle.invalidResponse": "Restyle response is invalid. Please try again.",
    "restyle.completedTitle": "Restyle completed!",
    "restyle.completedDesc": "Your new design is ready.",
    "restyle.generateErrorTitle": "Generation error",
    "restyle.loginRequiredDesc": "Log in to use VintMap Restyle.",
    "restyle.toLogin": "Go to login",
    "restyle.insiderExclusive": "Insider exclusive",
    "restyle.insiderDesc1": "VintMap Restyle is exclusive to Insiders.",
    "restyle.insiderDesc2": "Upcycle your thrift gems with AI power!",
    "restyle.feature1": "✓ 25 AI designs per month",
    "restyle.feature2": "✓ Photorealistic 1024px",
    "restyle.feature3": "✓ Smart paint-to-match option",
    "restyle.feature4": "✓ Instant result",
    "restyle.becomeInsider": "Become Insider (EUR2.99/m)",
    "restyle.title": "Thrift Upcycle Studio",
    "restyle.shareTitle": "Check out my Restyle!",
    "restyle.shareText": "Watch how I transformed this piece of furniture with VintMap AI!",
    "spaceblend.title": "SpaceBlend AI",
    "spaceblend.subtitle": "Visualize your find directly in your interior.",
    "spaceblend.start": "Upload",
    "spaceblend.chooseInterior": "Choose your interior",
    "spaceblend.uploadInterior": "Upload new room photo",
    "spaceblend.uploadInteriorDesc": "Take a photo of where the furniture should go.",
    "spaceblend.myInteriors": "My Interiors",
    "spaceblend.recentRestyles": "YOUR TREASURES",
    "spaceblend.noTreasures": "No treasures yet to blend.",
    "spaceblend.placeInInterior": "Place in my interior",
    "spaceblend.generating": "SpaceBlend is working...",
    "spaceblend.preparing": "Preparing furniture...",
    "spaceblend.editor.dragTip": "Drag the furniture to the right spot.",
    "spaceblend.editor.pinchTip": "Use two fingers to scale.",
    "spaceblend.editor.generate": "Generate Blend",
    "spaceblend.archive.title": "My Blends",
    "spaceblend.archive.link": "Archive",
    "spaceblend.archive.subtitle": "Your complete history of SpaceBlend projects.",
    "spaceblend.archive.emptyTitle": "No blends yet",
    "spaceblend.archive.emptyDesc": "Start your first SpaceBlend project to see your results here.",
    "spaceblend.archive.aiBadge": "AI Blend",
    "spaceblend.defaultInteriorName": "My interior",
    "spaceblend.uploadSuccessTitle": "Success!",
    "spaceblend.uploadSuccessDesc": "Interior added.",
    "spaceblend.uploadErrorTitle": "Upload failed",
    "spaceblend.uploadErrorDesc": "Something went wrong.",
    "spaceblend.editor.generateSuccessTitle": "Success!",
    "spaceblend.editor.generateSuccessDesc": "Your blend has been generated.",
    "spaceblend.editor.generateErrorTitle": "Error",
    "spaceblend.editor.generateErrorDesc": "Generation failed.",
    "spaceblend.editor.rmbgErrorTitle": "Error",
    "spaceblend.editor.rmbgErrorDesc": "Background removal failed.",
    "spaceblend.editor.back": "Back",
    "spaceblend.editor.generatingDetails": "VintMap AI combines your furniture with your interior. Matching lighting and shadows...",
    "spaceblend.editor.noRestyles": "No restyled items found yet.",
    "spaceblend.project.notFound": "Project not found",
    "spaceblend.project.backToSpaceblend": "Back to SpaceBlend",
    "spaceblend.project.back": "Back",
    "spaceblend.project.share": "Share",
    "spaceblend.project.download": "Download",
    "spaceblend.project.downloadFailed": "Download failed",
    "spaceblend.project.interior": "Interior",
    "spaceblend.project.composition": "Composition",
    "spaceblend.project.title": "SpaceBlend Project",
    "spaceblend.project.resultTitle": "RESULT",
    "spaceblend.project.defaultDesc": "New furniture placed in interior with SpaceBlend AI.",
    "spaceblend.project.report": "Report",
    "spaceblend.project.createAnother": "Make another blend",
    "spaceblend.project.shareTitle": "Check out my SpaceBlend!",
    "spaceblend.project.shareText": "I virtually placed this furniture in my home with VintMap SpaceBlend AI!",
    "spaceblend.usage": "{used} / {limit} blends this month",
    "spaceblend.limitReached": "Monthly limit reached",
    "spaceblend.noInteriors": "Upload a photo of your room first.",
    "spaceblend.maxInteriors": "Maximum number of interiors reached (20).",
    "restyle.subtitle": "Visualize your new project before you start sanding.",
    "restyle.tipTitle": "Tip!",
    "restyle.tipText": "For the best results, ensure a front-view of your furniture with good lighting.",
    "restyle.disclaimer": "AI does its best, but sometimes paints outside the lines. Use it as inspiration!",
    "restyle.verticalTip": "Vertical (2:3 ratio) photos work best for furniture.",
    "restyle.optimizing": "AI Optimization...",
    "restyle.preparing": "Just a moment, we are preparing your furniture for the restyler...",
    "restyle.fullscreen": "Full screen (before/after)",
    "restyle.yourDesign": "Your design",
    "restyle.furniturePhoto": "Photo of your furniture",
    "restyle.startRestyle": "Start your Restyle",
    "restyle.takeOrChoosePhoto": "Take a photo of your furniture or choose one from your gallery (Vertical 2:3 is best).",
    "restyle.removingBackground": "Removing background...",
    "restyle.localProcessing": "This happens locally on your device",
    "restyle.camera": "Upload",
    "restyle.previewAlt": "Preview",
    "restyle.clear": "Clear",
    "restyle.furnitureColor": "Furniture color*",
    "restyle.finishStyle": "Finish style*",
    "restyle.chooseFinish": "Choose finish",
    "restyle.finish.mat": "Matte",
    "restyle.finish.satin": "Satin",
    "restyle.finish.gloss": "Gloss",
    "restyle.finish.highGloss": "High gloss",
    "restyle.finish.chalkPaint": "Chalk paint",
    "restyle.finish.woodVisible": "Visible wood grain",
    "restyle.finish.distressed": "Distressed (Vintage)",
    "restyle.fabric.velvet": "Velvet",
    "restyle.fabric.linen": "Linen",
    "restyle.fabric.leather": "Leather",
    "restyle.fabric.chenille": "Chenille",
    "restyle.fabric.microsuede": "Microsuede",
    "restyle.floor.oak_boards": "Oak Floorboards",
    "restyle.floor.herringbone_oak": "Herringbone Parquet",
    "restyle.floor.terracotta_tiles": "Terracotta Tiles",
    "restyle.floor.travertine_tiles": "Travertine (Natural Stone)",
    "restyle.floor.concrete_floor": "Concrete Floor",
    "restyle.floor.dark_oak_boards": "Dark Oak Boards",
    "restyle.floor.carrara_marble": "Carrara Marble",
    "restyle.handleColor": "Handle color*",
    "restyle.chooseColor": "Choose color",
    "restyle.handleColorOption.keepCurrent": "Keep current",
    "restyle.handleColorOption.gold": "Gold",
    "restyle.handleColorOption.black": "Black",
    "restyle.handleColorOption.bronze": "Bronze",
    "restyle.handleColorOption.rustCopper": "Rust copper",
    "restyle.handleColorOption.chrome": "Chrome",
    "restyle.handleColorOption.matteBlack": "Matte black",
    "restyle.handleColorOption.white": "White",
    "restyle.handleColorOption.matchFurniture": "Paint to match furniture",
    "restyle.handleStyle": "Handle style*",
    "restyle.chooseStyle": "Choose style",
    "restyle.handleStyleOption.keepCurrent": "Keep current",
    "restyle.handleStyleOption.vintage": "Vintage handles",
    "restyle.handleStyleOption.modern": "Modern knobs",
    "restyle.handleStyleOption.leatherLoops": "Leather loops",
    "restyle.handleStyleOption.leatherHandles": "Leather handles",
    "restyle.handleStyleOption.halfMoon": "Half moon pulls",
    "restyle.handleStyleOption.bar": "Bar pulls",
    "restyle.handleStyleOption.wooden": "Wooden knobs",
    "restyle.handleStyleOption.porcelain": "Porcelain knobs",
    "restyle.handleStyleOption.cup": "Cup pulls",
    "restyle.handleStyleOption.industrial": "Industrial pulls",
    "restyle.typeToggle": "Furniture type",
    "restyle.hout": "Wood / Hard material",
    "restyle.bekleed": "Upholstered (Fabric/Leather)",
    "restyle.houtwerk_behouden": "Preserve woodwork",
    "restyle.houtwerk_verwijderen": "Paint woodwork too",
    "restyle.meubel_kleur": "Furniture color",
    "restyle.meubel_kleur1": "Main Color (dominant)",
    "restyle.meubel_kleur2": "Color 2 (secondary)",
    "restyle.meubel_kleur3": "Color 3 (accent)",
    "restyle.achterwand_kleur": "Back wall color",
    "restyle.greepjes": "Handles",
    "restyle.niet_toepasselijk": "Not applicable",
    "restyle.houtwerk_behandeling": "Woodwork treatment",
    "restyle.behandeling.behouden": "Preserve (Original)",
    "restyle.behandeling.beitsen": "Stain (Darker)",
    "restyle.behandeling.logen": "Strip/Sand (Lighter)",
    "restyle.behandeling.schilderen": "Paint (In color)",
    "restyle.stoffering_kleur": "Upholstery color",
    "restyle.stof_patroon": "Fabric pattern",
    "restyle.hasGlass": "Contains glass",
    "restyle.hasMirror": "Contains mirror",
    "restyle.hasWebbing": "Contains webbing",
    "restyle.print.effen": "Solid",
    "restyle.print.bloemen": "Floral",
    "restyle.print.strepen": "Stripes",
    "restyle.print.kleine_bloemetjes": "Small Flowers",
    "restyle.print.tartan": "Tartan",
    "restyle.print.ruit": "Checkered",
    "restyle.print.toile_de_jouy": "Toile de Jouy",
    "restyle.print.custom": "Use my own pattern",
    "restyle.stof_type": "Fabric type",
    "restyle.afwerking": "Finish",
    "restyle.vloer": "Floor type",
    "restyle.genereren": "Generate new design",
    "restyle.resultaat": "End result",
    "restyle.before": "Original",
    "restyle.after": "After",
    "restyle.upload": "Upload photo",
    "restyle.handleStyleOption.apothecary": "Apothecary pulls",
    "restyle.handleStyleOption.ring": "Ring pulls",
    "restyle.handleStyleOption.edge": "Minimal edge pulls",
    "restyle.handleStyleOption.none": "No handles (push-to-open)",
    "restyle.backwallColor": "Back wall color*",
    "restyle.default.furnitureColor": "Moss green",
    "restyle.default.backwall": "White stucco",
    "restyle.outputRatio": "Output ratio*",
    "restyle.chooseRatio": "Choose ratio",
    "restyle.ratio.portrait": "Portrait (1024x1536)",
    "restyle.ratio.square": "Square (1024x1024)",
    "restyle.ratio.landscape": "Landscape (1536x1024)",
    "restyle.generating": "AI Studio is working...",
    "restyle.color.bordeaux": "Deep Burgundy",
    "restyle.color.warm_terracotta": "Warm Terracotta",
    "restyle.color.coral": "Coral",
    "restyle.color.dusty_rose": "Dusty Rose",
    "restyle.color.ochre": "Ochre",
    "restyle.color.honey_yellow": "Honey Yellow",
    "restyle.color.egg_yellow": "Egg Yellow",
    "restyle.color.olive_green": "Olive Green",
    "restyle.color.moss_green": "Moss Green",
    "restyle.color.sage_green": "Sage Green",
    "restyle.color.pine_green": "Pine Green",
    "restyle.color.teal": "Peacock Blue (Teal)",
    "restyle.color.sky_blue": "Sky Blue",
    "restyle.color.royal_blue": "Royal Blue",
    "restyle.color.lavender": "Lavender",
    "restyle.color.aubergine": "Aubergine",
    "restyle.color.linen": "Linen White",
    "restyle.color.antique_white": "Antique White",

    "restyle.color.sand_beige": "Sand Beige",
    "restyle.color.taupe": "Taupe",
    "restyle.color.slate_gray": "Slate Gray",
    "restyle.color.charcoal": "Charcoal",
    "restyle.color.jet_black": "Jet Black",
    "restyle.webbing": "Webbing",
    "restyle.webbing.none": "No webbing",
    "restyle.webbing.natural": "Natural (rattan)",
    "restyle.webbing.painted": "Paint webbing along",
    "restyle.glass.transparent": "Keep transparent",
    "restyle.glass.painted": "Paint along",
    "restyle.glass.webbing": "Replace with webbing",
    "restyle.mirror.transparent": "Keep reflective",
    "restyle.mirror.painted": "Paint along",
    "restyle.mirror.webbing": "Replace with webbing",
    "restyle.woodworkColor": "Woodwork color",
    "restyle.backofficeTranslation": "Backoffice translation",
    "restyle.behandeling.bladgoud": "Gold Leaf (Gilding)",
    "restyle.behandeling.bladzilver": "Silver Leaf (Silvering)",
    "restyle.zoom_title": "Zoom view",


    "restyle.generateConcept": "Generate Restyle concept",
    "restyle.creditInfo": "Costs 1 credit - 25/month as Insider",
    "restyle.aiResult": "AI result",
    "restyle.analyzing": "Analyzing...",
    "restyle.analyzingDesc": "Our Expert AI analyzes your furniture and generates a unique photorealistic visualization of your choices.",
    "restyle.aiResultAlt": "AI result",
    "restyle.openFullscreen": "Open fullscreen",
    "restyle.placeholderTitle": "Your AI Restyle concept appears here",
    "restyle.placeholderDesc": "Upload a photo and choose your colors",
    "restyle.archiveTitle": "My Restyle archive",
    "restyle.archiveDesc": "Find your restyles by month or year (including before photo).",
    "restyle.openSeparatePage": "Open separate page",
    "routeDetail.addedFavorite": "Added to favorites",
    "routeDetail.removedFavorite": "Removed from favorites",
    "routeDetail.addedBucketlist": "Added to bucket list",
    "routeDetail.removedBucketlist": "Removed from bucket list",
    "routeDetail.errorTitle": "Something went wrong",
    "routeDetail.loading": "Loading...",
    "routeDetail.notFoundTitle": "Route not found",
    "routeDetail.notFoundDesc": "This route does not exist or was removed.",
    "routeDetail.onBucketlist": "{count} on bucket list",
    "routeDetail.stopsCount": "{count} stops",
    "routeDetail.openInMaps": "Open in Google Maps",
    "routeDetail.share": "Share",
    "routeDetail.copyAsOwn": "Copy as my route",
    "routeDetail.editRoute": "Edit route",
    "routeDetail.stopsHeading": "Stops ({count})",
    "routeDetail.summary": "Route summary",
    "routeDetail.numberOfStops": "Number of stops",
    "routeDetail.totalDistance": "Total distance",
    "routeDetail.provinces": "Province(s)",
    "routeDetail.cities": "City/Cities",
    "routeDetail.unknown": "Unknown",
    "routeDetail.saved": "Saved",
    "routeDetail.shopStatus": "Shop status",
    "routeDetail.dayShort.mon": "Mon",
    "routeDetail.dayShort.tue": "Tue",
    "routeDetail.dayShort.wed": "Wed",
    "routeDetail.dayShort.thu": "Thu",
    "routeDetail.dayShort.fri": "Fri",
    "routeDetail.dayShort.sat": "Sat",
    "routeDetail.dayShort.sun": "Sun",
    "routeDetail.closedShort": "Closed",
    "routeDetail.personalize": "Personalize route",
    "routeDetail.insiderOnly": "Insider members only",
    "routeDetail.routeFinds": "Finds from this route",
    "routeDetail.shareTitlePrefix": "Thrift route",
    "routeDetail.shareTextIntro": "Check out this thrift route on VintMap!",
    "routeDetail.distanceUnknown": "Distance unknown",
    "routeList.deleteFailed": "Delete failed",
    "routeList.deletedTitle": "Route deleted",
    "routeList.deletedDesc": "The route was removed successfully from your overview.",
    "routeList.deleteErrorTitle": "Delete error",
    "routeList.deleteErrorDesc": "An error occurred while deleting the route.",
    "routeList.foundCount": "{count} routes found",
    "routeList.searchPlaceholder": "Search routes...",
    "routeList.allCities": "All cities",
    "routeList.sort.new": "Newest",
    "routeList.sort.mostSaved": "Most saved",
    "routeList.sort.mostShops": "Most shops",
    "routeList.sort.fewestShops": "Fewest shops",
    "routeList.sort.longest": "Longest route",
    "routeList.sort.shortest": "Shortest route",
    "routeList.noRoutesFound": "No routes found",
    "routeList.tryOtherFilters": "Try different search terms or filters.",
    "routeList.inYourOverview": "{count} routes in your overview",
    "routeList.noMyRoutes": "You have not created any routes yet.",
    "routeList.makeFirstRoute": "Create your first route",
    "routeList.searchTitle": "Search routes",
    "routeList.resultsFor": "{count} results for \"{search}\"",
    "routeList.noResultsFor": "No results for \"{search}\". Try different search terms.",
    "routeList.enterSearch": "Enter a search term to find routes.",
    "routeList.clearSearchTerms": "Clear search terms",
    "shopReviews.backToShop": "Back to shop",
    "shopReviews.title": "Reviews",
    "shopReviews.forShop": "for {name}",
    "shopReviews.userFallback": "User",
    "shopReviews.atmosphere": "Atmosphere",
    "shopReviews.service": "Service",
    "shopReviews.price": "Price",
    "shopReviews.empty": "There are no reviews for this shop yet.",
    "shopDetail.notFoundTitle": "Shop not found",
    "shopDetail.notFoundDesc": "This shop does not exist or was removed.",
    "shopDetail.backToShops": "Back to shops",
    "shopDetail.backToThriftShops": "Back to thrift shops",
    "shopDetail.uploadGem": "Upload",
    "shopDetail.camera": "Camera",
    "shopDetail.addToExistingOrNewRoute": "Do you want to add this thrift shop to your route, or start a new one?",
    "shopDetail.addToExistingRoute": "Add to route",
    "shopDetail.startNewRoute": "Start new route",
    "shopDetail.temporarilyClosed": "This shop is temporarily closed",
    "shopDetail.reopenExpectedOn": "Reopening expected on:",
    "shopDetail.permanentlyClosed": "This shop is marked as permanently closed.",
    "shopDetail.permanentlyClosedDesc": "It is hidden for visitors. If the shop reopens, you can reactivate it here.",
    "shopDetail.reopenShop": "Reopen shop",
    "shopDetail.adminPanel": "Admin panel",
    "shopDetail.adminPanelDesc": "You are the owner of this shop on VintMap.",
    "shopDetail.thriftProfileDashboard": "Thrift profile dashboard",
    "shopDetail.editShop": "Edit shop",
    "shopDetail.verificationPending": "Verification pending",
    "shopDetail.verificationPendingDesc": "The letter with your verification code is on its way to",
    "shopDetail.enterCodeUnlock": "Enter the code below to unlock all features.",
    "shopDetail.verificationCode": "Verification code",
    "shopDetail.verificationCodePlaceholder": "E.g. VINT-1234",
    "shopDetail.testTip": "Test tip",
    "shopDetail.yourCodeIs": "Your code is",
    "shopDetail.verifyError": "Verification failed",
    "shopDetail.congrats": "Congratulations!",
    "shopDetail.shopFullyVerified": "Your shop is now fully verified.",
    "shopDetail.verifyCode": "Verify code",
    "shopDetail.shopPremiumVisible": "Your shop is fully verified and visible as premium.",
    "shopDetail.areYouOwner": "Are you the owner?",
    "shopDetail.changeAddress": "Change address?",
    "shopDetail.improveInfo": "Improve information",
    "shopDetail.reportProblem": "Report problem?",
    "shopDetail.like": "Like",
    "shopDetail.bucketlist": "Bucketlist",
    "shopDetail.review": "Review",
    "shopDetail.toVisit": "On bucketlist",
    "shopDetail.toVisitAlt": "Like to visit",
    "shopDetail.writeReview": "Write review",
    "shopDetail.route": "Route",
    "shopDetail.shopPhotos": "Shop photos",
    "shopDetail.shopInfo": "Shop information",
    "shopDetail.aboutShop": "About the shop",
    "shopDetail.address": "Address",
    "shopDetail.phone": "Phone number",
    "shopDetail.openingHours": "Opening hours",
    "shopDetail.noOpeningHoursKnown": "No opening hours known",
    "shopDetail.paymentOptions": "Payment options",
    "shopDetail.noSpecificCategories": "No specific categories known",
    "shopDetail.shareWithFollowers": "What do you want to share with your followers, {name}?",
    "shopDetail.photoVideo": "Photo/Video",
    "shopDetail.photo": "Photo",
    "shopDetail.postUpdate": "Post update",
    "shopDetail.update": "Update",
    "shopDetail.shopUpdates": "Shop updates",
    "shopDetail.foundGems": "Found gems",
    "shopDetail.shareGem": "Share gem",
    "shopDetail.noGemsShared": "No gems have been shared for this shop yet.",
    "shopDetail.shareFirstGem": "Share the first gem!",
    "shopDetail.inRoutes": "In routes",
    "shopDetail.notInRouteYet": "This shop is not in a route yet.",
    "shopDetail.reviews": "Reviews",
    "shopDetail.viewAllReviews": "View all reviews",
    "shopDetail.reviewCount": "{count} reviews",
    "shopDetail.noReviewsPosted": "No reviews posted yet.",
    "shopDetail.shopClaimed": "Shop claimed!",
    "shopDetail.claimCongrats": "Congratulations! You have successfully claimed",
    "shopDetail.claimSuccessSuffix": ".",
    "shopDetail.whatNow": "What now?",
    "shopDetail.whatNow1": "We will send your verification letter and sticker within 2 business days.",
    "shopDetail.whatNow2": "You can now directly update photos and opening hours via the \"Edit\" button.",
    "shopDetail.whatNow3": "You will soon receive an email with extra tips for your profile.",
    "shopDetail.getStarted": "Get started",
    "shopDetail.sharePrefix": "Check out",
    "shopDetail.shareSuffix": "on VintMap!",
    "shopDetail.reviewsLabel": "reviews",
    "wishlist.title": "Social wishlist",
    "wishlist.subtitle": "Manage your own hunt and help others find their gems.",
    "wishlist.mineTab": "My wishlist",
    "wishlist.friendsTab": "Friends wishlists",
    "wishlist.mySearchTitle": "My hunt",
    "wishlist.mySearchDesc": "Add items you are searching for. Check them off once found!",
    "wishlist.private": "Private",
    "wishlist.privateInfo": "When you share your wishlist, items marked as 'private' will not be visible to others.",
    "wishlist.backToSearchResults": "Back to search results",
    "wishlist.helpSearch": "Help {name} search!",
    "wishlist.alreadyFound": "Already found!",
    "wishlist.iFoundThis": "I found this!",
    "wishlist.userNoItems": "{name} has no items on their wishlist yet.",
    "wishlist.notFound": "Wishlist not found.",
    "wishlist.goToOwn": "Go to your own wishlist",
    "wishlist.loginToFollow": "Log in to follow this list and help search.",
    "wishlist.searchFriendsTitle": "Search friends",
    "wishlist.searchFriendsDesc": "Find other vintage lovers and view their wishlists.",
    "wishlist.searchByUsername": "Search by username...",
    "wishlist.noUsersFound": "No users found.",
    "wishlist.inviteFriends": "Invite friends to help search!",
    "wishlist.inviteDesc": "Share your wishlist via WhatsApp or email",
    "wishlist.shareViaWhatsApp": "Share via WhatsApp",
    "wishlist.shareViaEmail": "Share via email",
    "wishlist.copyLink": "Copy link",
    "wishlist.linkCopied": "Link copied!",
    "wishlist.followingTab": "Following",
    "wishlist.followers": "{count} follower(s)",
    "wishlist.follow": "Follow",
    "wishlist.unfollow": "Unfollow",
    "wishlist.unfollowConfirmTitle": "Unfollow",
    "wishlist.unfollowConfirmDesc": "Are you sure you want to unfollow {name}?",
    "wishlist.followingEmpty": "You're not following anyone yet. Invite friends via your personal link!",
    "wishlist.view": "View",
    "wishlist.viewList": "View list",
    "wishlist.foundSection": "Found ({count})",
    "wishlist.foundSectionEmpty": "Nothing found yet – good luck searching!",
    "wishlist.toFindSection": "Search list ({count})",
    "wishlist.pagePrev": "Previous",
    "wishlist.pageNext": "Next",
    "wishlist.pageLabel": "Page {page} of {total}",
    "wishlist.sendWhatsApp": "Send via WhatsApp",
    "wishlist.foundForFriend": "Hey {name}, I found the {item} for you!",
    "wishlist.sharedTitle": "{name}'s Wishlist",
    "wishlist.shareMessage": "Check out my thrift wishlist! {url}",
    "wishlist.imageOptional": "Photo (optional)",
    "wishlist.dimensions": "Dimensions",
    "wishlist.dimensionsPlaceholder": "e.g. 80×40×120 cm",
    "wishlist.guidePrice": "Guide price",
    "wishlist.guidePricePlaceholder": "e.g. 25",
    "wishlist.notes": "Notes",
    "wishlist.notesPlaceholder": "Extra info or remarks",
    "wishlist.addItem": "Add item",
    "wishlist.loginRequired": "My Wishlist",
    "wishlist.loginRequiredDesc": "Log in to create your own wishlist, share it with friends, and mark items as 'found'.",
    "emailConfirmed.title": "Email confirmed!",
    "emailConfirmed.desc": "Thank you for confirming your email address. You can now log in to your account.",
    "emailConfirmed.loginTitle": "Log in to VintMap",
    "wishlist.editItem": "Edit item",
    "wishlist.saveChanges": "Save",
    "wishlist.addedTitle": "Added to wishlist",
    "wishlist.addedDesc": "This gem is now on your list!",
    "privacyDialog.title": "Privacy protection",
    "privacyDialog.intro": "You are about to share this route publicly, but you added personal addresses.",
    "privacyDialog.notShared": "The following data will NOT be shared:",
    "privacyDialog.startAddress": "Your start address",
    "privacyDialog.endAddress": "Your end address",
    "privacyDialog.shared": "What WILL be shared:",
    "privacyDialog.sharedItem1": "Route name and description",
    "privacyDialog.sharedItem2": "Selected thrift shops",
    "privacyDialog.sharedItem3": "Order of shops",
    "privacyDialog.outro": "You can still keep this route private with your personal addresses, or share publicly without addresses.",
    "privacyDialog.shareWithoutAddresses": "Share without addresses",
    "postDetail.title": "Post details",
    "postDetail.fullViewDesc": "Full view of this community post including comments.",
    "postDetail.viewShop": "View shop",
    "postDetail.viewRoute": "View route",
    "postDetail.comments": "Comments",
    "postDetail.noComments": "No comments yet",
    "postDetail.addComment": "Add a comment...",
    "postDetail.loginToComment": "Log in to comment",
    "postComposer.captionRequired": "Description is required",
    "postComposer.minOnePhotoRequired": "At least 1 photo is required",
    "postComposer.categoryRequired": "Category is required",
    "postComposer.loginRequired": "You must be logged in to share posts. Please log in first.",
    "postComposer.uploadFailed": "Upload failed",
    "postComposer.createFailed": "Failed to create post",
    "postComposer.genericError": "An error occurred. Please try again.",
    "postComposer.placeShopUpdate": "Post a shop update",
    "postComposer.shareFind": "Share your find",
    "postComposer.shopUpdateDesc": "Share news, opening hours, or announcements with your followers.",
    "postComposer.shareDesc": "Share your best find with the community.",
    "postComposer.messageLabel": "Message",
    "postComposer.shopUpdatePlaceholder": "What do you want to share with your followers?",
    "postComposer.findPlaceholder": "Tell us about your find...",
    "postComposer.photosLabel": "Photos",
    "postComposer.optional": "(optional)",
    "postComposer.photosRequiredHint": "* (minimum 1, maximum 4)",
    "postComposer.shareFindWithCommunity": "Share your find with the community",
    "postComposer.maxPhotosHint": "Max 4 photos - Automatically optimized",
    "postComposer.shopLabel": "Shop",
    "postComposer.selectedShop": "Selected shop",
    "postComposer.searchShop": "Search shop...",
    "postComposer.noShopFound": "No shop found. Try another search term.",
    "postComposer.categoryLabel": "Category",
    "postComposer.chooseCategory": "Choose category",
    "postComposer.postAs": "Post as",
    "postComposer.myAccount": "My account",
    "postComposer.userFallback": "user",
    "postComposer.owner": "owner",
    "postComposer.postOfficiallyAsShop": "You are posting this message officially on behalf of the shop.",
    "postComposer.sharing": "Sharing...",
    "editShop.approveFailed": "Approval failed",
    "editShop.approved": "Approved",
    "editShop.suggestionApplied": "The suggestion has been applied.",
    "editShop.removed": "Removed",
    "editShop.suggestionRejected": "The suggestion has been rejected.",
    "editShop.bannerUploadFailed": "Banner upload failed",
    "editShop.saveFailed": "Save failed",
    "editShop.updated": "Updated",
    "editShop.suggestionSaved": "Suggestion saved",
    "editShop.shopSaved": "Shop details were saved successfully.",
    "editShop.suggestionSavedDesc": "Your suggestion was saved and is awaiting approval.",
    "editShop.noPendingSuggestions": "There are currently no pending suggestions.",
    "editShop.communitySuggestionsDesc": "Below are improvements submitted by the community. Approve them to update your profile details immediately.",
    "editShop.submittedOn": "Submitted on",
    "editShop.addressOnlyDesc": "Are address details incorrect? Submit the correct details here. A moderator will review the change.",
    "editShop.streetNumber": "Street and number",
    "editShop.city": "City",
    "editShop.name": "Name",
    "editShop.website": "Website",
    "editShop.aboutShop": "About the shop",
    "editShop.bannerImage": "Banner image",
    "editShop.setBanner": "Set banner",
    "editShop.bannerVerifiedOnly": "Banners are only available for verified thrift shops.",
    "editShop.featuresPayment": "Features and payment",
    "editShop.shopPhotos": "Shop photos ({count} / {max})",
    "editShop.facadeFirst": "Facade photo (always shown first)",
    "editShop.addFacadePhoto": "Add facade photo",
    "editShop.ownerDesc": "Manage your shop details and review visitor suggestions.",
    "editShop.communityDesc": "Help keep this information up to date.",
    "editShop.suggestions": "Suggestions",
    "editShop.submitSuggestion": "Submit suggestion",
    "itemWishlist.addedTitle": "Item added",
    "itemWishlist.addedDesc": "Your item was added to your wishlist.",
    "itemWishlist.addFailed": "Could not add item.",
    "itemWishlist.deletedTitle": "Item removed",
    "itemWishlist.deletedDesc": "Your item was removed from your wishlist.",
    "itemWishlist.itemsToFind": "{count} items to find",
    "itemWishlist.placeholder": "What are you looking for? (e.g. Vintage desk lamp)",
    "itemWishlist.foundOn": "Found on",
    "itemWishlist.emptyTitle": "You have no wishlist items yet.",
    "itemWishlist.emptyDesc": "Add items you would like to find!",
    "itemWishlist.postCaptionPrefix": "I finally found my",
    "itemWishlist.itemFallback": "item",
    "itemWishlist.postCaptionSuffix": "! #thriftluck",
    "itemWishlist.foundCongratsTitle": "Congrats on your gem! 🎉",
    "itemWishlist.foundCongratsDesc1": "Great that you found",
    "itemWishlist.foundCongratsDesc2": "! Put this gem on the map!",
    "itemWishlist.noOnlyCheck": "No, only check off",
    "itemWishlist.yesShare": "Yes, share!",
    "cookie.title": "Cookies? Yes, but only essential ones.",
    "cookie.desc": "We use cookies to keep the app running and measure anonymous usage. We do not track you outside this app.",
    "cookie.readMore": "Read more in our",
    "cookie.accept": "Okay, continue",
    "cookie.preferences": "Preferences",
    "cookie.functional": "Functional (Always on)",
    "cookie.analytical": "Analytical",
    "cookie.marketing": "Marketing",
    "cookie.save": "Save preferences",
    "cookie.functional.desc": "Basic functionality to make the app work.",
    "cookie.analytical.desc": "Helps us improve the app by measuring anonymous usage.",
    "cookie.marketing.desc": "Allows us to show relevant advertisements.",
    "addressSelector.locationFetchFailed": "Could not retrieve your location. Check your browser settings.",
    "addressSelector.geolocationNotSupported": "Geolocation is not supported by your browser.",
    "addressSelector.selectSource": "Select address source",
    "addressSelector.none": "None",
    "addressSelector.homeAddress": "Home address",
    "addressSelector.customAddress": "Custom address",
    "addressSelector.currentLocation": "Current location",
    "addressSelector.home": "Home",
    "addressSelector.custom": "Custom",
    "addressSelector.noCoordinates": "No coordinates",
    "limitBanner.maxReached": "You reached the maximum ({current}/{max} {plural})",
    "limitBanner.nearLimit": "Almost at your limit ({current}/{max} {plural})",
    "limitBanner.upgradeUnlimited": "Upgrade to Routes Insider for unlimited usage.",
    "limitBanner.upgradeUnlimitedOptions": "Upgrade to Routes Insider for unlimited possibilities.",
    "loginModal.signupFailed": "An error occurred while creating your account.",
    "loginModal.accountCreateFailed": "Could not create account. Please try again.",
    "loginModal.accountCreatedCheckEmail": "Account created! Check your email for the verification link.",
    "loginModal.loginFailed": "Login failed. Check your email and password.",
    "loginModal.couldNotLogin": "Could not log in. Please try again.",
    "loginModal.genericError": "Something went wrong. Please try again.",
    "loginModal.googleLoginFailed": "Something went wrong with Google login. Please try again.",
    "loginModal.forgotPassword": "Forgot password?",
    "loginModal.resetPassword": "Reset password",
    "loginModal.resetPasswordSent": "Email sent! Check your inbox.",
    "loginModal.newPassword": "New password",
    "loginModal.updatePassword": "Update password",
    "loginModal.passwordUpdated": "Password updated!",
    "loginModal.createAccount": "Create account",
    "loginModal.createToUseFeatures": "Create an account to use all features",
    "loginModal.password": "Password",
    "loginModal.orContinueWith": "Or continue with",
    "loginModal.loginWithGoogle": "Sign in with Google",
    "loginModal.haveAccountLogin": "Already have an account? Log in",
    "loginModal.noAccountCreate": "No account yet? Create one",
    "loginModal.freeAccountCan": "With a free account you can:",
    "loginModal.termsNotice": "By continuing you agree to our terms.",
    "postCard.noImage": "No image",
    "postCard.report": "Report",
    "footer.copyright": "© 2025 VintMap.app. All rights reserved.",
    "header.menu": "Menu",
    "profileAddress.title": "Home address",
    "profileAddress.desc": "Save your home address for use in routes",
    "profileAddress.save": "Save address",
    "profileAddress.saveFailed": "Could not save address. Please try again.",
    "pwa.title": "VintMap on your phone?",
    "pwa.desc": "Add the app to your home screen for a faster experience.",
    "pwa.installNow": "Install now",
    "pwa.iosInstallInstructions": "VintMap on iPhone:\n1. Tap 'Share' (square with arrow) at the bottom of Safari.\n2. Choose 'Add to Home Screen'.",
    "pwa.androidInstallInstructions": "VintMap on Android:\n\n1. Tap the menu (3 dots/lines).\n2. Choose 'Install app' or 'Add to Home screen'.\n\nSamsung Internet:\nChoose 'Add page to' > 'Home screen'.",
    "report.title": "Report content",
    "report.desc": "Help us keep the community safe and fun. Why do you want to report this?",
    "report.explanationOptional": "Explanation (optional)",
    "report.extraContext": "Add extra context...",
    "report.submit": "Submit report",
    "report.sending": "Sending...",
    "report.thanksTitle": "Thanks for your report",
    "report.thanksDesc": "We received your report and will review it.",
    "report.alreadyReportedTitle": "Already reported",
    "report.alreadyReportedDesc": "You already reported this item.",
    "report.submitFailed": "Could not submit report",
    "report.errorTitle": "Something went wrong",
    "report.errorDesc": "Could not send the report. Please try again later.",
    "reportShop.titleAbout": "Report about {shopName}",
    "reportShop.desc": "Help us keep the map up to date. What is happening?",
    "reportShop.explanation": "Explanation",
    "reportShop.tellMore": "Tell us more...",
    "reportShop.checking": "We will check it.",
    "reportShop.closedPermanentlyDesc": "We check this directly with Google. If closure is confirmed, we update the status.",
    "report.reason.spam": "Spam or advertising",
    "report.reason.inappropriate": "Inappropriate content",
    "report.reason.offensive": "Offensive or harmful",
    "report.reason.misleading": "Misleading information",
    "report.reason.copyright": "Copyright infringement",
    "report.reason.other": "Other",
    "reportShop.reason.notThrift": "This is not a thrift / second-hand shop",
    "reportShop.reason.closedPermanently": "This thrift shop no longer exists",
    "reportShop.reason.closedTemporary": "Temporarily closed (renovation/holiday)",
    "reportShop.reason.wrongInfo": "Information is incorrect (address, time, etc.)",
    "reportShop.reason.other": "Other",
    "account.stats.rides": "rides",
    "account.stats.reviews": "reviews",
    "account.stats.scans": "scans",
    "map.googleMapsNotConfigured": "Google Maps configuration is missing or incorrect.",
    "map.loading": "Loading map...",
    "map.locationUnavailable": "Location unavailable",
    "map.startHome": "Start (Home)",
    "map.start": "Start",
    "map.endHome": "End (Home)",
    "map.end": "End",
    "map.startPoint": "Start point",
    "map.endPoint": "End point",
    "map.loadInteractive": "Load interactive map",
    "map.addressNotFound": "Address not found.",
    "map.stopLabel": "Stop {number}",
    "map.limitReachedTitle": "Map limit reached",
    "shops.categoriesAvailable": "Categories available",
    "rating.desc": "Share your experience and help others find the best shops.",
    "rating.overall": "Overall impression",
    "rating.atmosphere": "Atmosphere",
    "rating.assortment": "Assortment",
    "rating.price": "Price",
    "rating.parking": "Parking",
    "rating.average": "Average rating",
    "rating.commentOptional": "Comment (optional)",
    "rating.commentPlaceholder": "Share your experience with this thrift shop...",
    "rating.photosOptional": "Photos (optional, maximum 4)",
    "rating.addPhotos": "Upload",
    "rating.save": "Save review",
    "rating.editTitle": "Edit your review",
    "shops.details": "Shop features",
    "shops.isCharity": "Charity",
    "shops.paymentMethods": "Payment methods",
    "shops.cash": "Cash",
    "shops.card": "Card",
    "categories.Meubels": "Furniture",
    "categories.Kleding & Schoenen": "Clothing & Shoes",
    "categories.Boeken & Media (LP's, CD's)": "Books & Media (LPs, CDs)",
    "categories.Woondecoratie & Kunst": "Home Decor & Art",
    "categories.Keuken- & Huishoudelijk": "Kitchen & Household",
    "categories.Elektronica (klein)": "Electronics (small)",
    "categories.Speelgoed & Spellen": "Toys & Games",
    "categories.Sieraden & Accessoires": "Jewelry & Accessories",
    "categories.Winkelnieuws": "Shop News",
    "share.copiedTitle": "Copied!",
    "share.copiedDesc": "The text has been copied to your clipboard.",
    "share.notSupportedTitle": "Not supported",
    "share.notSupportedDesc": "Your browser does not support this feature.",
    "share.failedTitle": "Sharing failed",
    "share.failedDesc": "Opening the share menu failed.",
    "share.pleaseWait": "One moment...",
    "share.downloadingImage": "Downloading image...",
    "share.imageDownloadedTitle": "Image downloaded",
    "share.imageDownloadedDesc": "Open Instagram and post the image!",
    "share.noImageTitle": "No image",
    "share.noImageDesc": "No image is available.",
    "share.titlePrefix": "Share",
    "share.desc": "Via social media or copy the link.",
    "share.applyingBranding": "Applying branding...",
    "share.shopDetails": "Shop details",
    "share.routeOverview": "Route overview",
    "share.downloadImage": "Download image",
    "share.messagePreview": "Message preview",
    "share.beforeLabel": "BEFORE",
    "share.afterLabel": "AFTER",
    "share.originalLabel": "ORIGINAL",
    "share.whatsapp": "WhatsApp",
    "share.facebook": "Facebook",
    "share.pinterest": "Pinterest",
    "share.instaSave": "Insta/Save",
    "share.x": "X",
    "share.email": "Email",
    "share.shareVia": "Share via...",
    "shopCard.charity": "Charity",
    "shopCard.inRoutes": "In {count} routes",
    "shopCard.verified": "Verified",
    "shopCard.paymentOptions": "Payment options",
    "shopCard.cash": "Cash",
    "shopCard.card": "Card",
    "shopCard.removeFavorite": "Remove from favorites",
    "shopCard.addFavorite": "Add to favorites",
    "shopCard.removeWishlist": "Remove from to-visit",
    "shopCard.addWishlist": "Mark as to-visit",
    "shopCard.navigateToShop": "Navigate to this shop",
    "shopCard.maps": "Maps",
    "shopCard.removedFromRoute": "Removed from route",
    "shopCard.addedToRoute": "Added to route",
    "shopCard.inRoute": "In route",
    "shopCard.planRoute": "+ Plan route",
    "shopDashboard.notLoggedIn": "Not logged in",
    "shopDashboard.loginToManage": "Log in to manage your shops.",
    "shopDashboard.loadingShops": "Loading shops...",
    "shopDashboard.title": "Shop dashboard",
    "shopDashboard.manageClaimed": "Manage your claimed shops",
    "shopDashboard.noneClaimed": "No shops claimed yet",
    "shopDashboard.claimToManage": "Claim a shop to manage it.",
    "shopDashboard.manageAndVerify": "Manage your claimed shops and verification",
    "shopDashboard.viewProfile": "View profile",
    "shopDashboard.hideStats": "Hide stats",
    "shopDashboard.stats": "Stats",
    "shopDashboard.manage": "Manage",
    "shopDashboard.confirmUnclaim": "Are you sure you want to unlink this shop? You will lose management rights.",
    "shopDashboard.waitingLetter": "Waiting for verification letter",
    "shopDashboard.notVerified": "Not verified",
    "shopDashboard.startVerification": "Start verification (EUR19.95 ex VAT)",
    "shopDashboard.letterOnTheWay": "Your verification letter is on the way. Enter the code as soon as you receive it.",
    "shopDashboard.verificationCode": "Verification code",
    "shopDashboard.codeExample": "e.g. TEST1234",
    "shopDashboard.verify": "Verify",
    "shopDashboard.verifiedAllFeatures": "Your shop is verified. All features are now available.",
    "shopDashboard.validVerificationCode": "Enter a valid verification code",
    "scanner.loginRequired": "You must be logged in to scan photos.",
    "scanner.scanStarted": "Scan started",
    "scanner.photosUploadedForAnalysis": "{count} photos uploaded for analysis.",
    "scanner.optimizingImages": "Optimizing images...",
    "scanner.keepQuality": "Keeping quality, reducing size",
    "scanner.startVintMapScan": "Start your VintMap Scan",
    "scanner.startScanDesc": "Take a photo of an item or choose one from your gallery to discover what it is.",
    "scanner.maxPhotosPerScan": "Maximum {max} photos per scan",
    "scanner.aiObjectRecognition": "AI-powered object recognition",
    "shopsSearchMap.apiKeyMissing": "Google Maps API key not found",
    "shopsSearchMap.checkEnv": "Check VITE_GOOGLE_MAPS_API_KEY in your .env file",
    "shopsSearchMap.loadError": "Error loading Google Maps",
    "simpleMapTest.title": "Simple Map Test",
    "simpleMapTest.checkEnv": "Check your .env file or Vercel environment variables",
    "simpleMapTest.desc": "If you see a RED border and YELLOW background but NO map, the issue is with Google Maps rendering.",
    "simpleMapTest.checkConsole": "Check browser console for logs.",
    "simpleMapTest.debugInfo": "Debug Info:",
    "simpleMapTest.item1": "Container: RED border (10px), Yellow background, 500px height",
    "simpleMapTest.item2": "Map: Should fill 100% of container",
    "simpleMapTest.item3": "Check browser console for detailed logs",
    "simpleMapTest.item4": "If you see yellow but no map -> Google Maps rendering issue",
    "simpleMapTest.item5": "If you see nothing -> Container CSS issue",
    "shopStats.loadFailed": "Could not load statistics.",
    "shopStats.titleFor": "Statistics for {shopName}",
    "shopStats.desc": "Insight into your visibility and reach.",
    "shopStats.thisMonth": "This month",
    "shopStats.lastMonth": "Last month",
    "shopStats.routeImpact": "Route Impact",
    "shopStats.newPublicRoutes": "New public routes:",
    "shopStats.newPrivateRoutes": "New private routes:",
    "shopStats.routeImpactHint": "The more often your shop appears in routes, the more often it is visited by focused shoppers.",
    "shopRating.vintMap": "VintMap:",
    "shopRating.yourScore": "Your score:",
    "shopRating.google": "Google:",
    "shopRating.noReviews": "no reviews",
    "shopRating.writeReview": "Write a review",
    "shopRating.editReview": "Edit your review",
    "openingHours.tempClosed": "Temporarily closed",
    "openingHours.untilDate": "until",
    "openingHours.unknown": "Opening hours unknown",
    "openingHours.closedOnSelectedDay": "Closed on this day",
    "openingHours.openLabel": "Open",
    "openingHours.openUntil": "Open until",
    "openingHours.closingSoon": "closing soon",
    "openingHours.closedNow": "Closed now",
    "openingHours.reopensAt": "reopens at",
    "openingHours.tomorrow": "tomorrow",
    "openingHours.opensLabel": "open",
    "openingHours.atLabel": "at",
    "app.name": "VintMap",
    "about.title": "The story behind VintMap",
    "about.p1": "Anyone who knows me knows I love treasure hunting. Whether for my interior or clothing, most of it is second hand.",
    "about.p2": "My favorite days off are spent with my son, driving around to find great places.",
    "about.p3": "I wanted a tool that does more than navigation: plan routes, share finds, and inspire each other.",
    "about.quote": "\"Because that app did not exist, we built it ourselves.\"",
    "about.p4": "VintMap is built for everyone who loves thrifting. More than a route planner.",
    "about.p5": "Together we keep improving the map with hidden gems, reviews, and shared finds.",
    "about.cta": "Let's keep finding treasures together.",
    "about.signatureLead": "Happy treasure hunting,",
    "about.signatureName": "VintMap",
    "about.signatureRole": "",
    "contact.title": "Contact",
    "contact.intro": "Do you have questions about VintMap, app suggestions, or collaboration ideas? We would love to hear from you.",
    "contact.emailTitle": "Email",
    "contact.emailDesc": "For general questions and support.",
    "contact.emailCta": "Email info@vintmap.app",
    "contact.faqTitle": "Frequently asked questions",
    "contact.faqDesc": "Check our FAQ for quick answers.",
    "contact.faqCta": "Go to FAQ",
    "contact.ownersTitle": "For shop owners",
    "contact.ownersDesc": "Want to add, claim, or update your shop? In most cases you can do this directly in the app.",
    "contact.ownersBullet1": "Find your shop on the map and submit changes with the report flag.",
    "contact.ownersBullet2": "Click \"Claim this shop\" on the detail page to become manager.",
    "contact.ownersBullet3prefix": "Not in the app yet?",
    "contact.ownersBullet3link": "Add your shop here",
    "login.welcomeBack": "Welcome back",
    "login.subtitle": "Log in to manage your routes and share your finds",
    "notFound.goHome": "Go home",
    "unauthorized.title": "Access Denied",
    "unauthorized.desc": "You do not have sufficient permissions to view this page.",
    "unauthorized.errorNote": "If you think this is an error, please log in again or contact the administrator.",
    "unauthorized.backHome": "Back to Home",
    "unauthorized.loginAgain": "Log in again",
    "verification.noShopId": "No shop ID found",
    "verification.successTitle": "Shop claimed successfully!",
    "verification.successDesc": "You are now the owner. Verify your shop to unlock all features.",
    "verification.errorFetch": "Something went wrong while retrieving status.",
    "verification.loadingTitle": "Processing payment...",
    "verification.loadingDesc": "One moment, we are linking the shop to your account.",
    "verification.errorTitle": "Something went wrong",
    "verification.errorFallback": "Contact support if this problem persists.",
    "verification.retry": "Try again",
    "verification.goDashboard": "Go to dashboard",
    "verification.congrats": "Congratulations!",
    "verification.ownerNow": "You are now the proud owner.",
    "verification.paymentOk": "Payment succeeded and the shop is linked to your account.",
    "verification.letterSent": "The verification letter and sticker will be sent to the shop address.",
    "verification.nextSteps": "Next steps:",
    "verification.step1": "Wait for the letter",
    "verification.step1desc": "You will receive it within 2-3 business days.",
    "verification.step2": "Enter the code",
    "verification.step2desc": "Open your admin panel and enter the code.",
    "verification.step3": "Done",
    "verification.step3desc": "You can now upload photos, update hours, and more.",
    "verification.goAdmin": "Go to admin panel",
    "verification.myAccount": "My account",
    "community.title": "Community",
    "community.subtitle": "Discover the best thrift gems and updates from your favorite shops.",
    "community.shareFind": "Share your find",
    "community.category": "Category",
    "community.allCategories": "All categories",
    "community.region": "Region",
    "community.allRegions": "All regions",
    "community.loadingPosts": "Loading posts...",
    "community.userGems": "Thrift Gems",
    "community.noGems": "No gems found yet.",
    "community.shopUpdates": "Shop updates",
    "community.myFavoriteShops": "My favorite shops",
    "community.noFavoriteUpdates": "No updates from your favorites",
    "community.likeShopsHint": "Like shops to see their updates here.",
    "community.noShopUpdates": "No shop updates found.",
    "community.viewMore": "View more",
    "community.tabs.explore": "Explore",
    "community.tabs.treasureHunters": "Treasure Hunters",
    "community.currency": "Currency",
    "community.currency.eur": "EUR (€)",
    "community.currency.gbp": "GBP (£)",
    "community.currency.usd": "USD ($)",
    "community.currency.chf": "CHF (₣)",
    "home.hero.imageAlt": "Thrift shop",
    "home.insider.quote": "\"Thanks to VintMap I found this gem!\"",
    "faq.visitors.q5": "Are there limits to using the map?",
    "faq.visitors.a5": "Yes, to keep VintMap free and sustainable for everyone, we use a Fair Use policy for interactive components. For Insiders, there is a generous limit of 20 interactive map views per day (240 per month) and 10 route optimizations per day (60 per month). For free accounts, these limits are lower. Static overview maps are always unlimitedly visible.",
    "faq.visitors.q6": "Does my temporary shop list disappear automatically after a while?",
    "faq.visitors.a6": "Yes. Shops you have temporarily selected (your 'draft route') are automatically cleared after 72 hours. This prevents you from accidentally taking an outdated selection with you. Save your route if you want to keep it!",

    // === Legal: FAQ ===
    "faq.title": "Frequently Asked Questions",
    "faq.intro": "Here you will find answers to the most frequently asked questions about VintMap. Is your question not listed? Please contact us.",
    "faq.general.title": "General",
    "faq.general.q1": "What is VintMap?",
    "faq.general.a1": "VintMap is the platform for thrift and vintage enthusiasts. You can easily discover the best thrift stores, plan routes, and share your finds with the community.",
    "faq.general.q2": "Is VintMap free to use?",
    "faq.general.a2": "Yes, using VintMap is free for visitors. You can search, view routes, and find shops for free. For additional features like 'My Routes' (unlimited saving), we offer an Insider subscription.",
    "faq.general.q3": "Is there an app available?",
    "faq.general.a3": "VintMap is a Progressive Web App (PWA). This means you can 'install' the website via your browser on your phone as an app, without having to go through the App Store or Play Store. You will also get an icon on your home screen.",
    "faq.visitors.title": "For Visitors",
    "faq.visitors.q1": "How do I plan a route?",
    "faq.visitors.a1": "Go to the 'Routes' page in the menu. Here you can enter a start and end point and add your favorite shops. The app then calculates the most optimal route for you.",
    "faq.visitors.q2": "Can I add a shop myself?",
    "faq.visitors.a2": "Certainly! If you are logged in, you can make a new suggestion via 'Add Shop' in the menu. Our team checks the details before the shop officially goes online.",
    "faq.visitors.q4": "Can I share my finds?",
    "faq.visitors.a4": "Yes, via 'Share your loot' you can share photos of your best purchases with the community. Other users can like and respond to these.",
    "faq.owners.title": "For Shop Owners",
    "faq.owners.q1": "How do I claim my shop?",
    "faq.owners.a1": "Find your shop on the map or in the list. If it has not been claimed yet, you will see a button 'Are you the owner?'. Follow the steps to claim your shop and get access to the admin panel.",
    "faq.owners.q2": "What does a business profile cost?",
    "faq.owners.a2": "A basic listing is always free. For extensive options such as managing opening hours, uploading your own photos, and responding to reviews, we ask for a one-time verification contribution or offer premium subscriptions.",
    "faq.owners.q3": "How do I change my opening hours?",
    "faq.owners.a3": "After claiming and verifying your shop, you can log in to your dashboard. There you can directly adjust your opening hours, address details, and photos.",
    "faq.owners.q4": "I have not received a verification letter, what now?",
    "faq.owners.a4": "The verification letter will be sent within 2 working days. Have you not received anything after 5 working days? Please contact support@vintmap.app.",
    "faq.account.title": "Account & Privacy",
    "faq.account.q1": "How do I change my password?",
    "faq.account.a1": "In your account settings, you can change your password. If you have forgotten your password, use the 'Forgot Password' link on the login page.",
    "faq.account.q2": "Is my data shared?",
    "faq.account.a2": "We care a lot about your privacy. Your personal data is not sold to third parties. Only necessary data for the operation of the app (such as location for the map) is used according to our Privacy Policy.",
    "faq.account.q3": "How do I delete my account?",
    "faq.account.a3": "You can submit a request for account deletion via your account settings or by sending an email to privacy@vintmap.app. We will then delete all your personal data.",
    "faq.tech.title": "Technical & Contact",
    "faq.tech.q1": "The map won't load, what should I do?",
    "faq.tech.a1": "Check if your internet connection is working and if you have allowed location access in your browser. Try refreshing the page. If the problem persists? Send us a message.",
    "faq.tech.q2": "How do I contact you?",
    "faq.tech.a2": "For technical questions, suggestions, or collaborations, you can email info@vintmap.app.",
    "faq.q12": "Why does the scanner sometimes warn that something is not visible?",
    "faq.a12": "That is intentional: the scanner should not invent details. If information is not visually certain, it is explicitly stated.",
    "faq.q13": "In which language does the scanner output appear?",
    "faq.a13": "Scanner output follows your chosen app language. With missing translations, the app uses a temporary fallback until translations are added.",
    "faq.q14": "Can I save scanner results?",
    "faq.a14": "Yes, scanner results are linked to your account history within the available functionality of your account level.",
    "faq.q15": "How do likes, reviews, and community posts work?",
    "faq.a15": "You can post contributions and respond within the community features. Content must comply with platform rules.",
    "faq.q16": "How do paid features and subscriptions work?",
    "faq.a16": "Paid features are handled via Stripe. Billing and renewal follow the chosen subscription type.",
    "faq.q17": "Can I cancel?",
    "faq.a17": "Yes, you can cancel via your account settings. The change takes effect according to the terms of your active period.",
    "faq.q18": "What data do you keep about me?",
    "faq.a18": "Only data necessary for account, functionality, security, and legal obligations, as described in the privacy policy.",
    "faq.q19": "How do I request access or deletion?",
    "faq.a19": "Through the available account options or by contacting us via the contact page. We handle requests in accordance with legislation.",
    "faq.q20": "Where do I report bugs or legal questions?",
    "faq.a20": "Use the contact page for support, bug reports, and legal questions. Mention as much context as possible for quick follow-up.",
    "faq.q21": "Why does the Restyler sometimes give strange results?",
    "faq.a21": "The Restyler uses generative AI to 'repaint' your furniture. AI works with patterns and can sometimes make mistakes (hallucinate). For example, the AI might accidentally add glass to wood carvings or distort a handle. Use the available extra options (such as 'Contains glass') to help guide the AI.",
    "faq.q22": "Is the Scanner always 100% accurate?",
    "faq.a22": "No. The AI scanner learns from millions of images, but can misinterpret specific vintage objects, materials, or rare brands. Always see scanner output as a 'second opinion' or starting point for your own research, never as an indisputable fact.",
    "faq.q23": "Why does the AI sometimes seem stubborn?",
    "faq.a23": "AI models sometimes have a strong preference for certain styles or lighting they have seen a lot during their training. Because of this, the AI might ignore a specific instruction or put its own spin on a design. We continuously improve our prompts to minimize this.",
    "faq.q24": "What happens to the photos I send to the AI?",
    "faq.a24": "Photos you restyle are temporarily processed by secure third-party AI servers. These images are not used to train third-party public models without your consent. View our privacy policy for all details on data processing.",
    "faq.q25": "Do I only get the shared image with text, or also the clean photo?",
    "faq.a25": "VintMap always saves the full, clean restyled image separately from the 'share image'. You can find and download the clean version of your design at any time on the 'My Restyle Projects' page. Click on the large image there for a full view.",
    "faq.contact.title": "Didn't find what you were looking for?",
    "faq.contact.desc": "Our support team is ready for you. Send us a message and we'll be happy to help you.",
    "faq.contact.link": "Contact us",

    "legal.updated": "Last updated: 05-03-2026",
    "privacy.dashboard.title": "Privacy Dashboard",
    "privacy.dashboard.desc": "Manage your data and privacy settings.",
    "privacy.dashboard.geotargeting": "Geotargeting Level",
    "privacy.dashboard.geotargetingDesc": "Control how accurately we use your location for relevant advertisements.",
    "privacy.dashboard.geotargeting.national": "National",
    "privacy.dashboard.geotargeting.provincial": "Provincial",
    "privacy.dashboard.geotargeting.local": "Local",
    "privacy.dashboard.ads": "Personalized Advertisements",
    "privacy.dashboard.adsDesc": "Show ads based on my interests and behavior.",
    "privacy.functional.desc": "Necessary for the operation of the app.",
    "privacy.export.desc": "Receive a copy of all your saved data.",
    "privacy.delete.desc": "Invoke the right to be forgotten.",
    "privacy.locationConsent": "Location data",
    "privacy.locationConsentDesc": "Permission to use your current location for route optimization.",
    "privacy.dashboard.dataRights": "Data rights",
    "privacy.dashboard.export": "Download all my data (JSON)",
    "privacy.dashboard.deleteNote": "Your data will be permanently deleted after 30 days. You can still cancel this request within that period by contacting us.",
    "privacy.dashboard.confirmDelete": "Permanently delete",
    "privacy.title": "Privacy Policy",
    "privacy.1.title": "1. Introduction and Scope",
    "privacy.1.body": "### 1.1 Scope\nThis privacy policy describes how VintMap collects, uses, shares, and protects personal data within the PWA, website, and all associated functionalities such as route planning, AI furniture restyler, and the community.\n\n### 1.2 Applicable Legislation\nWe process data in accordance with the GDPR (AVG), the Dutch GDPR Implementation Act, and the Telecommunications Act.\n\n### 1.3 Age Restriction\nVintMap is intended for persons aged 16 and older. We do not knowingly collect data from minors.",
    "privacy.2.title": "2. Data Controller and Contact Information",
    "privacy.2.body": "### 2.1 Data Controller\nVintMap is responsible for the processing of your data.\nKvK: 01119829\nVoorstraat 142, 9291 CP Kollum\nThe Netherlands\n\n### 2.2 Contact for Privacy Questions\nFor questions about your privacy, you can contact us via privacy@vintmap.nl or via the contact page in the app. We aim to respond within 2 working days.",
    "privacy.3.title": "3. Which Personal Data We Process",
    "privacy.3.body": "### 3.1 Account Data\nEmail address, username, password (encrypted), profile picture, and account status.\n\n### 3.2 Usage Data\nSaved routes, favorite shops, search history, and interactions with the community.\n\n### 3.3 Location Data\nCurrent location (only during active use) and chosen starting points. We do not track you in the background.\n\n### 3.4 User Content\nReviews, uploaded photos, and community routes.\n\n### 3.5 Technical Data\nIP address (anonymized), browser type, device type, and error reports.\n\n### 3.6 Payment Metadata\nVia Stripe, we process subscription status and payment history. We do not store full credit card details.",
    "privacy.4.title": "4. Purposes and Legal Bases for Processing",
    "privacy.4.body": "### 4.1 Execution of the Agreement\nWe process data to offer the app functions, such as route planning and account management.\n\n### 4.2 Legitimate Interest\nSecurity, fraud prevention, quality improvement of our services, and optimization of the user experience.\n\n### 4.3 Legal Obligations\nTax administration and accounting obligations (such as the 7-year retention obligation).\n\n### 4.4 Consent\nFor specific functions such as location access, we ask for explicit consent, which you can withdraw at any time.",
    "privacy.5.title": "5. Location Data and Map Functionality",
    "privacy.5.body": "### 5.1 When Location Data is Used\nWhen opening the map, starting a route, or using Insider navigation.\n\n### 5.2 How Location Data is Processed\nLocation is processed live via the Google Maps API. We do not build historical movement profiles.\n\n### 5.3 Managing Location Consent\nYou can withdraw consent via your browser or device settings. Without consent, you must manually enter a starting point.\n\n### 5.4 External Navigation\nWhen using the \"Open in Google Maps\" button, you leave the VintMap webapp. From that moment, the privacy terms and policy of Google Maps apply.",
    "privacy.6.title": "6. AI Features and Image Processing",
    "privacy.6.body": "### 6.1 AI Furniture Restyler\nWith this function, you can upload photos of furniture for visualization.\n\n### 6.2 Processing of Images\nPhotos are sent securely for AI processing. The edited images are stored in your personal history so you can view them later. You can delete these images manually at any time.\n\n### 6.3 External AI Provider\nWe work with specialized AI providers (such as OpenAI/Replicate). Photos are not used to train their models without your consent.",
    "privacy.7.title": "7. Community Features and User Content",
    "privacy.7.body": "### 7.1 Public Content\nReviews, public routes, and photos with reviews are visible to other users.\n\n### 7.2 Control Over Content\nYou decide what you share. Data is private by default. You can delete or change content at any time.\n\n### 7.3 Moderation\nWe moderate content based on our terms of use to keep the community safe.",
    "privacy.8.title": "8. Cookies, Local Storage, and Similar Techniques",
    "privacy.8.body": "### 8.1 What We Use\nVintMap uses cookies and local storage for essential functions (sessions, language) and functional improvements (preferences).\n\n### 8.2 Your Control\nYou can manage and delete stored data via your browser settings or app settings. Note that the app may then work less optimally.",
    "privacy.9.title": "9. Sharing with Third Parties",
    "privacy.9.body": "### 9.1 Service Providers\nWe share data with providers for hosting (Cloudflare, Vercel), database (Supabase), and payments (Stripe).\n\n### 9.2 No Selling of Data\nWe never sell your personal data to third parties for marketing purposes.\n\n### 9.3 Legal Requirement\nWe only share data with authorities if legally required.",
    "privacy.10.title": "10. Security of Personal Data",
    "privacy.10.body": "We use modern techniques such as TLS 1.3 encryption, bcrypt hashing, and firewalls. Access to identifiable data is strictly limited and logged for audit purposes.",
    "privacy.11.title": "11. International Data Transfer",
    "privacy.11.body": "We strive for processing within the EEA. Where this happens outside the EEA (e.g., Google or Stripe), we use approved safeguards such as Standard Contractual Clauses (SCCs).",
    "privacy.12.title": "12. Retention Periods",
    "privacy.12.body": "### 12.1 Periods\nWe keep account data as long as you are active. Inactive accounts are deleted after 3.5 years. Invoices are kept for 7 years in accordance with the law.\n\n### 12.2 Deletion\nUpon account deletion, your data is permanently erased or anonymized within 30 days. Images in the AI history remain saved until you delete them yourself or cancel your account.",
    "privacy.13.title": "13. Your Privacy Rights",
    "privacy.13.body": "### 13.1 Rights\nYou have the right to access, correction, deletion, restriction, data portability, and objection.\n\n### 13.2 Access and Export\nYou can directly view and export your data via Settings > Privacy > 'Download my data'. For other requests, you can email privacy@vintmap.nl.",
    "privacy.14.title": "14. Complaints and Supervisory Authority",
    "privacy.14.body": "In case of dissatisfaction, please contact us first. You also always have the right to file a complaint with the Data Protection Authority.",
    "privacy.15.title": "15. Changes to This Privacy Policy",
    "privacy.15.body": "We may adjust this policy. In case of major changes, we will inform you proactively via the app or email.",
    "privacy.16.title": "16. Contact Details",
    "privacy.16.body": "VintMap Privacy Team\nEmail: privacy@vintmap.nl\nFor urgent security incidents: security@vintmap.nl",
    "privacy.17.title": "17. Legal Mention",
    "privacy.17.body": "VintMap is registered at the Dutch Chamber of Commerce (KvK) under number 01119829.",

    // === Legal: Terms ===
    "terms.title": "Terms and Conditions",
    "terms.1.title": "1. Definitions",
    "terms.1.body": "**Vintmap**: The service provided via the website and app, located at Voorstraat 142, 9291 CP Kollum, Netherlands (KvK: 01119829).\n\n**Service**: All functionalities, features, and content offered via Vintmap.\n\n**User / You**: Any natural person using Vintmap.\n\n**Account**: The personal user profile.\n\n**Insiders**: The paid premium subscription (€ 2.99 p/m or € 29.99 p/y).\n\n**User Content**: All content you upload, post, or share.",
    "terms.2.title": "2. Applicability and Acceptance",
    "terms.2.body": "2.1 These terms apply to all use of the Vintmap website and app.\n\n2.2 By using Vintmap, you declare to have fully read and accepted these General Terms and Conditions and to be at least 16 years old.\n\n2.3 Acceptance occurs by ticking the checkbox during registration or by actively using the Service.",
    "terms.3.title": "3. The Service and Functionalities",
    "terms.3.body": "3.1 Vintmap offers a platform for finding thrift shops, route planning, and AI visualizations.\n\n3.2 Free users have access to basic search functions and limited route planning.\n\n3.3 Insiders get extra features such as direct-start navigation from the current location and the AI Restyler for furniture (25 restyles p/m).",
    "terms.4.title": "4. Account and Registration",
    "terms.4.body": "4.1 An Account is required for certain functionalities.\n\n4.2 You provide truthful information and choose a secure password.\n\n4.3 You may only create one Account. Sharing accounts is not allowed.",
    "terms.5.title": "5. Permitted and Prohibited Use",
    "terms.5.body": "5.1 You use Vintmap in a lawful and careful manner.\n\n5.2 Explicitly prohibited is technical abuse (hacking, scraping via bots), unlawful use (fraud, harassment), and spreading harmful code.\n\n5.3 Commercial abuse, such as systematic copying of the database, is prohibited without permission.",
    "terms.6.title": "6. User Content and Community",
    "terms.6.body": "6.1 User content remains your property.\n\n6.2 You grant Vintmap a non-exclusive, worldwide license to display and process your content within the Service.\n\n6.3 You guarantee that you are the rights holder and do not violate any third-party rights.",
    "terms.7.title": "7. Intellectual Property",
    "terms.7.body": "7.1 All intellectual property rights (software, design, database content) belong to Vintmap.\n\n7.2 You receive a limited, non-transferable license to use the Service as intended.",
    "terms.8.title": "8. Insiders Subscription and Payments",
    "terms.8.body": "8.1 Subscriptions renew automatically unless canceled in time.\n\n8.2 Payment is made in advance. You have a 14-day right of withdrawal after the first purchase, unless you have already used the Service (such as the AI Restyler).\n\n8.3 Cancellation is possible up to 24 hours before the renewal date via the settings.",
    "terms.9.title": "9. AI Restyler Function",
    "terms.9.body": "9.1 The Restyler allows Users to visualize furniture.\n\n9.2 The result is a visualization and not photographic precision. Vintmap is not liable for differences between the visualization and reality.",
    "terms.10.title": "10. Availability and Maintenance",
    "terms.10.body": "10.1 We strive for high availability (99% uptime) but cannot guarantee this.\n\n10.2 Planned maintenance will be announced 48 hours in advance if possible.",
    "terms.11.title": "11. Changes to the Service",
    "terms.11.body": "11.1 Vintmap may change the Service or add/remove functionalities.\n\n11.2 Material changes will be communicated at least 30 days in advance.",
    "terms.12.title": "12. Guarantees and Disclaimers",
    "terms.12.body": "12.1 The Service is provided 'as-is'.\n\n12.2 We do not guarantee that all information (opening hours, locations) is 100% up-to-date or correct.\n\n12.3 **Advertisements and Promotions**: The Service may display third-party advertisements. VintMap DOES NOT endorse and is NOT responsible for the content, products, or services of these ads. NO RIGHTS OR GUARANTEES can be derived from advertisements shown.",
    "terms.13.title": "13. Liability and Damages",
    "terms.13.body": "13.1 Vintmap is not liable for indirect damages, traffic fines, or injuries.\n\n13.2 TO THE MAXIMUM EXTENT PERMITTED BY LAW, VINTMAP DISCLAIMS ALL LIABILITY FOR ANY LOSS OR DAMAGE ARISING FROM YOUR INTERACTION WITH ADVERTISEMENTS OR THIRD-PARTY SELLERS.\n\n13.3 For Insiders, liability is limited to the amount of the annual subscription.",
    "terms.14.title": "14. Termination and Suspension",
    "terms.14.body": "14.1 You can delete your account at any time.\n\n14.2 Vintmap can terminate your account in case of serious violations of these terms.",
    "terms.15.title": "15. Data Protection and Privacy",
    "terms.15.body": "15.1 Personal data is processed in accordance with the GDPR and our Privacy Policy.",
    "terms.16.title": "16. Complaints and Disputes",
    "terms.16.body": "16.1 Complaints can be reported via support@vintmap.nl.\n\n16.2 We aim for a solution within 2 working days.",
    "terms.17.title": "17. Applicable Law and Competent Court",
    "terms.17.body": "17.1 Dutch law applies to these terms.\n\n17.2 Disputes will be submitted to the competent court in the Northern Netherlands.",
    "terms.18.title": "18. Changes to These Terms",
    "terms.18.body": "18.1 We may adjust the terms. Major changes will be announced by email.",
    "terms.19.title": "19. Miscellaneous",
    "terms.19.body": "19.1 If a provision proves invalid, the remaining provisions remain in effect.",
    "terms.20.title": "20. Contact Information",
    "terms.20.body": "VintMap\nVoorstraat 142, 9291 CP Kollum\nlegal@vintmap.nl",

    // === Legal: Disclaimer ===
    "disclaimer.title": "Disclaimer",
    "disclaimer.1.title": "1. Guide and Applicability",
    "disclaimer.1.body": "1.1 Purpose of this disclaimer\nThis disclaimer clarifies the scope and limitations of the information and services provided by VintMap. The goal is to inform users about:\n\n- The nature and reliability of the provided information\n- The boundaries of VintMap's responsibility\n- The user's own responsibility\n- Risks associated with using the service\n\n1.2 Scope of application\nThis disclaimer applies to:\n\n- All information shown within VintMap (website and app)\n- All functionalities and features of the service\n- Community content and user contributions\n- Advertisements and commercial content\n- Links to external websites and services\n\n1.3 Legal context\nThis disclaimer:\n\n- Is an integral part of the General Terms and Conditions\n- Should be read in conjunction with the Privacy Policy\n- Limits the liability of VintMap in accordance with statutory limits\n- Leaves statutory consumer rights intact where they cannot be excluded\n\n1.4 Acceptance\nBy using VintMap, you accept this disclaimer and declare:\n\n- To have taken note of the limitations described herein\n- To understand that VintMap is an informational aid, not an absolute source of truth\n- To take your own responsibility for decisions based on VintMap information\n- To accept the limits of the service",
    "disclaimer.2.title": "2. General Informational Nature",
    "disclaimer.2.body": "2.1 Tool, no guarantee\nVintMap is intended as a tool for discovering thrift shops and planning routes. It is not a:\n\n- Guaranteed source of up-to-date information\n- Complete inventory of all thrift shops\n- Professional navigation service with guarantees\n- Decision-making tool with legal validity\n\n2.2 No rights can be derived\nNo (unconditional) rights can be derived from the information within VintMap. This means:\n\n- No right to the presence of specific information\n- No right to the accuracy or completeness of data\n- No right to uninterrupted availability\n- No right to specific functionality or performance\n- No right to compensation for incorrect or missing information\n\n2.3 Best-effort basis\nVintMap strives to:\n\n- Keep information as up-to-date and correct as possible\n- Provide a reliable and user-friendly service\n- Correct errors and omissions when reported\n- Regularly update the database\n\nHowever, this is a best-efforts obligation, not an obligation of result.\n\n2.4 Use at your own risk\nYou use VintMap entirely at your own risk. VintMap accepts no liability for:\n\n- Decisions made on the basis of VintMap information\n- Disappointments or inconveniences during store visits\n- Time or costs incurred for routes or visits\n- Missed opportunities due to incorrect information",
    "disclaimer.3.title": "3. Map and Location Data",
    "disclaimer.3.body": "3.1 Sources and actuality\nOrigin of data:\nVintMap collects thrift store information from various sources:\n\n- Public sources (websites, online directories)\n- User contributions and community input\n- Google Maps and other map services\n- Direct contact with shops (where possible)\n\nActuality:\n\n- Information may be outdated at the time of consultation\n- Shops may close, move, or change opening hours without notice\n- Database updates take place on a regular but non-real-time basis\n- Seasonal changes are not always processed immediately\n\n3.2 No guarantee of completeness\nVintMap DOES NOT guarantee:\n\n- That all thrift stores in an area are included\n- That the displayed shops actually exist or are open\n- That opening hours are current and correct\n- That contact details (telephone number, website) are correct\n- That addresses precisely match the actual location\n\nReasons for incompleteness:\n\n- New shops not yet added\n- Closed shops not yet removed\n- Small or unknown shops not in the database\n- Temporary pop-ups or events not included\n- Geographical limitations or focus areas\n\n3.3 Recommendation: always verify\nFor important visits we advise:\n\n- Call ahead to confirm opening hours\n- Check the store's website or social media\n- Prepare for possibly closed shops (alternative plan)\n- Take contact details of the store with you\n- Do not blindly trust navigation without verification\n\nEspecially essential for:\n\n- Long trips (>30 minutes driving)\n- Visit for a specific sought item\n- Time-bound plans (last day of vacation, etc.)\n- Group outings or planned events\n\n3.4 Reporting inaccuracies\nHelp VintMap get better:\n\n- Report closed shops via \"Report problem\" in the app\n- Update incorrect opening hours\n- Add missing shops via the community feature\n- Share corrections via feedback@vintmap.nl\n\nYour contribution helps other users and improves the database for everyone.",
    "disclaimer.4.title": "4. Routes and Navigation",
    "disclaimer.4.body": "4.1 Dependence on external services\nVintMap uses the Google Maps Platform for:\n\n- Route calculation and navigation\n- Map display and geocoding\n- Traffic information (if available)\n- Estimated travel times\n\nImportant disclaimer:\n\n- VintMap is dependent on the accuracy of Google Maps data\n- Errors in Google Maps are adopted in VintMap\n- VintMap has no control over Google's data quality\n- See Google's own disclaimers and conditions\n\n4.2 Route suggestions are suggestions\nRoutes within VintMap are:\n\n- Suggested sequences, not absolute optimal routes\n- Based on distance and available data\n- Not always taking into account all local circumstances\n- Indicative, not binding or guaranteed efficient\n\nRoutes DO NOT take into account:\n\n- Current traffic situation (traffic jams, accidents)\n- Road closures or road works\n- Traffic rules (one-way traffic, through traffic prohibited)\n- Seasonal changes (winter conditions)\n- Vehicle-specific restrictions (height, weight, fuel)\n- Personal preferences (highway avoidance, toll roads)\n\n4.3 Own responsibility for traffic safety\nYOU ARE AT ALL TIMES RESPONSIBLE FOR:\n\n- Safely and legally driving your vehicle\n- Concentrating on the traffic, not on the app\n- Complying with all traffic rules and signs\n- Assessing whether a route is suitable and safe\n- Deviating from the route if the situation requires\n\nPROHIBITED:\n\n- Using VintMap while driving\n- Entering data while you are driving\n- Getting distracted by the app in traffic\n- Blindly trusting navigation without your own assessment\n\nRECOMMENDATION:\n\n- Set route before you start driving\n- Use hands-free or voice control where possible\n- Pull over safely if you need to adjust something\n- Let a passenger operate the app\n\n4.4 Estimated travel times\nTravel times are indicative and based on:\n\n- Standard traffic conditions\n- Average driving speeds\n- Historical traffic data (if available)\n\nTravel times DO NOT take into account:\n\n- Current traffic jams or delays\n- Parking time at shops\n- Shopping time or browsing time\n- Pauses, refueling stops, or other stops\n- Personal driving style (calm vs. sporty)\n\nActual travel time can deviate significantly.\n\n4.5 No liability for traffic situations\nVintMap is NOT liable for:\n\n- Traffic accidents during use of routes\n- Traffic fines (speeding, parking, etc.)\n- Damage to vehicle (bad roads, height, etc.)\n- Wrong turns or missed signs\n- Extra costs (toll, parking, fuel)\n- Missed appointments due to delays\n\nYou are the driver. You determine the route. You are responsible.",
    "disclaimer.5.title": "5. AI Restyler Visualizations",
    "disclaimer.5.body": "5.1 Character of AI output\nThe AI Restyler is a visualization tool that uses artificial intelligence. It is not a:\n\n- Professional design tool\n- Guarantee for final result\n- Photographically accurate representation\n- Replacement for professional restoration advice\n\n5.2 Technical limitations\nAI-generated images are:\n\n- Digital interpretations, not exact predictions\n- Dependent on quality of input photo\n- Subject to AI limitations and \"hallucinations\"\n- Not always physically realizable\n\nAI struggles with:\n\n- Complex patterns or decorations\n- Specific materials (metal, glass, fabric)\n- Details such as handles, hinges, texture\n- Reflections, shadows, and lighting\n- Non-standard shapes or sizes\n\n5.3 No guarantees on colors\nColors in restyles are indicative:\n\n- Screen color representation differs from reality\n- Paint colors in the store may deviate\n- Lighting affects how colors look\n- Wood types react differently to paint\n- Pre-treatment (primer, sanding) affects the final result\n\nRecommendation:\n\n- Use restyles as inspiration, not as an exact blueprint\n- Test paint colors first on an invisible part\n- Consult professional advice for important projects\n- Expect deviations between visualization and reality\n\n5.4 No replacement advice for DIY projects\nVintMap gives NO advice about:\n\n- Which paint or materials to use\n- How to pre-treat furniture (sanding, degreasing)\n- Which techniques to apply\n- Whether a piece of furniture is suitable for processing\n- Safety of materials (lead paint, asbestos, etc.)\n\nFor DIY projects:\n\n- Seek professional advice\n- Follow instructions from paint manufacturers\n- Take safety precautions (masks, ventilation)\n- Test first on a small surface\n\n5.5 Purchase at own judgment\nWhen purchasing furniture after restyling:\n\n- Inspect the furniture physically carefully\n- Assess condition, quality, and stability\n- Check for defects, woodworm, damage\n- Ask advice from the store about suitability for processing\n- VintMap is not responsible for disappointments\n\nVintMap is NOT liable for:\n\n- Furniture that disappoints after purchase\n- Cost of materials and labor for processing\n- Failed DIY projects\n- Difference between expectation (restyle) and reality\n- Value or saleability of processed furniture",
    "disclaimer.6.title": "6. Community and User Content",
    "disclaimer.6.body": "6.1 Third-party content\nVintMap offers community functions where users can share:\n\n- Reviews and ratings of shops\n- Photos of furniture or shops\n- Homemade routes\n- Tips and experiences\n\nImportant disclaimer:\nThis content is created by users, not by VintMap.\n\n6.2 No prior checking\nVintMap:\n\n- DOES NOT check all user content in advance\n- Moderates reactively (after reporting or by chance)\n- Removes inappropriate content when flagged\n- Is NOT responsible for content of users\n\n6.3 No guarantees of reliability\nReviews and ratings:\n\n- Are subjective opinions of individual users\n- Can be dated, incomplete, or biased\n- Are not verified for truthfulness\n- Can be colored negatively or positively by personal experience\n\nVintMap DOES NOT guarantee:\n\n- That reviews are honest or balanced\n- That ratings are representative\n- That photos are current or match the current situation\n- That users have real experience with the store\n\n6.4 Possibility of fake reviews\nVintMap tries to prevent fake reviews and spam, but cannot guarantee:\n\n- That all reviews are authentic\n- That reviews are not placed by owners or competitors\n- That there is no case of paid or influenced reviews\n- That reviews are not manipulated\n\nRecommendation:\n\n- Read multiple reviews for a nuanced picture\n- Pay attention to details and consistency between reviews\n- Trust your own impression when visiting\n- Report suspicious reviews via \"Report problem\"\n\n6.5 User routes\nCommunity routes are:\n\n- Made by other users, based on their preferences\n- Not checked or optimized by VintMap\n- Possibly not current (shops closed, moved)\n- Based on personal taste and goals\n\nNo guarantee of:\n\n- Efficiency or optimality of route\n- Suitability for your vehicle or preferences\n- Actuality of included stores\n- Quality of descriptions\n\n6.6 Reporting inappropriate content\nIf you encounter content that:\n\n- Is inappropriate, offending, or illegal\n- Contains personal data\n- Is misleading or spam\n- Infringes intellectual property rights\n\nReport this via:\n\n- \"Report problem\" function in app\n- E-mail to moderation@vintmap.nl\n\nVintMap takes appropriate action after review.",
    "disclaimer.7.title": "7. Advertisements and Commercial Content",
    "disclaimer.7.body": "7.1 Presence of advertisements\nVintMap may show advertisements from:\n\n- Thrift shops and vintage stores\n- Related products and services (paints, DIY, interior)\n- Partners and collaborations\n- Third-party ad networks\n\nAdvertisements may appear as:\n\n- Banners in the app or website\n- Sponsored search results\n- Sponsored thrift stores (marked)\n- Promotional content in newsletters (if applicable)\n\n7.2 No guarantees or endorsement\nIMPORTANT: VintMap guarantees NOTHING about advertised products, services, or stores.\nThis means:\n\n- VintMap DOES NOT test or verify advertised products\n- Advertisements are NO recommendation or approval by VintMap\n- VintMap DOES NOT vouch for quality, safety, or legality\n- VintMap is NOT involved in transactions between you and advertisers\n\nAdvertising on VintMap DOES NOT mean:\n\n- That VintMap trusts or recommends the advertiser\n- That products/services are of high quality\n- That claims in ads are true or proven\n- That VintMap takes responsibility\n\n7.3 No rights to be derived from advertisements\nNO rights can be derived from advertisements:\n\n- No right to presence of specific offer\n- No right to correctness of price or conditions\n- No right to availability of product\n- No right to quality or specifications as advertised\n- No right to fulfillment of promises in advertisements\n\nIf an advertisement is wrong:\n\n- VintMap can remove the advertisement\n- VintMap is NOT liable for damages\n- Your recourse lies with the advertiser, not with VintMap\n\n7.4 Third-party ad networks\nVintMap may use external ad networks (such as Google Ads, Facebook Ads, etc.).\nThese parties:\n\n- May collect data about your behavior (see Privacy Policy)\n- Have their own terms and privacy policy\n- Determine which ads are shown (within VintMap guidelines)\n- Are responsible for their own advertisements\n\nVintMap has limited control over:\n\n- Which specific ads are shown\n- Content of dynamic ads\n- Targeting and personalization by advertisers\n\n7.5 No liability for advertisers\nVintMap is NOT liable for:\n\n- Misleading or false claims in advertisements\n- Products that do not meet expectations\n- Non-delivery or poor service from advertisers\n- Disputes between you and advertiser\n- Damage from defective products\n- Privacy violations by advertisers\n- Problems with payments or refunds\n\nFor problems with an advertiser:\n\n- Contact the advertiser directly\n- Use normal complaint procedures or consumer law\n- Report serious deception to VintMap (moderation@vintmap.nl)\n- VintMap can remove advertiser but does not compensate\n\n7.6 Sponsored content transparency\nSponsored content is marked as:\n\n- \"Sponsored\"\n- \"Advertisement\"\n- \"Paid placement\"\n- \"Partner\"\n\nVintMap strives for transparency regarding commercial relationships.\nNOTE:\nSponsored shops in search results are not necessarily better or more reliable. They pay for visibility, not for quality.\n\n7.7 Advertising policy\nVintMap refuses advertisements for:\n\n- Illegal products or services\n- Discriminatory or hateful content\n- Explicit or inappropriate content\n- Clearly misleading or fraudulent offers\n\nHowever, this policy is not 100% watertight. If you see a problematic advertisement, please report it.",
    "disclaimer.8.title": "8. External Services and Links",
    "disclaimer.8.body": "8.1 Links to external websites\nVintMap may contain links to:\n\n- Websites of thrift shops\n- Social media pages of shops\n- Partner websites\n- Informational sources\n- Google Maps and external navigation\n\n8.2 No control or responsibility\nVintMap is NOT responsible for:\n\n- Content of external websites\n- Availability of external links\n- Privacy practices of external parties\n- Safety or malware on external sites\n- Terms or policies of third parties\n- Transactions via external websites\n\nExternal websites are entirely the own responsibility of their owners.\n\n8.3 No endorsement\nThe presence of a link DOES NOT mean:\n\n- That VintMap recommends the website or organization\n- That VintMap has checked or approves the content\n- That VintMap vouches for the accuracy of information\n- That any affiliation or cooperation exists (unless explicitly stated)\n\n8.4 Broken links\nLinks can over time:\n\n- No longer work (404 error)\n- Lead to other content (site taken over)\n- Be redirected to unwanted content\n\nReport broken or problematic links via feedback@vintmap.nl.\n\n8.5 Third-party services\nVintMap uses third-party services:\n\n- Google Maps: For maps and navigation\n- Stripe: For payments\n- OpenAI / AI providers: For restyler functionality\n- Hosting providers: For technical infrastructure\n\nThese parties:\n\n- Have their own terms and disclaimer\n- Are responsible for their own service provision\n- May have their own limitations or failures\n\nSee their own websites for specific disclaimers.\n\n8.6 Social media integration\nVintMap can integrate with social media:\n\n- Sharing content on Facebook, Instagram, etc.\n- Logging in via social media accounts (if implemented)\n- Display of social media feeds\n\nWhen using these integrations:\n\n- The terms of the social media platform apply\n- VintMap may share data with the platform (see Privacy Policy)\n- You have no control over how the platform processes your data",
    "disclaimer.9.title": "9. No Professional Advice",
    "disclaimer.9.body": "9.1 General provision of information\nVintMap offers general information and tools. It provides NO professional advice in the field of:\n\n- Legal matters\n- Financial planning or investments\n- Appraisals or valuations\n- Restoration or furniture processing\n- Interior design\n- Health and safety (e.g., asbestos, lead paint)\n\n9.2 No replacement for professional consultation\nFor important decisions:\n\n- Consult a qualified professional\n- Do not rely solely on information from VintMap\n- Seek second opinions where necessary\n- Take responsibility for your own research\n\n9.3 Specific disclaimers\nAppraisals and valuations:\n\n- VintMap gives NO indication of value of furniture\n- Prices in stores are not objective market value\n- For insurance or sale: consult appraiser\n\nRestoration and DIY:\n\n- VintMap gives NO instructions for restoration\n- Restyler is visualization, not manual\n- For technical advice: consult professional\n\nHealth and safety:\n\n- VintMap DOES NOT warn against hazardous materials\n- Old furniture can contain asbestos, lead paint, etc.\n- When in doubt: have tested by professional\n\nLegal:\n\n- VintMap gives NO legal advice\n- For contracts, liability, etc.: consult lawyer\n\n9.4 Educational content\nAny tips, blogs, or educational content within VintMap:\n\n- Are general information, no personal advice\n- Are intended for inspiration, not as professional guideline\n- May be outdated or incomplete\n- Do not replace professional training or education",
    "disclaimer.10.title": "10. Availability and Technical Operation",
    "disclaimer.10.body": "10.1 No guarantee of availability\nVintMap strives for high availability but does not guarantee:\n\n- 24/7 uninterrupted operation\n- Absence of bugs or errors\n- Compatibility with all devices/browsers\n- Specific loading times or performance\n\n10.2 Technical limitations\nVintMap may experience problems due to:\n\n- Server downtime or maintenance\n- Internet failure (at user or provider)\n- Browser incompatibility\n- Mobile data limitations\n- Device-specific problems\n- Third-party services failing\n\n10.3 No compensation in case of failure\nIn case of technical problems or failure:\n\n- VintMap resolves problems as quickly as possible\n- There is no right to compensation (except where legally required)\n- Insider users: see General Terms and Conditions for specific regulations\n\n10.4 Software updates and changes\nVintMap can:\n\n- Add, change or remove features\n- Adjust user interface\n- Change functionality\n- Change system requirements\n\nSee General Terms and Conditions article 11 for details on changes.",
    "disclaimer.11.title": "11. Third-Party Intellectual Property",
    "disclaimer.11.body": "11.1 Google Maps attribution\nMap data and route calculations are the property of Google LLC and licensed data providers. By using VintMap you automatically accept the Google Maps terms.\nSee: https://www.google.com/help/terms_maps/\n\n11.2 Other third-party content\nVintMap may contain:\n\n- Icons and graphics from third-party libraries\n- Fonts licensed from designers\n- AI models licensed from providers\n- Data from public databases\n\nAll rights belong to respective owners.\n\n11.3 Trademarks of third parties\nBrand names and logos of thrift shops, products or services shown within VintMap are the property of respective trademark holders. VintMap claims no ownership over these trademarks.",
    "disclaimer.12.title": "12. Prices and Offers",
    "disclaimer.12.body": "12.1 Prices in shops\nPrices of items in thrift shops:\n\n- Are NOT determined or guaranteed by VintMap\n- May differ from possible mentions in reviews\n- Are subject to change without notice\n- Are always to be verified at the store itself\n\nVintMap DOES NOT show product prices and DOES NOT give price indications.\n\n12.2 VintMap subscription prices\nPrices for Insider subscriptions:\n\n- Are mentioned on the website and in the app\n- Include VAT (for Dutch users)\n- May change in accordance with General Terms and Conditions article 8.5\n- Are always current at the time of purchase (price in checkout applies)\n\n12.3 Discount codes and discounts\nPromotions and discounts:\n\n- Have limited validity\n- May contain conditions (one-time, new users)\n- Are not combinable unless otherwise stated\n- Can be terminated without notice\n\nNo rights can be derived from expired promotions.",
    "disclaimer.13.title": "13. Limitation of Liability",
    "disclaimer.13.body": "13.1 General limitation of liability\nTo the extent permitted by law VintMap is NOT liable for:\n\nDirect damage:\n- Lost time during incorrect routes\n- Parking or fuel costs\n- Disappointment over store visit\n- Missed opportunities due to incorrect information\n\nIndirect damage:\n- Loss of profit\n- Missed business opportunities\n- Damage to reputation\n- Consequential damage of any kind whatsoever\n\nIntangible damage:\n- Stress or frustration\n- Lack of pleasure or free time\n\n13.2 Maximization of liability\nWhere VintMap IS liable, the liability is limited as described in the General Terms and Conditions article 13.\n\n13.3 Exceptions\nLiability is NOT excluded for:\n\n- Intent or gross negligence by VintMap\n- Damage resulting from death or physical injury\n- Statutory liability that cannot be excluded\n\n13.4 Damage by third parties\nVintMap is NOT liable for damage caused by:\n\n- Other users (incorrect reviews, etc.)\n- Thrift shops (poor service, defective items)\n- Third-party services (Google Maps, AI provider)\n- Advertisers\n- External websites\n\nYour recourse lies with the causer of the damage, not with VintMap.",
    "disclaimer.14.title": "14. Own Responsibility",
    "disclaimer.14.body": "14.1 Duty of verification\nIt is your responsibility to:\n\n- Verify important information (opening hours, location)\n- Consult multiple sources when in doubt\n- Contact stores in case of uncertainties\n- Have realistic expectations\n\n14.2 Own assessment\nUse your own judgment for:\n\n- Decisions to buy furniture\n- Assessment of quality and condition of items\n- Choice of routes and travel planning\n- Interpreting reviews and visualizations\n- Trusting AI restyles\n\n14.3 Safety\nYou are responsible for:\n\n- Traffic safety while using routes\n- Safe handling of tools and materials during DIY\n- Protecting your own account and devices\n- Complying with laws and rules\n\n14.4 Back-ups\nMake your own back-ups of:\n\n- Important saved routes\n- Photos or content you do not want to lose\n- Account information\n\nVintMap guarantees no 100% data retention.",
    "disclaimer.15.title": "15. Changes and Updating",
    "disclaimer.15.body": "15.1 Continuous development\nVintMap is a continuously developed platform. This means:\n\n- Information can become outdated\n- Features can change\n- Functionality can be added or removed\n\n15.2 No guarantee of actuality\nDespite efforts, information may:\n\n- Be outdated at the time of use\n- Not reflect the latest changes\n- Be incomplete due to technical limitations\n\n15.3 Changes to disclaimer\nThis disclaimer may be amended:\n\n- To comply with new legislation\n- In case of changes to the service\n- For clarification or improvement\n\nSee General Terms and Conditions article 18 for details on modification procedure.",
    "disclaimer.16.title": "16. Relationship to Other Documents",
    "disclaimer.16.body": "16.1 General Terms and Conditions\nThis disclaimer is an integral part of the General Terms and Conditions of VintMap. In case of conflict, the General Terms and Conditions prevail.\nFor legally binding provisions on:\n\n- Rights and obligations\n- Subscriptions and payments\n- Termination and suspension\n- Applicable law\n\nSee: https://vintmap.nl/voorwaarden\n\n16.2 Privacy Policy\nFor information about data protection and privacy:\nSee: https://vintmap.nl/privacy\n\n16.3 Precedence in case of conflict\nIn the event of a contradiction between documents, the following order applies:\n\n- General Terms and Conditions (legally binding)\n- Privacy Policy (GDPR compliance)\n- This Disclaimer (clarification and warnings)",
    "disclaimer.17.title": "17. Contact Details",
    "disclaimer.17.body": "17.1 Questions about this disclaimer\nFor questions, comments or suggestions about this disclaimer:\nE-mail: legal@vintmap.nl\nMailing address:\nVintMap\nVoorstraat 142\n9291 CP Kollum\nThe Netherlands\n\n17.2 Reporting problems\nIncorrect information or technical problems:\nfeedback@vintmap.nl\nInappropriate content or advertisements:\nmoderation@vintmap.nl\nSecurity leaks:\nsecurity@vintmap.nl\n\n17.3 Response time\nWe aim to respond to questions about this disclaimer within 5 working days.",
    "terms.footer.note": "VintMap reserves the right to change these terms at any time. Use of the app after change implies acceptance.",
    "disclaimer.footer.note": "VintMap is not liable for damages caused by incorrect information or wrong route choices.",
    "privacy.footer.note": "VintMap processes your data with the utmost care in accordance with the GDPR.",
    "rating.noReviews": "no reviews",
    "rating.count": "reviews",
    "#6D0E0E": "Bordeaux",
    "#E2725B": "Warm Terracotta",
    "#FF7F50": "Coral",
    "#CCA9AC": "Dusty Rose",
    "#CC7722": "Ochre",
    "#E3A857": "Honey Yellow",
    "#F6E0A4": "Egg Yellow",
    "#6B8E23": "Olive Green",
    "#4E5B31": "Moss Green",
    "#87A96B": "Sage Green",
    "#1A2421": "Pine Green",
    "#006060": "Teal",
    "#A5C5D9": "Sky Blue",
    "#1A3A6D": "Royal Blue",
    "#B2A4D4": "Lavender",
    "#3D0C29": "Aubergine",
    "#FAF9F6": "Linen",
    "#F5F5DC": "Antique White",
    "#D9C5A3": "Sand Beige",
    "#8B8589": "Taupe",
    "#708090": "Slate Gray",
    "#36454F": "Charcoal",
    "#0A0A0A": "Jet Black",
    "mossGreen": "Moss Green",
    "whiteStucco": "White Stucco",
    "account.deleteSuccess": "Account deleted successfully",
    "badges.vonst_week": "Find of the week",
    "badges.route_week": "Route of the week",
    "badges.kringloop_expert": "Thrift store expert",
    "badges.route_planner": "Route planner",
    "badges.insider": "Insider",
    "badges.power_user": "Power user",
    "restyle.aiDesign": "AI Design 🎨",
    "restyle.noDescription": "No description provided.",
    "restyle.foundAt": "Found at",
    "common.months.january": "January",
    "common.months.february": "February",
    "common.months.march": "March",
    "common.months.april": "April",
    "common.months.may": "May",
    "common.months.june": "June",
    "common.months.july": "July",
    "common.months.august": "August",
    "common.months.september": "September",
    "common.months.october": "October",
    "common.months.november": "November",
    "common.months.december": "December",
  },
  de: {
    "categories.Meubels": "Möbel",
    "categories.Kleding & Schoenen": "Kleidung & Schuhe",
    "categories.Boeken & Media (LP's, CD's)": "Bücher & Medien (LPs, CDs)",
    "categories.Woondecoratie & Kunst": "Wohndekor & Kunst",
    "categories.Keuken- & Huishoudelijk": "Küchen- & Haushaltswaren",
    "categories.Elektronica (klein)": "Elektronik (klein)",
    "categories.Speelgoed & Spellen": "Spielzeug & Spiele",
    "categories.Sieraden & Accessoires": "Schmuck & Accessoires",
    "categories.Winkelnieuws": "Shop-News",
    "shops.details": "Ladenmerkmale",
    "shops.isCharity": "Wohltätigkeitszweck",
    "shops.paymentMethods": "Zahlungsmethoden",
    "shops.cash": "Barzahlung",
    "shops.card": "Kartenzahlung",
    "shops.categoriesAvailable": "Verfügbare Kategorien",
    "common.optional": "(Optional)",
    "rating.overall": "Gesamteindruck",
    "rating.atmosphere": "Atmosphäre",
    "rating.assortment": "Sortiment",
    "rating.price": "Preis",
    "rating.parking": "Parken",
  },
  fr: {
    "categories.Meubels": "Meubles",
    "categories.Kleding & Schoenen": "Vêtements & Chaussures",
    "categories.Boeken & Media (LP's, CD's)": "Livres & Médias (LPs, CDs)",
    "categories.Woondecoratie & Kunst": "Décoration d'intérieur & Art",
    "categories.Keuken- & Huishoudelijk": "Cuisine & Ménage",
    "categories.Elektronica (klein)": "Électronique (petit)",
    "categories.Speelgoed & Spellen": "Jouets & Jeux",
    "categories.Sieraden & Accessoires": "Bijoux & Accessoires",
    "categories.Winkelnieuws": "Nouvelles du magasin",
    "shops.details": "Caractéristiques du magasin",
    "shops.isCharity": "Charité",
    "shops.paymentMethods": "Moyens de paiement",
    "shops.cash": "Espèces",
    "shops.card": "Carte",
    "shops.categoriesAvailable": "Catégories disponibles",
    "common.optional": "(Optionnel)",
    "rating.overall": "Impression générale",
    "rating.atmosphere": "Atmosphère",
    "rating.assortment": "Assortiment",
    "rating.price": "Prix",
    "rating.parking": "Parking",
  },
  es: {
    "categories.Meubels": "Muebles",
    "categories.Kleding & Schoenen": "Ropa y Zapatos",
    "categories.Boeken & Media (LP's, CD's)": "Libros y Medios (LPs, CDs)",
    "categories.Woondecoratie & Kunst": "Decoración y Arte",
    "categories.Keuken- & Huishoudelijk": "Cocina y Hogar",
    "categories.Elektronica (klein)": "Electrónica (pequeña)",
    "categories.Speelgoed & Spellen": "Juguetes y Juegos",
    "categories.Sieraden & Accessoires": "Joyas y Accesorios",
    "categories.Winkelnieuws": "Noticias de la tienda",
    "shops.details": "Características de la tienda",
    "shops.isCharity": "Caridad",
    "shops.paymentMethods": "Métodos de pago",
    "shops.cash": "Efectivo",
    "shops.card": "Tarjeta",
    "shops.categoriesAvailable": "Categorías disponibles",
    "common.optional": "(Opcional)",
    "rating.overall": "Impresión general",
    "rating.atmosphere": "Atmósfera",
    "rating.assortment": "Surtido",
    "rating.price": "Precio",
    "rating.parking": "Aparcamiento",
    "faq.visitors.q3": "¿Por qué siempre navego desde mi ubicación actual en Google Maps en lugar de mi dirección de inicio?",
    "faq.visitors.a3": "La dirección de inicio que proporcionaste en VintMap sirve puramente para calcular la ruta o el orden óptimo para que puedas verla como una vista previa. Cuando abres la ruta en Google Maps (o en otra app), la navegación siempre comienza desde tu ubicación actual por defecto, ya que así es como funciona Google Maps. Importante: esto NO significa que VintMap pueda ver tu ubicación exacta a menos que la hayas compartido explícitamente. Todos los datos de ubicación para la navegación activa permanecen completamente privados con Google Maps en tu propio dispositivo.",
    "restyle.floor.dark_gray_tiles": "Baldosas gris oscuro",
  },
  it: {
    "categories.Meubels": "Mobili",
    "categories.Kleding & Schoenen": "Abbigliamento e Scarpe",
    "categories.Boeken & Media (LP's, CD's)": "Libri e Media (LP, CD)",
    "categories.Woondecoratie & Kunst": "Arredamento e Arte",
    "categories.Keuken- & Huishoudelijk": "Cucina e Casalinghi",
    "categories.Elektronica (klein)": "Elettronica (piccola)",
    "categories.Speelgoed & Spellen": "Giocattoli e Giochi",
    "categories.Sieraden & Accessoires": "Gioielli e Accessori",
    "categories.Winkelnieuws": "Novità del negozio",
    "shops.details": "Caratteristiche del negozio",
    "shops.isCharity": "Carità",
    "shops.paymentMethods": "Metodi di pagamento",
    "shops.cash": "Contanti",
    "shops.card": "Carta",
    "shops.categoriesAvailable": "Categorie disponibili",
    "common.optional": "(Opzionale)",
    "rating.overall": "Impressione generale",
    "rating.atmosphere": "Atmosfera",
    "rating.assortment": "Assortimento",
    "rating.price": "Prezzo",
    "rating.parking": "Parcheggio",
  },
  da: {
    "categories.Meubels": "Møbler",
    "categories.Kleding & Schoenen": "Tøj og Sko",
    "categories.Boeken & Media (LP's, CD's)": "Bøger og Medier (LP'er, CD'er)",
    "categories.Woondecoratie & Kunst": "Boligindretning og Kunst",
    "categories.Keuken- & Huishoudelijk": "Køkken og Husholdning",
    "categories.Elektronica (klein)": "Elektronik (lille)",
    "categories.Speelgoed & Spellen": "Legetøj og Spil",
    "categories.Sieraden & Accessoires": "Smykker og Tilbehør",
    "categories.Winkelnieuws": "Butiksnyheder",
    "shops.details": "Butiksfunktioner",
    "shops.isCharity": "Velgørenhed",
    "shops.paymentMethods": "Betalingsmetoder",
    "shops.cash": "Kontant",
    "shops.card": "Kort",
    "shops.categoriesAvailable": "Tilgængelige kategorier",
    "common.optional": "(Valgfrit)",
    "rating.overall": "Samlet indtryk",
    "rating.atmosphere": "Atmosfære",
    "rating.assortment": "Sortiment",
    "rating.price": "Pris",
    "rating.parking": "Parkering",
  },
  sv: {
    "categories.Meubels": "Möbler",
    "categories.Kleding & Skor": "Kläder & Skor",
    "categories.Boeken & Media (LP's, CD's)": "Böcker & Media (LP, CD)",
    "categories.Woondecoratie & Kunst": "Heminredning & Konst",
    "categories.Keuken- & Huishoudelijk": "Kök & Hushåll",
    "categories.Elektronica (klein)": "Elektronik (liten)",
    "categories.Speelgoed & Spellen": "Leksaker & Spel",
    "categories.Sieraden & Accessoires": "Smycken & Accessoarer",
    "categories.Winkelnieuws": "Butiksnyheter",
    "shops.details": "Butiksdetaljer",
    "shops.isCharity": "Välgörenhet",
    "shops.paymentMethods": "Betalningsmetoder",
    "shops.cash": "Kontant",
    "shops.card": "Kort",
    "shops.categoriesAvailable": "Tillgängliga kategorier",
    "common.optional": "(Valfritt)",
    "rating.overall": "Helhetsintryck",
    "rating.atmosphere": "Atmosfär",
    "rating.assortment": "Sortiment",
    "rating.price": "Pris",
    "rating.parking": "Parkering",
    "nav.routes": "Rutter",
    "nav.shops": "Second hand-butiker",
    "nav.community": "Community",
    "nav.scanner": "Scanner",
    "nav.restyle": "Restyle",
    "nav.login": "Logga in",
    "nav.signup": "Registrera",
    "nav.account": "Konto",
    "nav.admin": "Admin",
    "shops.title": "Second hand-butiker",
    "shops.description": "Upptack second hand-butiker i Europa",
    "shops.roadtrip": "Planera den ultimata roadtripen med {count} unika stopp.",
    "mossGreen": "mossgrön",
    "whiteStucco": "vitt gips",
    "gems.shareTitle": "Fynd på VintMap",
    "gems.shareText": "Se så fint! {caption}",
    "restyle.shareTitle": "VintMap Restyle Studio",
    "restyle.shareText": "Se min möbel! Skapad med VintMap Restyle AI.",
  },
  "en-gb": {
    "routeBuilder.mapPreviewPending": "Loading map...",
    "routeBuilder.mapPreviewPendingDesc": "We are calculating the best route for you.",
    "routeBuilder.reversedWithAddressesDesc": "The route has been reversed, including your specified start and end addresses.",
    "scanner.marketItems": "Market References",
    "scanner.noMarketLinks": "No direct market links found for this specific item.",
    "faq.visitors.q3": "Why do I always navigate from my current location in Google Maps instead of my start address?",
    "faq.visitors.a3": "The starting address you provided on VintMap is purely for calculating the optimal route or order so you can view it as a preview. When you actually open the route in Google Maps (or another app), navigation starts from your current location by default, as this is how Google Maps works. Important: this does NOT mean VintMap can see your exact location unless you explicitly shared it. All location data for active navigation remains completely private with Google Maps on your own device.",
    "restyle.floor.dark_gray_tiles": "Dark grey tiles",
  },
};

export const TRANSLATION_KEYS = Array.from(new Set(
  Object.values(DEFAULT_MESSAGES).flatMap(messages => Object.keys(messages))
)).sort();
export const DEFAULT_TRANSLATION_BASE_EN = Object.fromEntries(
  TRANSLATION_KEYS.map((key) => [
    key,
    DEFAULT_MESSAGES.en[key] || DEFAULT_MESSAGES.nl[key] || key,
  ])
);
const LOCALE_STORAGE_KEY = "vintmap_preferred_locale";
export const LOCALE_LABELS: Record<AppLocale, string> = {
  nl: "Nederlands",
  en: "English (Global)",
  "en-gb": "English (UK & Ireland)",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  it: "Italiano",
  da: "Dansk",
  sv: "Svenska",
};

const LOCALIZED_ROUTE_PATHS: Record<string, Partial<Record<AppLocale, string>>> = {
  // Stable per-locale segments: all locales use the same in-app paths.
  routes: { en: "/routes", de: "/routes", fr: "/routes", es: "/routes", it: "/routes", da: "/routes", sv: "/routes", nl: "/routes" },
  routeDetail: { en: "/kringlooproutes", de: "/kringlooproutes", fr: "/kringlooproutes", es: "/kringlooproutes", it: "/kringlooproutes", da: "/kringlooproutes", sv: "/kringlooproutes", nl: "/kringlooproutes" },
  shops: { nl: "/kringloopwinkels", "en-gb": "/second-hand-shop", en: "/thrift-store", de: "/second-hand-laeden", fr: "/magasins-doccasion", es: "/tiendas-de-segunda-mano", it: "/negozi-di-usato", da: "/genbrugsbutikker", sv: "/second-hand-butiker" },
  community: { en: "/community", de: "/community", fr: "/community", es: "/community", it: "/community", da: "/community", sv: "/community", nl: "/community" },
  scanner: { en: "/scanner", de: "/scanner", fr: "/scanner", es: "/scanner", it: "/scanner", da: "/scanner", sv: "/scanner", nl: "/scanner" },
  wishlist: { en: "/wishlist", nl: "/wishlist", de: "/wishlist", fr: "/wishlist", es: "/wishlist", it: "/wishlist", da: "/wishlist", sv: "/wishlist" },
  restyle: { en: "/restyle", de: "/restyle", fr: "/restyle", es: "/restyle", it: "/restyle", da: "/restyle", sv: "/restyle", nl: "/restyle" },
  spaceblend: { en: "/spaceblend", de: "/spaceblend", fr: "/spaceblend", es: "/spaceblend", it: "/spaceblend", da: "/spaceblend", sv: "/spaceblend", nl: "/spaceblend" },
  // Shop detail base paths for SEO
  shopDetailKringloop: { nl: "/kringloopwinkel", en: "/thrift-store", de: "/second-hand-laden", fr: "/magasin-doccasion", es: "/tienda-de-segunda-mano", it: "/negozio-di-usato", da: "/genbrugsbutik", sv: "/second-hand-butik" },
  shopDetailThrift: { en: "/thrift-store", nl: "/kringloopwinkel", de: "/second-hand-laden", fr: "/magasin-doccasion", es: "/tienda-de-segunda-mano", it: "/negozio-di-usato", da: "/genbrugsbutik", sv: "/second-hand-butik" },
  shopDetailSecondHand: { en: "/second-hand-shop", nl: "/kringloopwinkel", de: "/second-hand-laden", fr: "/magasin-doccasion", es: "/tienda-de-segunda-mano", it: "/negozio-di-usato", da: "/genbrugsbutik", sv: "/second-hand-butik" },
};

interface I18nContextValue {
  locale: AppLocale;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue>({
  locale: "en",
  t: (key: string) => key,
});

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, token: string) => String(vars[token] ?? ""));
}

const missingTranslationQueue = new Set<string>();
let missingTranslationFlushTimer: number | null = null;

function flushMissingTranslationQueue(locale: AppLocale) {
  if (typeof window === "undefined") return;
  if (missingTranslationQueue.size === 0) return;
  const keys = Array.from(missingTranslationQueue).slice(0, 100);
  keys.forEach((key) => missingTranslationQueue.delete(key));
  window.fetch("/api/features/translations/missing", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ locale, keys }),
    keepalive: true,
  }).catch(() => {
    // ignore telemetry errors
  });
}

function reportMissingTranslationKey(key: string, locale: AppLocale) {
  if (!key || typeof window === "undefined") return;
  missingTranslationQueue.add(key);
  if (missingTranslationFlushTimer !== null) return;
  missingTranslationFlushTimer = window.setTimeout(() => {
    missingTranslationFlushTimer = null;
    flushMissingTranslationQueue(locale);
  }, 800);
}

export function I18nProvider({
  locale,
  overrides,
  children,
}: {
  locale: AppLocale;
  overrides?: TranslationOverrides;
  children: React.ReactNode;
}) {
  const value = useMemo<I18nContextValue>(() => {
    const localeMessages = {
      ...(DEFAULT_MESSAGES[locale] || {}),
      ...(overrides?.[locale] || {}),
    };
    const englishMessages = {
      ...DEFAULT_MESSAGES.en,
      ...(overrides?.en || {}),
    };
    return {
      locale,
      t: (key: string, vars?: Record<string, string | number>) => {
        const localeRaw = localeMessages[key];
        const englishRaw = englishMessages[key];
        const raw = localeRaw || englishRaw || DEFAULT_MESSAGES.nl[key] || key;
        if (!localeRaw) {
          const shouldReportMissing = !(locale === "en-gb" && Boolean(englishRaw));
          if (shouldReportMissing) {
            reportMissingTranslationKey(key, locale);
          }
        }
        return interpolate(raw, vars);
      },
    };
  }, [locale, overrides]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export function getLocalizedPath(routeKey: keyof typeof LOCALIZED_ROUTE_PATHS, locale: AppLocale): string {
  return LOCALIZED_ROUTE_PATHS[routeKey]?.[locale] || LOCALIZED_ROUTE_PATHS[routeKey]?.en || "/";
}

export function getLocaleFromPath(pathname: string): AppLocale | null {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0]?.toLowerCase();

  // 1. Check explicit prefix
  if (isAppLocale(first)) {
    return first as AppLocale;
  }

  // 2. Check localized routes (e.g. "/kringloopwinkels" implies "nl")
  const possibleLocales = new Set<AppLocale>();
  const firstSegment = `/${first}`;

  // We need to access the map, ensuring we check values safely
  const routes = LOCALIZED_ROUTE_PATHS as Record<string, Partial<Record<AppLocale, string>>>;

  for (const key in routes) {
    const mapping = routes[key];
    if (!mapping) continue;

    for (const locale of APP_LOCALES) {
      if (mapping[locale] === firstSegment) {
        possibleLocales.add(locale);
      }
    }
  }

  if (possibleLocales.size > 0) {
    // If we have multiple matches (e.g. "/routes" matches all), prefer the user's stored/detected locale if valid
    const preferred = detectPreferredLocale();
    if (possibleLocales.has(preferred)) {
      return preferred;
    }
    // Otherwise, favor 'nl' as default if present
    if (possibleLocales.has('nl')) return 'nl';
    // Finally just take the first one
    return Array.from(possibleLocales)[0];
  }

  return null;
}

export function stripLocalePrefix(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "/";
  if (isAppLocale(parts[0])) {
    const stripped = `/${parts.slice(1).join("/")}`;
    return stripped === "/" ? "/" : stripped.replace(/\/+$/, "");
  }
  return pathname || "/";
}

function normalizeLocalizedRoutePath(pathname: string): string {
  const rawPath = pathname || "/";
  const normalized = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  const segments = normalized.split("/").filter(Boolean);
  if (segments.length === 0) return "/";

  const firstSegment = `/${segments[0]}`;
  const legacyAliases: Record<string, string> = {
    "/routen": "/routes",
    "/itineraires": "/routes",
    "/rutas": "/routes",
    "/percorsi": "/routes",
    "/genbrugsruter": "/routes",
    "/rutter": "/routes",
    "/kringloopwinkels": "/shops",
    "/kringloopwinkel": "/shops",
    "/thrift-stores": "/shops",
    "/thrift-store": "/shops",
    "/second-hand-shops": "/shops",
    "/second-hand-shop": "/shops",
    "/communaute": "/community",
    "/comunidad": "/community",
    "/faellesskab": "/community",
    "/kringlooproutes": "/kringlooproutes", // Already internal canonical
  };

  const canonicalFirst = legacyAliases[firstSegment] || firstSegment;
  const remainder = segments.slice(1).join("/");
  return remainder ? `${canonicalFirst}/${remainder}` : canonicalFirst;
}

export function withLocalePrefix(pathname: string, locale: AppLocale): string {
  const stripped = stripLocalePrefix(pathname);
  // This normalizes "/kringloopwinkels" -> "/shops" (canonical internal)
  const canonical = normalizeLocalizedRoutePath(stripped);

  // If canonical is just "/", return localized root
  if (canonical === "/") {
    return locale === "nl" ? "/" : `/${locale}`;
  }

  // OPTIMIZATION: If we are in 'nl' (default) and the path is already valid 
  // (doesn't have a prefix), we don't want to force-translate it 
  // to a canonical form which might trigger redirects.
  if (locale === "nl") {
    return stripped;
  }

  // Split canonical to find the first segment (e.g. "shops" from "/shops/detail")
  const segments = canonical.split('/').filter(Boolean);
  if (segments.length === 0) return `/${locale}`;

  const firstCanonicalSegment = `/${segments[0]}`;

  // Find which route key corresponds to this canonical segment
  const trimmedSegment = firstCanonicalSegment.substring(1); // e.g. "shops" from "/shops"
  let routeKey = trimmedSegment as keyof typeof LOCALIZED_ROUTE_PATHS;

  if (!LOCALIZED_ROUTE_PATHS[routeKey]) {
    // Fallback: check against known EN or NL base paths in LOCALIZED_ROUTE_PATHS
    routeKey = Object.keys(LOCALIZED_ROUTE_PATHS).find((key) => {
      const p = LOCALIZED_ROUTE_PATHS[key];
      return p?.en === firstCanonicalSegment || p?.nl === firstCanonicalSegment;
    }) as keyof typeof LOCALIZED_ROUTE_PATHS;
  }

  // If we found a matching route key, get its localized path for the target locale
  if (routeKey) {
    const localizedBase = getLocalizedPath(routeKey, locale);
    // localizedBase is e.g. "/kringloopwinkels" or "/thrift-store"

    // Re-attach the rest of the path (if any)
    const remainder = segments.slice(1).join('/');
    const cleanBase = localizedBase.startsWith('/') ? localizedBase : `/${localizedBase}`;
    const finalPath = remainder ? `${cleanBase}/${remainder}` : cleanBase;

    // Add locale prefix if not default (nl)
    // (Note: we already returned early if locale was 'nl')
    return `/${locale}${finalPath.startsWith("/") ? finalPath : `/${finalPath}`}`;
  }

  // Fallback: if not found in map, just prefix the canonical path
  return `/${locale}${canonical.startsWith("/") ? canonical : `/${canonical}`}`;
}

export function detectPreferredLocale(): AppLocale {
  const country = (detectUserCountry() || "NL").toUpperCase();
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isAppLocale(stored)) {
      if (stored === "en" && (country === "UK" || country === "GB" || country === "IE")) {
        return "en-gb";
      }
      return stored;
    }
  }
  const countryLocaleMap: Record<string, AppLocale> = {
    NL: "nl",
    BE: "nl",
    DE: "de",
    FR: "fr",
    ES: "es",
    IT: "it",
    DK: "da",
    SE: "sv",
    US: "en",
    CA: "en",
    UK: "en-gb",
    GB: "en-gb",
    IE: "en-gb",
  };
  return countryLocaleMap[country] || "nl";
}

export function persistPreferredLocale(locale: AppLocale): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

export { APP_LOCALES, type AppLocale, isAppLocale };
